import { PAGE_SLOT_COUNT } from "./slotCount";
import { createGoalFlagElement } from "./goalFlag";
import { isFreshEscape } from "./escapeKey";
import { entryDirection } from "./entryDirection";
import goalStarBadgeUrl from "../assets/images/goal-star.svg";
import unidentifiedBadgeUrl from "../assets/images/visitor_analytics_icons/unidentified.svg";
import { returnMarkFor } from "./returnMark";
import { awayGlyphPath } from "./awayGlyph";
import { perfProbeText } from "./perfProbe";
import { appendNewCategories, computeRegionGrid, filterDimId, formatShare, initialCategoryOrder, parseFilterDim, ZOOM_FILTERS } from "./zoomDimensions";
/**
 * Density cloud particle engine — canvas-based, zero D3, light physics.
 *
 * Two modes:
 *   grid     — per-slot canvas clouds; each slot independently crosses its own
 *              threshold and renders a particle cloud over the SVG bubble.
 *              Below the threshold the slot shows normal SVG bubbles unchanged.
 *   rivers   — full-canvas takeover for ONE page. Triggered by clicking a cloud
 *              slot. Particles segregate into labeled spatial regions by the
 *              active dimension (interactionState | browser | newReturn | converted).
 *
 * Physics ported from interaction-rivers-mockup.html:
 *   deterministic easeInOutCubic tween (no spring/force, cannot overshoot)
 *   tiny sinusoidal wobble layered on top (cosmetic, does not feed back)
 *   dt capped at 48ms to prevent spiral on tab wake
 *
 * Integration contract with clusterService:
 *   — Call notifyParticleCount(slotIndex, pageName, count) every time the
 *     per-slot visitor count changes. The service shows/hides canvas overlays.
 *   — Call notifyInteractionState(visitorId, state) on every pulse event.
 *   — Call notifyVisitorPage(visitorId, pageName) on every track-url / new-visitor.
 *   — Call notifyVisitorData(visitorId, data) on new-visitor to set browser/etc.
 *   — Call notifyVisitorDeparture(visitorId) on disconnect-visitor.
 *   — Call handleSlotClick(slotIndex, pageName) on a density-cloud slot click.
 *   — Call handleBackButton() to exit rivers mode.
 *   — Call resize(svgEl) on window resize.
 *   — Call destroy() on full teardown.
 */

// ── constants ─────────────────────────────────────────────────────────────────

// The app's typeface; everything the zoomed view draws or builds uses it.
const UI_FONT = "\"Fira Sans\", sans-serif";

// Whether the interaction states (reading, watching, idle, away ...) exist for what is on screen. They
// are live-only: nothing stores them, so a past day's playback has none and every visitor is "focused".
let interactionStatesProvider: (() => boolean) | null = null;
export function setInteractionStatesProvider(fn: typeof interactionStatesProvider) {
  interactionStatesProvider = fn;
}

// Whether a page is the conversion GOAL page, so its zoomed card can carry the same flag the grid's page box carries (the host decides, exactly as it does for the grid's flag: the current goals live,
// the day's recorded goal pages in playback).
let goalPageProvider: ((pageName: string) => boolean) | null = null;
export function setGoalPageProvider(fn: typeof goalPageProvider) {
  goalPageProvider = fn;
}
export const GOAL_FLAG_CARD_PX = 46;

// A d3-cluster look without the force simulation: same-size dots on a fixed,
// evenly spaced honeycomb (see "static packed layout" below).
const CLOUD_THRESHOLD = 15;
const CLOUD_EXIT_THRESHOLD = 15;          // under the entry count it is a d3 cluster again
const CLOUD_MIN_LIFE_MS = 8000;          // a formed cloud holds at least this long
const CLOUD_REFORM_COOLDOWN_MS = 8000;   // and cannot re-form this soon after dissolving
const CLOUD_REFORM_OVERRIDE = 18;        // ...unless the crowd is clearly back
const PARTICLE_RADIUS = 2.4;         // mockup: r = 2.4 + rand * 1.4
const PARTICLE_RADIUS_JITTER = 1.4;
const TWEEN_DUR_BASE = 1400;
const TWEEN_DUR_JITTER = 700;
const MODE_SWITCH_DUR_BASE = 1800;
const MODE_SWITCH_DUR_JITTER = 900;
const CATEGORY_MOVE_MAX_MS = 1300;
const WOBBLE_AMP = 0.6; // packed dots have ~2px gaps; keep the settle wobble inside them
const TRAIL_MAX = 14;
const DT_CAP = 48;
const GRAD_START_FPS = 1 / 12;      // travelers spawn at most this fast per slot

// region layout constants (rivers view)
const REGION_RING_RADIUS = 220;      // distance from center to satellite regions
const REGION_RING_ASPECT = 1.6;      // wide ellipse, not circular
const REGION_BREATHE_ALPHA = 0.06;

// interaction state colours (always-on, even with no active dimension)
const INTERACTION_COLORS: Record<string, string> = {
  focused: "#35d0c4", idle: "#9a8cf2", scrolling: "#ef7d6b", typing: "#f0b955",
  clicking: "#f0b955", away: "#6c788a",
  // not in the mockup (added after it): picked to stay distinct from the above
  reading: "#4fb3e8", watching: "#e879b9",
};
const DEFAULT_COLOR = "#78909c";

// dimension definitions (mirrors mockup)
type StaticDimId = "interactionState" | "browser" | "newReturn" | "converted";
// a grouping by one of the real-time filters is "f:<menuType>" (see zoomDimensions.ts); there are as many as the host offers
type DimensionId = StaticDimId | `f:${string}`;

interface DimensionDef {
  label: string;
  getCategory: (p: Particle) => string;
  categories: string[];
  colors: Record<string, string>;
  /** a filter grouping: its categories are discovered from the visitors (and a known universe), its colours come from the host */
  dynamic?: {
    menuType: string;
    colorFor: (category: string) => string | undefined;
    /** the order the regions are laid out in for these counts; stable once chosen */
    order: (counts: Record<string, number>) => string[];
    /** forget the order, so the next layout re-sorts by the current counts (done when the grouping is chosen) */
    reset: () => void;
    /** a group that means "we do not know" while Hide unknown is on: it gets no region, its dots are not drawn or hovered, and it is not in the shares */
    hides: (category: string) => boolean;
  };
}

const DIMENSIONS: Record<StaticDimId, DimensionDef> = {
  interactionState: {
    label: "Interaction State",
    getCategory: (p) => p.interactionState,
    categories: ["focused", "clicking", "typing", "scrolling", "reading", "watching", "idle", "away"],
    colors: INTERACTION_COLORS,
  },
  browser: {
    label: "Browser",
    getCategory: (p) => (["Chrome", "Safari", "Firefox", "Edge"].includes(p.browser) ? p.browser : "Other"),
    categories: ["Chrome", "Safari", "Firefox", "Edge", "Other"],
    colors: {
      Chrome: "#35d0c4", Safari: "#9a8cf2", Firefox: "#f0b955",
      Edge: "#ef7d6b", Other: "#6c788a",
    },
  },
  newReturn: {
    label: "New vs Returning",
    getCategory: (p) => p.newReturn,
    categories: ["New", "Returning"],
    colors: { New: "#35d0c4", Returning: "#9a8cf2" },
  },
  converted: {
    label: "Converted",
    getCategory: (p) => p.converted ? "Converted" : "Not Converted",
    categories: ["Converted", "Not Converted"],
    colors: { Converted: "#f0b955", "Not Converted": "#6c788a" },
  },
};

// ── filter groupings ──────────────────────────────────────────────────────────

// What the host (clusterService) tells the zoomed view about the filters' values: the name shown for a value, the colour it has everywhere else in the app (its bubble, tooltip and
// drawer banner), and - where the values are a known list (search engines, social networks, devices...) - the whole list, so every one has a region, even with nobody in it.
export interface ZoomCategoryProvider {
  label: (menuType: string, value: string) => string;
  color: (menuType: string, value: string) => string | undefined;
  universe: (menuType: string) => string[] | undefined;
  /** true while Hide unknown is on and this value means "we do not know" (the host's own rule, the same one the historical dock uses) */
  hide?: (menuType: string, value: string) => boolean;
}
let zoomProvider: ZoomCategoryProvider | null = null;
export function setZoomCategoryProvider(fn: ZoomCategoryProvider | null) { zoomProvider = fn; }

const NO_VALUE = "(No data for this category)";   // the host's name for "this visitor has no value for the filter" (unknownBucket.ts)
const rawValueOf = (p: Particle, menuType: string): string => {
  const attrs = visitorData.get(p.visitorId)?.attrs;
  const v = attrs ? attrs[menuType] : undefined;
  if (v === undefined || v === null || v === "") return menuType === "browser" && p.browser ? p.browser : NO_VALUE;

  return String(v);
};
const filterDefs = new Map<string, DimensionDef>();
function filterDef(menuType: string): DimensionDef {
  const cached = filterDefs.get(menuType);
  if (cached) return cached;
  const labelOfRaw = new Map<string, string>();
  const rawOfLabel = new Map<string, string>();
  const labelFor = (raw: string) => {
    let l = labelOfRaw.get(raw);
    if (l === undefined) {
      l = zoomProvider ? zoomProvider.label(menuType, raw) : raw;
      labelOfRaw.set(raw, l);
      if (!rawOfLabel.has(l)) rawOfLabel.set(l, raw);
    }

    return l;
  };
  let known: string[] | null = null;
  let settleUntil = 0;   // until then the order is re-worked every frame: the dots of a grouping that has just been chosen land over a second or so, and the order is not to be fixed on the first few
  const universeLabels = () => (zoomProvider?.universe(menuType) ?? []).map((raw) => labelFor(raw));
  const self: DimensionDef = {
    label: ZOOM_FILTERS.find((f) => f.menuType === menuType)?.label ?? menuType,
    getCategory: (p) => labelFor(rawValueOf(p, menuType)),
    categories: [],
    colors: {},
    dynamic: {
      menuType,
      colorFor: (category) => { const raw = rawOfLabel.get(category); return raw === undefined ? undefined : zoomProvider?.color(menuType, raw); },
      order: (counts) => {
        // chosen from the FIRST counts that have anybody in them: asked before the visitors are counted (a zoom that has only just opened), it answers with the universe and decides nothing
        if (known === null && !Object.keys(counts).some((k) => counts[k] > 0)) {
          return universeLabels();
        }
        if (settleUntil === 0) settleUntil = performance.now() + ORDER_SETTLE_MS;
        known = known === null || performance.now() < settleUntil ? initialCategoryOrder(counts, universeLabels()) : appendNewCategories(known, counts);
        self.categories = known;

        return known;
      },
      reset: () => { known = null; settleUntil = 0; self.categories = []; },
      hides: (category) => { const raw = rawOfLabel.get(category); return raw !== undefined && !!zoomProvider?.hide?.(menuType, raw); },
    },
  };
  filterDefs.set(menuType, self);

  return self;
}
function dimDef(dim: DimensionId): DimensionDef {
  const menuType = parseFilterDim(dim);

  return menuType ? filterDef(menuType) : DIMENSIONS[dim as StaticDimId];
}

// ── particle type ─────────────────────────────────────────────────────────────

interface Particle {
  x: number; y: number;
  r: number;
  phase: number;
  speedMul: number;
  radialFrac: number;
  homeAngle: number;
  tweenStartX: number; tweenStartY: number;
  tweenT0: number; tweenDur: number;
  trail: Array<{ x: number; y: number }>;

  // data dims — interaction changes live; others set-once on arrival
  visitorId: string;
  interactionState: string;
  browser: string;
  newReturn: string;
  converted: boolean;
  unidentified: boolean;   // an "unidentified" visitor (the "?" badge the grid draws on its bubble)
  pageName: string;

  // traveler state (arriving / departing)
  traveler: boolean;
  travelerAlpha: number;  // 0→1 arriving, 1→0 departing
  travelerDir: 1 | -1;   // 1 = arriving, -1 = departing
  exitTarget?: { x: number; y: number };
  stateT0?: number;
  rUnit?: number;
  colorStr?: string;
  packX?: number;
  packY?: number;
  pcat?: string;   // the group pack this dot was last reconciled into
  src?: Particle;  // zoomed view: the slot cloud's own dot this one mirrors
  arrive?: { x0: number; y0: number; tc: number; t0: number }; // zoomed view: flying in from the spawn point (client px); t0 = -1 until its destination is known
  spawned?: boolean;   // a new visitor: until it lands it waits at its reserved spot, never follows its real bubble
  iconUrl?: string;    // icon mode: this visitor's category icon
  iconAspect?: number; // icon height / width
  following?: boolean;
  enter?: { x: number; y: number; t0: number; dur: number };   // a visitor who moved here from a neighbour: comes in from that side's sliver
  landed?: boolean;
  sx?: number;
  sy?: number;
  colorT?: number;
  vx: number;
  vy: number;
  opacity: number;
}

interface Region {
  label: string;
  x: number; y: number;
  r: number;
  count: number;
  color: string;
}

// ── per-slot cloud state ───────────────────────────────────────────────────────

interface SlotCloud {
  arrived: Map<string, { x: number; y: number; t: number }>;  // real bubble has arrived and been hidden; the dot follows a moment later
  pending: Map<string, number>;                          // visitors whose real bubble is still flying to its spot
  pendingTarget: Map<string, { x: number; y: number }>;  // that spot, in svg coords
  pendingRest: Map<string, { x: number; y: number; n: number }>; // how long each flying bubble has been still
  pack: Pack;
  seeded?: boolean;
  bornAt?: number;
  forced?: boolean;        // formed by a click on a quiet slot (zoomSlot), not by the crowd reaching CLOUD_THRESHOLD
  instantUntil?: number;   // until then a visitor already on the page lands at once, where its bubble is
  slotIndex: number;
  pageName: string;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  particles: Particle[];
  travelerTimer: number;   // ms since last traveler spawn
  cx: number; cy: number; // center in SVG/page coords
  w: number; h: number;
  byId: Map<string, Particle>;  // landed dots by visitor
  mv: number;                   // bumped whenever the cloud's membership changes
  mvSeen: number;               // the membership the pack was last reconciled to
  lastReconcile: number;
  pvSeen: number;               // the pageVersion this cloud last scanned
  lastScan: number;
  frame: number;
}

// ── service state ─────────────────────────────────────────────────────────────

let svgEl: SVGElement | null = null;
let svgRect: DOMRect | null = null;

// per-slot visitor counts (updated by clusterService on every change)
const slotCounts = new Map<number, number>();
// per-slot active cloud overlays
const slotClouds = new Map<number, SlotCloud>();
const lastCloudExit = new Map<number, number>();

// visitor → state/data maps (updated by clusterService events)
const visitorInteraction = new Map<string, string>();
const visitorStateT0 = new Map<string, number>();
const visitorPage = new Map<string, string>();
const visitorPrevPage = new Map<string, string>();
let riversNeighbors: SlotNeighbors = {};
// Which slot a page belongs to: a selected page owns its slot, every other page
// belongs to "All other pages". Membership is by SLOT, never by page name, so
// the catch-all box (many different page names) can be a cloud too.
let pageSlotProvider: ((pageName: string) => number) | null = null;
export function setPageSlotProvider(fn: typeof pageSlotProvider) {
  pageSlotProvider = fn;
  pageVersion++;
}
function slotForPage(pageName: string | undefined, fallbackSlot: number, fallbackName: string): number {
  if (pageName === undefined) return -1;
  return pageSlotProvider ? pageSlotProvider(pageName) : pageName === fallbackName ? fallbackSlot : -1;
}
// The zoomed view is a camera on the real scene: it mirrors the slot cloud's own
// dots, draws a still-flying visitor at its REAL bubble's position, and keeps
// tracking a departing visitor's real bubble until it is gone.
let riversSlotIndex = -1;
const riversLeaving = new Map<string, number>();
const riversLeaveReason = new Map<string, string>();
let riversCamAnim: { t0: number } | null = null;
let riversCenter = { x: 0, y: 0 };
let riversLanded = new Map<string, Particle>();
let riversArrived = new Map<string, { x: number; y: number }>();
const CAM_PAN_MS = 1400;
// A zoom opened on a QUIET slot frames its crowd instead of opening at 1.0x, where a handful of
// dots (the same size as the grid's bubbles) are a few specks in a big stage. The camera zooms until
// the crowd's radius is FIT_TARGET of the stage's short side, at most FIT_MAX times, and counts the
// crowd as at least FIT_MIN_R wide, so the first visitor to arrive in an empty slot is not a pin-prick.
const FIT_TARGET = 0.2;
const FIT_MAX = 6;
const FIT_MIN_R = 14;
const FIT_SWITCH_MS = 600;   // easing between the framed view and a grouping's 1.0x layout
let riversAutoFit = false;       // this zoom was opened on a quiet slot: its home view frames the crowd
let riversFitTrack = false;      // ...and the camera follows the crowd's size until the person takes it over
let riversScaleAnim: { t0: number; from: number; to: number } | null = null;
const UNKNOWN_SLOT_INDEX = PAGE_SLOT_COUNT; // 'All other pages': the box after the page slots
// The single-cluster view has no page boxes: its one cluster is a cloud of its own, holding every
// visitor whatever page they are on. That cloud is shown AS the rivers view, open for as long as the
// cloud exists (the same controls as a page's zoom, no Back button, the view icons stay clickable).
const SINGLE_SLOT_INDEX = 14;
let riversPersistent = false;
// the grouping chosen in the single view, kept across its view closing and re-opening (a seek wipes the board)
let singleViewDimension: DimensionId | null = null;
// where the camera rests and the grouped regions are laid out: the cloud's own spot for a page's
// zoom (the camera pans there), the middle of the stage for the persistent single view (it stays put)
let riversHome = { x: 0, y: 0 };
let riversControlsBottom = 92;
/**
 * Open the single view's rivers view. The cloud and its view are ONE state, so this is asked on every
 * count the host sends, not once at the cloud's birth: a one-shot that missed for any reason (an
 * error part-way through, a zoom left open elsewhere) left a single-view cloud with no controls
 * for good.
 */
function openSingleView(repair: boolean) {
  if (riversMode || singleViewStats.failed >= 5) return;
  try {
    handleSlotClick(SINGLE_SLOT_INDEX, "All pages", {}, true);
    if (riversMode) {
      singleViewStats.opened++;
      if (repair) {
        singleViewStats.repaired++;
        console.warn("[density-cloud] the single view's cloud had no open view; opened it");
      }
    }
  } catch (error) {
    singleViewStats.failed++;
    console.error("[density-cloud] opening the single view failed", error);
    try { handleBackButton(false); } catch { /* nothing left to undo */ }
  }
}
/** Membership is by page -> slot: call when that mapping changes (the page view <-> single view switch). */
export function refreshMembership() {
  pageVersion++;
}
const LOAD_WINDOW_MS = 30000; // performance.now() counts from the page load
const visitorData = new Map<string, { browser?: string; visitor_type?: string; goalReached?: boolean; unidentified?: boolean; attrs?: Record<string, string | undefined> }>();

// rivers view state
let riversMode = false;
let riversPageName = "";
let riversCanvas: HTMLCanvasElement | null = null;
let riversCtx: CanvasRenderingContext2D | null = null;
let riversParticles: Particle[] = [];
let riversActiveDimension: DimensionId | null = null; // null = "One cloud"
let riversStageR = 150;
const camera = { scale: 1, cx: 0, cy: 0 };
let riversZoomEl: HTMLElement | null = null;
let riversEdgeEl: HTMLElement | null = null;
let riversTitleCountEl: HTMLElement | null = null;
let riversTitleNumEl: HTMLElement | null = null;
let riversTitleWordsEl: HTMLElement | null = null;
let riversOnPage = 0;
let riversOnPageShown = -1;
const EDGE_MARGIN = 18;

export type NeighborDir = "left" | "right" | "top" | "bottom" | "tl" | "tr" | "bl" | "br";
/** the page lying in each of the eight directions from the zoomed one (by name), and its slot (for its live count) */
export interface SlotNeighbors { left?: string; right?: string; top?: string; bottom?: string; tl?: string; tr?: string; bl?: string; br?: string; slots?: Partial<Record<NeighborDir, number>> }

// Hover/click on an individual bubble in the zoomed view is delegated to the
// host (clusterService), which owns the real tooltip and the session drawer.
interface BubbleHandlers {
  onHover: (visitorId: string, x: number, y: number) => void;
  onLeave: () => void;
  onClick: (visitorId: string) => void;
}
let bubbleHandlers: BubbleHandlers | null = null;
let hoveredVisitorId: string | null = null;

// ── the Sessions-tab rows pointing at a dot ───────────────────────────────────
// Hovering a row of the drawer's Sessions tab puts a static yellow ring on that visitor's bubble;
// selecting it (click) puts a pair of rippling rings on it. For a real d3 bubble the host draws those
// as SVG. A visitor that is a dot in a cloud has no visible SVG bubble (the host hides everything
// carrying its visitor_id), so the cloud draws the same two marks itself, on whichever canvas shows
// that dot: its slot's, and the zoomed view's. The ids are only a request: a mark is drawn while the
// visitor HAS a dot on that canvas, and not otherwise, so a visitor who lands in (or leaves) a cloud
// while its row is selected hands the mark over by itself.
let rowHoverId: string | null = null;
let rowSelectId: string | null = null;
let rowSelectColor = "#1b2433";
export function setRowHover(visitorId: string | null) {
  rowHoverId = visitorId;
}
export function setRowSelection(visitorId: string | null, color?: string) {
  rowSelectId = visitorId;
  if (visitorId && color) rowSelectColor = color;
}
/** What the cloud has been asked to mark (diagnostics and tests; nothing in the app reads it). */
export function getRowMarksDebug() {
  return { hover: rowHoverId, select: rowSelectId, color: rowSelectColor };
}
const ROW_RIPPLE_MS = 1600;   // the SVG ripple's own cycle (bubble-highlight-ripple), so both look the same
const ROW_RING_COLOR = "#ffd166";
/**
 * Draw the row marks for whichever of the two visitors this canvas has a dot for. `scale` is the canvas's
 * world-to-screen scale (1 for a slot, the camera's for the zoomed view): ring sizes and line widths are
 * kept in SCREEN pixels, as the SVG rings are (their stroke is non-scaling), whatever the zoom.
 */
function drawRowMarks(ctx: CanvasRenderingContext2D, now: number, scale: number, find: (id: string) => Particle | undefined) {
  if (!rowHoverId && !rowSelectId) return;
  ctx.save();
  const tau = Math.PI * 2;
  if (rowSelectId) {
    const p = find(rowSelectId);
    if (p) {
      const base = (p.r * scale * 1.15 + 3) / scale;   // the SVG ring's own size: 1.15x the bubble plus 3px
      ctx.strokeStyle = rowSelectColor;
      ctx.lineWidth = 1 / scale;
      for (let i = 0; i < 2; i++) {
        // two rings half a cycle apart, each growing to 2.4x while it fades from 0.8 to 0 (ease-out)
        const phase = ((((now - i * ROW_RIPPLE_MS / 2) % ROW_RIPPLE_MS) + ROW_RIPPLE_MS) % ROW_RIPPLE_MS) / ROW_RIPPLE_MS;
        const e = 1 - (1 - phase) * (1 - phase);
        ctx.globalAlpha = 0.8 * (1 - e);
        ctx.beginPath();
        ctx.arc(p.x, p.y, base * (1 + 1.4 * e), 0, tau);
        ctx.stroke();
      }
    }
  }
  if (rowHoverId) {
    const p = find(rowHoverId);
    if (p) {
      const radius = (p.r * scale * 1.15 + 3) / scale;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, tau);
      ctx.strokeStyle = ROW_RING_COLOR;
      ctx.globalAlpha = 0.35;     // the wide faint ring behind...
      ctx.lineWidth = 5 / scale;
      ctx.stroke();
      ctx.globalAlpha = 1;        // ...and the thin solid one on top
      ctx.lineWidth = 2 / scale;
      ctx.stroke();
    }
  }
  ctx.restore();
}

let bubbleColorProvider: ((visitorId: string) => string | null) | null = null;

// The dot takes the colour of that visitor's real bubble, so a cloud reads as
// the same population as the d3 cluster it replaces.
export function setBubbleColorProvider(fn: typeof bubbleColorProvider) {
  bubbleColorProvider = fn;
}

// What a visitor's real bubble looks like right now: its colour and, in icon mode,
// its category icon. Read together so the host does one lookup, not two.
interface BubbleLook { color: string | null; icon?: string; aspect?: number }
// Settings > "Show activity": off means no interaction glyphs, no action sparks and no
// away dimming, on every cloud and in the zoomed view (the app hides its own badges by a
// CSS class on the svg, which the clouds' canvases are outside of).
let activityProvider: (() => boolean) | null = null;
export function setActivityProvider(fn: typeof activityProvider) {
  activityProvider = fn;
}
let batchActivity = true;
let bubbleLookProvider: ((visitorId: string) => BubbleLook | null) | null = null;
export function setBubbleLookProvider(fn: typeof bubbleLookProvider) {
  bubbleLookProvider = fn;
}
const ICON_DOT_COLOR = "#2f5d7c";
function applyLook(p: Particle, vid: string) {
  const look = bubbleLookProvider?.(vid);
  if (look) {
    if (look.color) p.colorStr = look.color;
    // In icon mode the bubble has no fill colour, so a small dot (an icon shrinking toward an exit pill, past the size where it is
    // drawn as an icon) had nothing to be drawn in and fell back to the default TEAL. It takes the icons' own dark blue instead.
    else if (look.icon && !p.colorStr) p.colorStr = ICON_DOT_COLOR;
    p.iconUrl = look.icon;
    p.iconAspect = look.aspect;
    return;
  }
  const c = bubbleColorProvider?.(vid);
  if (c) p.colorStr = c;
}
function refreshColor(p: Particle, now: number) {
  if (p.colorT !== undefined && now - p.colorT < 1000) return;
  p.colorT = now;
  applyLook(p, p.visitorId);
}

// Small raster copies of each icon at the sizes actually being drawn (rounded to a
// device pixel, capped), made once and reused.
const spriteCache = new Map<string, HTMLCanvasElement>();
function iconSprite(url: string, img: HTMLImageElement, devWidth: number, aspect: number): HTMLCanvasElement | HTMLImageElement {
  const w = Math.max(2, Math.min(256, Math.round(devWidth)));
  const h = Math.max(2, Math.round(w * aspect));
  const key = `${url}|${w}|${h}`;
  let c = spriteCache.get(key);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = w; c.height = h;
  const x = c.getContext("2d");
  if (!x) return img;
  // Fitted INSIDE the box at the icon's own proportions, centred: exactly what the app's own
  // <image> does (preserveAspectRatio meet). drawImage with a destination of another shape
  // STRETCHES an SVG that declares its own width and height (the all-visitors person is 18x14
  // and came out squeezed to a square).
  const nw = img.naturalWidth || w, nh = img.naturalHeight || h;
  const k = Math.min(w / nw, h / nh);
  const dw = nw * k, dh = nh * k;
  x.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
  spriteCache.set(key, c);
  if (spriteCache.size > 600) spriteCache.delete(spriteCache.keys().next().value as string);
  return c;
}

// icon images, loaded once per URL; a dot is drawn as its icon only once the image is ready
const iconImages = new Map<string, { img: HTMLImageElement; ok: boolean }>();
function iconReady(url: string): HTMLImageElement | null {
  let e = iconImages.get(url);
  if (!e) {
    const img = new Image();
    e = { img, ok: false };
    const entry = e;
    img.onload = () => { entry.ok = img.naturalWidth > 0; };
    img.src = url;
    iconImages.set(url, e);
  }
  return e.ok ? e.img : null;
}

// A visitor who closes the site leaves the zoomed view as ONE bubble travelling in a STRAIGHT
// line from where its dot is on screen to the history bubble it docks into. It used to be two
// things in two coordinate frames (a dot scaled by the zoom, then at the stage's bottom edge the
// real, unscaled element taking over), which read as a dot vanishing and a bubble appearing.
// The host says where the dock is (client px) and how long the app's own dock move takes, so the
// arrival lands exactly when the app plays its burst there.
let leaveTargetProvider: ((visitorId: string) => { x: number; y: number; durationMs: number } | null) | null = null;
export function setLeaveTargetProvider(fn: typeof leaveTargetProvider) {
  leaveTargetProvider = fn;
}
interface Flight { p: Particle; r0: number; x0: number; y0: number; x1: number; y1: number; t0: number; dur: number }
const flights: Flight[] = [];
let flightCanvas: HTMLCanvasElement | null = null;
let flightCtx: CanvasRenderingContext2D | null = null;

function sizeFlightCanvas() {
  if (!flightCanvas) return;
  const dpr = canvasDpr();
  flightCanvas.width = Math.round(window.innerWidth * dpr);
  flightCanvas.height = Math.round(window.innerHeight * dpr);
  flightCanvas.style.width = `${window.innerWidth}px`;
  flightCanvas.style.height = `${window.innerHeight}px`;
}
function ensureFlightCanvas() {
  if (flightCanvas) return;
  const c = document.createElement("canvas");
  // the whole window, in client coordinates, above everything and never in the way of the mouse:
  // a leaving bubble crosses the stage's bottom edge and the history area in one piece
  c.style.cssText = "position:fixed;left:0;top:0;pointer-events:none;z-index:202;";
  document.body.appendChild(c);
  flightCanvas = c;
  flightCtx = c.getContext("2d");
  sizeFlightCanvas();
}
function destroyFlightCanvas() {
  flights.length = 0;
  flightCanvas?.remove();
  flightCanvas = null;
  flightCtx = null;
}
const worldToClient = (wx: number, wy: number) => {
  const cr = riversCanvas!.getBoundingClientRect();
  return {
    x: cr.left + riversOrigin.x + (wx - camera.cx) * camera.scale + riversCssW() / 2,
    y: cr.top + riversOrigin.y + (wy - camera.cy) * camera.scale + riversCssH() / 2,
  };
};

/** Start the one-bubble flight for a visitor closing the site. False if it cannot be done (the old path then applies). */
function startDepartureFlight(vid: string, now: number): boolean {
  const p = riversById.get(vid);
  if (!p || !riversCanvas) return false;
  const tgt = leaveTargetProvider?.(vid);
  if (!tgt) return false;
  const a = worldToClient(p.x, p.y);
  ensureFlightCanvas();
  const fp: Particle = { ...p, trail: [], traveler: false, exitTarget: undefined, following: false };
  flights.push({ p: fp, r0: p.r, x0: a.x, y0: a.y, x1: tgt.x, y1: tgt.y, t0: now, dur: Math.max(250, tgt.durationMs) });
  // the dot leaves the stage's canvas now; the flight draws it from here on
  riversParticles = riversParticles.filter((q) => q !== p);
  riversById.delete(vid);
  return true;
}

function renderFlights(now: number) {
  if (!flightCtx || !flightCanvas) return;
  const dpr = canvasDpr();
  const ctx = flightCtx;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  for (let i = flights.length - 1; i >= 0; i--) {
    const f = flights[i];
    const t = (now - f.t0) / f.dur;
    if (t >= 1) { flights.splice(i, 1); continue; }
    const e = easeInOutCubic(Math.max(0, t));
    f.p.x = f.x0 + (f.x1 - f.x0) * e;
    f.p.y = f.y0 + (f.y1 - f.y0) * e;
    f.p.r = Math.max(1, f.r0 * camera.scale);   // the size it had in the view, on screen
    const fade = Math.max(0, 1 - Math.max(0, t - 0.9) / 0.1);          // gone as it reaches the dock
    drawParticlesBatched(ctx, [f.p], now, null, () => 0.88 * fade, 1, undefined);
  }
  renderArrivals(now);
}

// A NEW visitor travels from the spawn point (the category's dot in the sidebar, where the app
// spawns its real bubble) INTO the zoomed cluster as one bubble in a straight line, over the
// sidebar and into the stage, drawn on the same full-window layer as the departures.
let spawnPointProvider: ((visitorId: string) => { x: number; y: number; at?: number } | null) | null = null;
// In a replay the day is compressed, so visits last about a second: the host gives the flight length that fits them (null: live, normal).
let arrivalFlightProvider: (() => number | null) | null = null;
export function setArrivalFlightProvider(fn: (() => number | null) | null) { arrivalFlightProvider = fn; }
const arriveMs = () => { const fitted = arrivalFlightProvider?.(); return fitted != null ? fitted : moveMs(ARRIVE_BASE_MS); };
export function setSpawnPointProvider(fn: typeof spawnPointProvider) {
  spawnPointProvider = fn;
}
const ARRIVE_BASE_MS = 650;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
let arrivalsDrawn: Array<{ id: string; x: number; y: number; x0: number; y0: number; ageMs: number }> = [];

/** Where a flying-in visitor is headed, in world coords: the spot it will occupy in the zoomed view. */
function arrivalHome(p: Particle): { x: number; y: number } | null {
  if (riversActiveDimension) return computeTarget(p, riversCenter.x, riversCenter.y, riversActiveDimension, riversRegions);
  const st = stageRect();
  const cloud = slotClouds.get(riversSlotIndex);
  if (p.src) return { x: riversCenter.x + p.src.x, y: riversCenter.y + p.src.y };
  const a = riversArrived.get(p.visitorId);
  if (a) return { x: riversCenter.x + a.x, y: riversCenter.y + a.y };
  const pt = cloud?.pendingTarget.get(p.visitorId);
  if (pt) return { x: pt.x - st.offX, y: pt.y - st.offY };
  return null;
}

function renderArrivals(now: number) {
  arrivalsDrawn = [];
  if (!flightCtx || !riversCanvas) return;
  for (const p of riversParticles) {
    const a = p.arrive;
    if (!a) continue;
    const h = arrivalHome(p);
    if (h) { p.x = h.x; p.y = h.y; p.sx = h.x; p.sy = h.y; }
    // the spot it is heading for is not decided yet: it waits at the spawn point (briefly)
    if (!h) {
      if (now - a.tc > 1500) { p.arrive = undefined; continue; }
      a.t0 = -1;
    } else if (a.t0 < 0) a.t0 = now;
    const t = a.t0 < 0 ? 0 : (now - a.t0) / arriveMs();
    if (t >= 1) {                                       // arrived: from here it is an ordinary dot, already at its spot
      if (h) { p.tweenStartX = h.x; p.tweenStartY = h.y; p.tweenT0 = now; p.tweenDur = 1; }
      p.arrive = undefined;
      continue;
    }
    const home = h ? worldToClient(h.x, h.y) : { x: a.x0, y: a.y0 };
    const e = easeOutCubic(Math.max(0, t));
    const x = a.x0 + (home.x - a.x0) * e;
    const y = a.y0 + (home.y - a.y0) * e;
    arrivalsDrawn.push({ id: p.visitorId, x, y, x0: a.x0, y0: a.y0, ageMs: now - a.tc });
    // a flight is drawn from a COPY of the dot, so the colour must be settled first: a visitor still being created had none and flew in teal
    if (!p.colorStr) applyLook(p, p.visitorId);
    const fp: Particle = { ...p, x, y, r: Math.max(1, (riversActiveDimension && riversDotR > 0 ? riversDotR : p.r) * camera.scale), trail: [], traveler: false, arrive: undefined };
    if (!fp.colorStr) fp.colorStr = ICON_DOT_COLOR;
    drawParticlesBatched(flightCtx, [fp], now, null, () => 0.88, 1, undefined);
  }
}

// Where a visitor's bubble ELEMENT is on screen right now (svg coords), whether or not the
// app still lists the visitor. When a visitor closes, the app drops its bubble from its list
// at once and then animates the element down to the history area; the zoomed view follows
// that element, so the descent can be seen.
let elementPosProvider: ((visitorId: string) => { x: number; y: number } | null) | null = null;
export function setElementPositionProvider(fn: typeof elementPosProvider) {
  elementPosProvider = fn;
}
const leaverPos = (vid: string) => bubblePosProvider?.(vid) ?? (riversLeaving.has(vid) ? elementPosProvider?.(vid) ?? null : null);
let bubblePosProvider: ((visitorId: string) => { x: number; y: number } | null) | null = null;

// Where a visitor's real bubble currently is (SVG coords), so a cloud forming
// over a slot starts each dot exactly there and blooms outward from it.
// When a dot leaves its cloud (visitor moved page / closed), the host puts the
// real d3 bubble back where the dot was so the app's own motion takes over.
let dotReleaseHandler: ((visitorId: string, svgX: number, svgY: number) => void) | null = null;
// Why an arrival did or did not fly in (read from the staging stamp). A brand-new visitor either flew in from its spawn point, or
// appeared in place (no spawn point known / the view had only just opened), or was ALREADY in the cloud the first time this view saw
// it (landed at once: the first 30s after a page load, a view that has just opened, or a real bubble that glided in before the view's
// next refresh); a visitor who came from another page is counted apart.
const arrivalStats = { flew: 0, inPlace: 0, fromPage: 0, noSpawn: 0, quiet: 0, alreadyThere: 0 };
let ghostHidden = 0;
/** a dot with no colour of its own AND no real bubble anywhere is a ghost (a visitor the board no longer has): it is not drawn */
function isGhost(p: Particle): boolean {
  if (p.colorStr || !p.visitorId) return false;
  applyLook(p, p.visitorId);
  if (p.colorStr) return false;
  return !bubblePosProvider?.(p.visitorId) && !elementPosProvider?.(p.visitorId);
}
const singleViewStats = { opened: 0, repaired: 0, failed: 0 };
const cloudStats = { landed: 0, moved: 0, left: 0, dissolved: 0, repaired: 0 };
// bumped whenever any visitor's page (or the page->slot mapping) may have changed
let pageVersion = 0;
function releaseDot(visitorId: string, svgX: number, svgY: number, reason: "moved" | "left" | "dissolved" = "moved", fromSlot = -1) {
  cloudStats[reason]++;
  // The zoomed view shows THIS page's visitors. A visitor released from ANOTHER page's cloud (moving on, or closing the site) is not
  // followed or revealed here: those were the bubbles moving and closing that crept into the view. Their real bubble is still handed
  // back to the grid below.
  if (riversMode && (fromSlot < 0 || fromSlot === riversSlotIndex)) {
    const now = performance.now();
    if (!(reason === "left" && startDepartureFlight(visitorId, now))) {
      riversLeaving.set(visitorId, now);
      riversLeaveReason.set(visitorId, reason);
    }
  }
  dotReleaseHandler?.(visitorId, svgX, svgY);
}
export function setDotReleaseHandler(fn: typeof dotReleaseHandler) {
  dotReleaseHandler = fn;
}

// A visitor whose real bubble is on its way into a cloud is aimed at the exact
// honeycomb spot reserved for it; the host uses this as that bubble's d3 target.
let bubbleHideHandler: (() => void) | null = null;
export function setBubbleHideHandler(fn: typeof bubbleHideHandler) {
  bubbleHideHandler = fn;
}
let pendingTargetsHandler: (() => void) | null = null;
export function setPendingTargetsHandler(fn: typeof pendingTargetsHandler) {
  pendingTargetsHandler = fn;
}
export function getPendingTarget(visitorId: string): { x: number; y: number } | undefined {
  for (const cloud of slotClouds.values()) {
    const t = cloud.pendingTarget.get(visitorId);
    if (t) return t;
  }
  return undefined;
}

/**
 * Where a visitor stands with the clouds, for the staging diagnostic: has a dot
 * ("landed"), is a bubble still flying in ("pending", with how long and how far from
 * its spot), just arrived ("arrived"), or the clouds know nothing about it ("none").
 */
export function getVisitorCloudInfo(visitorId: string): { state: "landed" | "arrived" | "pending" | "none"; ageMs: number; distToTarget: number } {
  const now = performance.now();
  for (const cloud of slotClouds.values()) {
    if (cloud.byId.has(visitorId)) return { state: "landed", ageMs: 0, distToTarget: 0 };
    if (cloud.arrived.has(visitorId)) return { state: "arrived", ageMs: 0, distToTarget: 0 };
    const since = cloud.pending.get(visitorId);
    if (since !== undefined) {
      const tgt = cloud.pendingTarget.get(visitorId);
      const bp = bubblePosProvider?.(visitorId);
      const dist = tgt && bp ? Math.hypot(bp.x - tgt.x, bp.y - tgt.y) : -1;
      return { state: "pending", ageMs: now - since, distToTarget: dist };
    }
  }
  return { state: "none", ageMs: 0, distToTarget: 0 };
}

/** True on the staging build (the one that shows the build stamp): where diagnostics may draw. */
export function isStagingDiagnostic(): boolean {
  return !!stampEl;
}

let playbackStatsProvider: (() => string) | null = null;
export function setPlaybackStatsProvider(fn: (() => string) | null) { playbackStatsProvider = fn; }
let stampExtra: (() => string) | null = null;
export function setStampExtra(fn: (() => string) | null) {
  stampExtra = fn;
}

/** Visitors that currently have a dot in a cloud (their real bubble is handed off). */
export function getLandedVisitorIds(): Set<string> {
  const out = new Set<string>();
  for (const cloud of slotClouds.values()) {
    for (const p of cloud.particles) out.add(p.visitorId);
    for (const v of cloud.arrived.keys()) out.add(v);
  }
  return out;
}

export function setBubblePositionProvider(fn: typeof bubblePosProvider) {
  bubblePosProvider = fn;
}

export function setBubbleHandlers(h: BubbleHandlers | null) {
  bubbleHandlers = h;
}

/** a dot of a group Hide unknown leaves out of the grouped view: not drawn, not hovered (the visitor is still on the stage and in the list: this is only the zoomed grouping) */
function hiddenInZoom(p: Particle): boolean {
  if (!riversActiveDimension) return false;
  const def = dimDef(riversActiveDimension);

  return !!def.dynamic && def.dynamic.hides(def.getCategory(p));
}
function nearestBubble(mx: number, my: number): string | null {
  const W = riversCssW(), H = riversCssH();
  let best: string | null = null;
  let bestD = 1e9;
  for (let i = 0; i < riversParticles.length; i++) {
    const p = riversParticles[i];
    if (p.traveler || p.arrive) continue;
    if (hiddenInZoom(p)) continue;
    const sx = (p.x - camera.cx) * camera.scale + W / 2;
    const sy = (p.y - camera.cy) * camera.scale + H / 2;
    const dx = sx - mx, dy = sy - my;
    const rr = Math.max(0.6, p.r * camera.scale) + 5;
    if (dx > rr || dx < -rr || dy > rr || dy < -rr) continue;
    const d = Math.hypot(dx, dy);
    if (d < rr && d < bestD) { best = p.visitorId; bestD = d; }
  }
  return best;
}
// In a page's card the canvas covers the whole grey stage (so a dot can leave the card across a sliver and fade at the stage's edge),
// while "the view" the camera, layout and exits work in is the CARD: riversOrigin is the card's top-left inside the canvas and
// riversStage its size. Elsewhere the canvas is the view and the origin is zero.
let riversOrigin = { x: 0, y: 0 };
let riversStage: { w: number; h: number } | null = null;
let riversCardGeo = { ix: 0, it: 0, ib: 0, cardW: 0, cardH: 0, panelW: 0, panelH: 0 };
const riversCssW = () => riversStage ? riversStage.w : (riversCanvas?.width ?? 0) / canvasDpr();
const riversCssH = () => riversStage ? riversStage.h : (riversCanvas?.height ?? 0) / canvasDpr();
let riversDragging = false;
let riversDragMoved = false;
let riversDragX = 0;
let riversDragY = 0;
let riversRegions: Region[] = [];

// animation loop handle
let rafHandle = 0;
let lastTs = 0;

// rivers view UI elements (back button + dimension buttons)
let riversOverlay: HTMLElement | null = null;

// ── easing ────────────────────────────────────────────────────────────────────

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ── particle factories ────────────────────────────────────────────────────────

function makeParticle(
  x: number, y: number, now: number,
  visitorId: string,
  data: Partial<Particle> = {}
): Particle {
  const ang = Math.random() * Math.PI * 2;
  const dur = TWEEN_DUR_BASE + Math.random() * TWEEN_DUR_JITTER;
  const look = bubbleLookProvider?.(visitorId);
  const initColor = (look ? look.color : bubbleColorProvider?.(visitorId)) || undefined;
  return {
    colorStr: initColor,
    iconUrl: look?.icon,
    iconAspect: look?.aspect,
    x, y,
    r: PARTICLE_RADIUS + Math.random() * PARTICLE_RADIUS_JITTER,
    phase: Math.random() * Math.PI * 2,
    speedMul: 0.6 + Math.random() * 0.6,
    radialFrac: Math.random(),
    homeAngle: ang,
    vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
    opacity: 0.95, // d3 bubbles are solid
    tweenStartX: x, tweenStartY: y, tweenT0: now, tweenDur: dur,
    trail: [],
    visitorId,
    interactionState: visitorInteraction.get(visitorId) || "focused",
    browser: visitorData.get(visitorId)?.browser || "Other",
    newReturn: (visitorData.get(visitorId)?.visitor_type === "new") ? "New" : "Returning",
    converted: !!(visitorData.get(visitorId)?.goalReached),
    unidentified: !!(visitorData.get(visitorId)?.unidentified),
    pageName: visitorPage.get(visitorId) || "",
    traveler: false, travelerAlpha: 1, travelerDir: 1,
    ...data,
  };
}

function startTween(p: Particle, now: number, dur?: number) {
  p.tweenStartX = p.x;
  p.tweenStartY = p.y;
  p.tweenT0 = now;
  // A move to a category (or between views) is capped at CATEGORY_MOVE_MAX_MS whatever Bubble Speed is set to, and it starts at speed
  // (see tickParticles): at the old 1.8 to 2.7s plus the Bubble Speed delay, with a slow ease-in, a visitor often moved or closed
  // before its dot got anywhere near its category.
  p.tweenDur = Math.min(CATEGORY_MOVE_MAX_MS, moveMs(dur ?? (MODE_SWITCH_DUR_BASE + Math.random() * MODE_SWITCH_DUR_JITTER)));
  p.trail = []; // no motion trails: nothing is drawn that isn't a dot
}

// ── region layout ─────────────────────────────────────────────────────────────

// Grouped view: every category owns a FIXED spot (its order in the dimension's own
// category list; the first, "default" category sits in the middle). Spots never
// depend on counts, so nothing swaps or jumps when the numbers change. The ring
// only grows (a high-water mark) if a region ever gets bigger than before, and it
// stays inside the free band under the controls so it never sits behind them.
let riversStageDims = { W: 1000, H: 600 };
// Every region has the SAME fixed radius, computed from the stage alone. Spots,
// titles and sizes therefore never move; a crowded category shrinks its dots
// to fit instead of growing its region.
let riversRegionR = 90;
const PILL_H = 26;              // a neighbour pill
const REGION_TITLE_H = 46;       // room for a category's label (its count above its name) above its disc
const REGION_CELL_PAD = 10;
const ORDER_SETTLE_MS = 2000;     // how long a freshly chosen grouping keeps re-sorting its regions by count before the order is frozen
const REGION_MIN_R = 34;          // the smallest disc a long list of groups is drawn at; past that the grid runs on below the view and the person pans
let riversCellW = 0;              // an overflowing layout's cell width: a name wider than this is shortened with an ellipsis
let riversGridExtra = 0;          // how far the grouped layout runs past the bottom of the view (0 unless a long list overflows it)

function layoutRegions(
  particles: Particle[],
  dim: DimensionId,
  cx: number, cy: number
): Region[] {
  const def = dimDef(dim);
  const counts: Record<string, number> = {};
  for (const p of particles) {
    const cat = def.getCategory(p);
    counts[cat] = (counts[cat] || 0) + 1;
  }
  const cats = (def.dynamic ? def.dynamic.order(counts) : def.categories).filter((c) => !def.dynamic?.hides(c));   // (Hide unknown leaves the "no data" groups out)
  const n = cats.length;
  const { W, H } = riversStageDims;

  // The FREE AREA: the visible stage minus the controls on top, the bottom pill and
  // the left/right neighbour pills. Every category gets a cell of an even grid over
  // it, so the regions use all the room there is, never touch each other or their
  // titles, and never sit under a control. Cells depend only on the number of
  // categories and the stage size, never on counts, so nothing ever moves when the
  // numbers change.
  // Margins follow what is really there: a neighbour pill only costs room on its own
  // side, and its width follows the page name (11px monospace plus arrow and padding).
  const pillW = (t?: string) => (t ? (t.length + 2) * 6.7 + 26 : 0);
  // (in the card the neighbour pills are OUTSIDE it: only the top row of controls and the page title at the bottom cost room)
  const card = cardMode();
  const padL = card ? 24 : riversNeighbors.left ? 10 + pillW(riversNeighbors.left) + 14 : 24;
  const padR = card ? 24 : riversNeighbors.right ? 10 + pillW(riversNeighbors.right) + 14 : 24;
  const padT = card ? riversControlsBottom + 14 : riversNeighbors.top ? riversControlsBottom + 20 + PILL_H + 18 : riversControlsBottom + 20;
  const padB = card ? 72 : riversNeighbors.bottom ? 10 + PILL_H + 14 : 24;   // (card: room for the title at the bottom left)
  const x0 = cx - W / 2 + padL, x1 = cx + W / 2 - padR;
  const y0 = cy - H / 2 + padT, y1 = cy + H / 2 - padB;
  const bw = Math.max(240, x1 - x0), bh = Math.max(180, y1 - y0);

  // the grid shape that gives the biggest regions (and, for a long list of groups, a grid bigger than the view: see computeRegionGrid)
  // a long name under its disc (a referring link) needs a wider cell than the disc: about 6.6px a letter at the label's size, never wider than 160
  const longest = cats.reduce((m, c) => Math.max(m, c.length), 0);
  const grid = computeRegionGrid(n, bw, bh, { titleH: REGION_TITLE_H, cellPad: REGION_CELL_PAD, minR: REGION_MIN_R, fitMin: 28, fitMax: 100, allowOverflow: !!def.dynamic, minCellW: Math.min(160, longest * 6.6 + 10) });
  riversCellW = grid.cw;
  riversRegionR = grid.R;
  riversGridExtra = grid.extra;
  const { cols, rows, cw, ch, R } = grid;
  const colorOf = (cat: string) => (def.dynamic ? def.dynamic.colorFor(cat) : undefined) || def.colors[cat] || DEFAULT_COLOR;

  return cats.map((cat, i) => {
    const row = Math.floor(i / cols);
    const inRow = row === rows - 1 ? n - cols * (rows - 1) : cols;
    const col = i - row * cols;
    // a short last row is centred (not in an overflowing grid, which reads left to right like text)
    const rowX0 = grid.overflow ? x0 : x0 + (bw - inRow * cw) / 2;
    const x = rowX0 + (col + 0.5) * cw;
    const y = y0 + row * ch + REGION_TITLE_H + (ch - REGION_TITLE_H) / 2;
    return { label: cat, x, y, r: R, count: counts[cat] || 0, color: colorOf(cat) };
  });
}

function breatheRegions(regions: Region[], particles: Particle[], dim: DimensionId) {
  const def = dimDef(dim);
  const counts: Record<string, number> = {};
  for (const p of particles) {
    const cat = def.getCategory(p);
    counts[cat] = (counts[cat] || 0) + 1;
  }
  for (const reg of regions) {
    const target = regionRadius(counts[reg.label] || 0);
    reg.count = counts[reg.label] || 0;
    reg.r += (target - reg.r) * REGION_BREATHE_ALPHA;
  }
}

// ── static packed layout ──────────────────────────────────────────────────────
// Every dot owns a fixed spot on an even honeycomb, filled from the centre out so
// the cluster is always a round disc. The honeycomb is laid out at a CONTINUOUS
// spacing: as loose as the real bubbles while the disc fits its box, then it
// tightens smoothly (dots shrink, the disc keeps its size) however many visitors
// arrive. The same disc holds 50 or 50,000 real dots; there is no sampling and no
// size limit. A dot only moves when it arrives, leaves, or the disc re-fits.
//
// The honeycomb is ONE unit lattice shared by every pack (spot i is at
// unit[i] * spacing), so re-fitting is a uniform scale, and the neighbours of a
// spot are a table lookup. Everything per arrival / departure is O(1).

interface PackSlot { x: number; y: number }

interface Lattice {
  n: number;
  x: Float32Array;     // unit-spacing position on the perfect honeycomb
  y: Float32Array;
  jx: Float32Array;    // each spot's own fixed offset, -0.5..0.5, scaled by the pack's jitter
  jy: Float32Array;
  rad: Float32Array;   // distance from the centre (without the offset)
  nbr: Int32Array;     // 6 neighbours per spot, -1 where there is none
}

const HEX_DY = 0.8660254037844386;
const NBR_DA = [1, -1, 0, 0, 1, -1];
const NBR_DB = [0, 0, 1, -1, -1, 1];

let lattice: Lattice | null = null;

function hash01(i: number): number {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function ensureLattice(minCount: number): Lattice {
  if (lattice && lattice.n >= minCount) return lattice;
  const want = Math.max(minCount, lattice ? lattice.n * 2 : 2048);
  const Rg = Math.sqrt((HEX_DY * want) / Math.PI) * 1.05 + 2;

  interface Pt { a: number; b: number; x: number; y: number; d: number; ang: number }
  const pts: Pt[] = [];
  const bMax = Math.ceil(Rg / HEX_DY) + 1;
  for (let b = -bMax; b <= bMax; b++) {
    const y = b * HEX_DY;
    const aLo = Math.floor(-b / 2 - Rg) - 1;
    const aHi = Math.ceil(-b / 2 + Rg) + 1;
    for (let a = aLo; a <= aHi; a++) {
      const x = a + b / 2;
      const d = Math.hypot(x, y);
      if (d <= Rg) pts.push({ a, b, x, y, d, ang: Math.atan2(y, x) });
    }
  }
  pts.sort((p, q) => p.d - q.d);

  // Within each ring, fill in an evenly spread order (bit-reversed rank by angle), so
  // that however many dots there are, the partly filled outer ring is spread all the
  // way round instead of being an arc on one side.
  const ordered: Pt[] = [];
  for (let i = 0; i < pts.length; ) {
    const d0 = pts[i].d;
    let j = i + 1;
    while (j < pts.length && pts[j].d - d0 < 0.02) j++;
    const ring = pts.slice(i, j).sort((p, q) => p.ang - q.ang);
    const m = ring.length;
    if (m > 2) {
      const bits = Math.ceil(Math.log2(m));
      const rev = (k: number) => {
        let r = 0;
        for (let bt = 0; bt < bits; bt++) if (k & (1 << bt)) r |= 1 << (bits - 1 - bt);
        return r;
      };
      const keyed = ring.map((p, k) => ({ p, key: rev(k) }));
      keyed.sort((p, q) => p.key - q.key);
      for (const o of keyed) ordered.push(o.p);
    } else {
      for (const p of ring) ordered.push(p);
    }
    i = j;
  }

  const n = ordered.length;
  const x = new Float32Array(n), y = new Float32Array(n), rad = new Float32Array(n);
  const jx = new Float32Array(n), jy = new Float32Array(n);
  const OFF = Math.ceil(Rg) + 4;
  const W = 2 * OFF + 1;
  const at = new Int32Array(W * W).fill(-1);
  for (let i = 0; i < n; i++) {
    const p = ordered[i];
    // a perfect lattice reads as mechanical; a FIXED offset per spot gives the
    // irregular-but-even look of a packed cluster (see jitterAt for how much)
    x[i] = p.x; y[i] = p.y;
    jx[i] = hash01(i) - 0.5;
    jy[i] = hash01(i + 977) - 0.5;
    rad[i] = p.d;
    at[(p.a + OFF) * W + (p.b + OFF)] = i;
  }
  const nbr = new Int32Array(n * 6).fill(-1);
  for (let i = 0; i < n; i++) {
    const p = ordered[i];
    for (let d = 0; d < 6; d++) {
      const a = p.a + NBR_DA[d] + OFF, b = p.b + NBR_DB[d] + OFF;
      if (a >= 0 && a < W && b >= 0 && b < W) nbr[i * 6 + d] = at[a * W + b];
    }
  }
  lattice = { n, x, y, jx, jy, rad, nbr };
  return lattice;
}

interface Pack {
  s: number;                          // spacing between neighbouring spots (px)
  r: number;                          // dot radius (px)
  J: number;                          // how far each spot may sit off the perfect honeycomb (fraction of s)
  occ: Array<string | undefined>;     // spot -> visitor
  owner: Map<string, number>;         // visitor -> spot
  holes: Set<number>;                 // free spots below the frontier
  frontier: number;                   // 1 + the highest occupied spot
  lastCompact: number;
}

function newPack(): Pack {
  return { s: 0, r: 0, J: 0.1, occ: [], owner: new Map(), holes: new Set(), frontier: 0, lastCompact: 0 };
}

// Dot sizes start at the SAME radius the d3 bubbles are drawn at (read from the
// app, so an icon-mode change follows), a touch smaller. Nothing here is
// decorative: a dot is a real visitor, in the real bubble's colour, at the real
// bubble's size. No random translucency, glow, breathing or drift.
const DOT_SIZE_VS_BUBBLE = 0.95;
const AWAY_ALPHA = 0.55; // away is a real state: the dot is dimmed (and slashed)
const DOT_MIN_R = 0.25;  // below this the dots overlap instead of shrinking further
// Dots keep their size while the disc has room. Once it is full they shrink FASTER
// than the spacing does (radius ~ spacing^1.19), so a very dense cloud stays a field
// of individual dots (about a quarter of the disc covered at ~10,000) and does not
// turn into a solid blob. Measured from the reference mockup: 26% at 10,247.
const DOT_SHRINK_POWER = 1.19;
let bubbleRadiusProvider: (() => number) | null = null;
export function setBubbleRadiusProvider(fn: (() => number) | null) {
  bubbleRadiusProvider = fn;
}
const tierSpacing = (r: number) => 2 * r + (r >= 3 ? 2.2 : 1.4);

// How loose the person's "Page Cluster Spacing" setting lets a cloud be, as a spot-to-spot distance in px (see clusterSpacing.ts). Never makes a cloud TIGHTER than
// the dots allow: the pack's loosest spacing is the larger of this and tierSpacing.
let clusterSpacingProvider: (() => number) | null = null;
export function setClusterSpacingProvider(fn: (() => number) | null) {
  clusterSpacingProvider = fn;
}

function dotBase(): number {
  return (bubbleRadiusProvider?.() || 4) * DOT_SIZE_VS_BUBBLE;
}

function radiusAt(s: number): number {
  const base = dotBase();
  const sMax = tierSpacing(base);
  return Math.max(DOT_MIN_R, base * Math.pow(Math.min(1, s / sMax), DOT_SHRINK_POWER));
}

// How far a spot may sit off the perfect honeycomb. Big dots never touch (strict, so
// a small cloud stays tidy). Once a dot is only a couple of device pixels across, the
// honeycomb's straight rows beat against the pixel grid and show up as stripes
// (measured: 35-38% brightness swing between pixel rows at 2,140 and 10,000), so the
// offsets open up and the field reads as even scatter.
function jitterAt(s: number, r: number): number {
  const rDev = r * canvasDpr();
  const t = Math.min(1, Math.max(0, (rDev - 2.2) / 1.3));   // 0 = a speck, 1 = a dot you can pick out
  const slack = 0.9 + t * (2.15 - 0.9);
  const cap = 0.8 + t * (0.4 - 0.8);
  return Math.min(cap, Math.max(0.05, 1 - (slack * r) / s));
}

/** The spacing this pack needs so its disc fits a box of radius Rbox (never looser than full size). */
function packFitS(pack: Pack, Rbox: number): number {
  const sMax = Math.max(tierSpacing(dotBase()), clusterSpacingProvider?.() || 0);
  const lat = ensureLattice(pack.frontier + 8);
  const rho = pack.frontier > 0 ? lat.rad[pack.frontier - 1] : 0;
  const extent = (sp: number) => {
    const r = radiusAt(sp);
    return sp * (rho + 0.71 * jitterAt(sp, r)) + r;   // furthest edge of the outermost dot
  };
  if (extent(sMax) <= Rbox) return sMax;
  let lo = 0.05, hi = sMax;
  for (let i = 0; i < 22; i++) {
    const mid = (lo + hi) / 2;
    if (extent(mid) > Rbox) hi = mid; else lo = mid;
  }
  return lo;
}

/** Set the pack's spacing and dot size. `sShared` lets several packs use one size. */
function packFit(pack: Pack, Rbox: number, sShared?: number) {
  pack.s = sShared !== undefined ? sShared : packFitS(pack, Rbox);
  pack.r = radiusAt(pack.s);
  pack.J = jitterAt(pack.s, pack.r);
}

function packFree(pack: Pack, idx: number) {
  pack.occ[idx] = undefined;
  if (idx === pack.frontier - 1) {
    pack.frontier = idx;
    while (pack.frontier > 0 && pack.occ[pack.frontier - 1] === undefined) {
      pack.frontier--;
      pack.holes.delete(pack.frontier);
    }
  } else {
    pack.holes.add(idx);
  }
}

function packRelease(pack: Pack, vid: string) {
  const idx = pack.owner.get(vid);
  if (idx === undefined) return;
  pack.owner.delete(vid);
  packFree(pack, idx);
}

/**
 * Reserve a spot for a visitor. The cluster is always the first n spots of the fill
 * order (a round disc): a new dot takes the free spot, among the first n+3, nearest
 * to where it is: an inside hole if there is one, otherwise the next ring position.
 */
function packClaim(pack: Pack, vid: string, here: { x: number; y: number } | undefined): number {
  const have = pack.owner.get(vid);
  if (have !== undefined) return have;
  const lat = ensureLattice(pack.frontier + 8);
  const n = pack.owner.size;
  const hx = here ? here.x : 0, hy = here ? here.y : 0;
  const s = pack.s;
  let best = -1, bestD = Infinity, seen = 0;
  for (const i of pack.holes) {
    if (i >= n + 3) continue;
    if (++seen > 64) break;
    const d = Math.hypot(lat.x[i] * s - hx, lat.y[i] * s - hy);
    if (d < bestD) { bestD = d; best = i; }
  }
  for (let i = pack.frontier; i < pack.frontier + 3; i++) {
    const d = Math.hypot(lat.x[i] * s - hx, lat.y[i] * s - hy);
    if (d < bestD) { bestD = d; best = i; }
  }
  if (best < 0) best = pack.frontier;
  pack.occ[best] = vid;
  pack.owner.set(vid, best);
  if (best >= pack.frontier) {
    for (let k = pack.frontier; k < best; k++) pack.holes.add(k);
    pack.frontier = best + 1;
  } else {
    pack.holes.delete(best);
  }
  return best;
}

/** Make the pack hold exactly these visitors. Returns how many it had to change. */
function packReconcile(
  pack: Pack, vids: string[], posOf: (vid: string) => { x: number; y: number } | undefined
): number {
  const live = new Set(vids);
  let changed = 0;
  const gone: string[] = [];
  pack.owner.forEach((_, v) => { if (!live.has(v)) gone.push(v); });
  for (const v of gone) { packRelease(pack, v); changed++; }
  for (const v of vids) {
    if (!pack.owner.has(v)) { packClaim(pack, v, posOf(v)); changed++; }
  }
  return changed;
}

function packMove(pack: Pack, from: number, to: number) {
  const vid = pack.occ[from]!;
  pack.occ[to] = vid;
  pack.owner.set(vid, to);
  pack.holes.delete(to);
  packFree(pack, from);
}

/**
 * Self-correction: the occupied spots should be exactly the first n of the fill
 * order. A hole inside is closed by sliding in the neighbour just outside it (one
 * spacing), which moves the hole outward, until it leaves the disc. A whole chain
 * of one-spacing slides runs at once, so a hole closes as one coherent shift.
 */
function packCompact(pack: Pack, maxMoves: number): number {
  if (pack.holes.size === 0) return 0;
  const lat = ensureLattice(pack.frontier + 8);
  const n = pack.owner.size;
  const batch: number[] = [];
  for (const h of pack.holes) {
    if (h < n) { batch.push(h); if (batch.length >= 512) break; }
  }
  let moves = 0;
  for (const h0 of batch) {
    if (moves >= maxMoves) break;
    if (!pack.holes.has(h0)) continue;
    let h = h0;
    while (h < n) {
      let bj = -1;
      for (let d = 0; d < 6; d++) {
        const j = lat.nbr[h * 6 + d];
        if (j > h && j > bj && pack.occ[j] !== undefined) bj = j;
      }
      if (bj < 0) break;
      packMove(pack, bj, h);
      moves++;
      if (!pack.holes.has(bj)) { h = -1; break; }   // the vacated spot was the rim: nothing left to close
      h = bj;
    }
    if (h >= 0 && h < n && pack.holes.has(h) && pack.frontier - 1 > h) {
      // nobody just outside: take the outermost dot
      packMove(pack, pack.frontier - 1, h);
      moves++;
    }
  }
  return moves;
}

function packPos(pack: Pack, vid: string): PackSlot {
  const i = pack.owner.get(vid);
  if (i === undefined) return { x: 0, y: 0 };
  const lat = ensureLattice(i + 1);
  return { x: (lat.x[i] + lat.jx[i] * pack.J) * pack.s, y: (lat.y[i] + lat.jy[i] * pack.J) * pack.s };
}

// radius of the region a group of `cnt` dots occupies, for the rivers layout
let riversPackS = 13;
function regionRadius(cnt: number): number {
  return riversPackS * Math.sqrt((0.866 * Math.max(cnt, 1)) / Math.PI) + riversPackS;
}

// ── compute target position for a particle ────────────────────────────────────

function computeTarget(
  p: Particle, cx: number, cy: number,
  dim: DimensionId | null, regions: Region[]
): { x: number; y: number } {
  if (p.exitTarget) return p.exitTarget;
  const px = p.packX ?? 0, py = p.packY ?? 0;
  if (!dim || regions.length === 0) return { x: cx + px, y: cy + py };
  const def = dimDef(dim);
  const cat = def.getCategory(p);
  const reg = regions.find((r) => r.label === cat);
  if (!reg) return { x: cx + px, y: cy + py };
  return { x: reg.x + px, y: reg.y + py };
}

// ── draw one particle ─────────────────────────────────────────────────────────

let lastKnownDotColor = "";
function particleColor(p: Particle, dim: DimensionId | null): string {
  // the bubble's own colour; interaction state is the glyph, never the fill
  // A dot whose bubble gave no colour (a visitor who moved here from another page, still being followed) used to be the fallback
  // TEAL, which matched nothing on screen. It takes the colour the other dots are drawn in.
  if (p.colorStr) { lastKnownDotColor = p.colorStr; return p.colorStr; }
  return lastKnownDotColor || INTERACTION_COLORS[p.interactionState] || DEFAULT_COLOR;
}

const SLOT_ACTION_LIFE_MS = 550; // mockup: action life 550

/** The converted star and the unidentified "?" on a dot, as the grid draws them on its bubbles (read at draw time: their data can arrive after the dot is made). */
function drawBadges(ctx: CanvasRenderingContext2D, p: Particle, a: number, dim: DimensionId | null) {
  if (p.traveler) return;
  // a converted visitor's star, as in the grid: centred over a plain dot, tucked into the bottom-right corner of an icon
  const vd = p.visitorId ? visitorData.get(p.visitorId) : undefined;   // read now, not when the dot was made: the data can arrive after it
  if ((p.converted || vd?.goalReached) && p.r * batchScale >= 2.5) {
    const simg = iconReady(goalStarBadgeUrl);
    if (simg) {
      const withIcon = !!p.iconUrl, ss = p.r * (withIcon ? 1.05 : 1.3), off = withIcon ? p.r * 0.6 : 0;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.drawImage(simg, p.x + off - ss / 2, p.y + off - ss / 2, ss, ss);
      ctx.restore();
    }
  }

  // the "?" badge of an unidentified visitor, bottom-left of the dot as in the grid (it vanished in the zoomed view)
  if ((p.unidentified || vd?.unidentified) && p.iconUrl !== unidentifiedBadgeUrl && p.r * batchScale >= 2.5) {
    const bimg = iconReady(unidentifiedBadgeUrl);
    if (bimg) {
      const bs = Math.max(p.r * 0.9, 9 / batchScale), off = p.r * 0.78;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.drawImage(bimg, p.x - off - bs / 2, p.y + off - bs / 2, bs, bs);
      ctx.restore();
    }
  }
}

/**
 * A returning visitor's arrows. Its own function, drawn BEFORE the state glyphs (Chris, 2026-10-12: "make all glyphs sit ABOVE the return bubble and circle
 * arrows"), whereas the star and "?" badges above are drawn after them.
 */
function drawReturnMark(ctx: CanvasRenderingContext2D, p: Particle, a: number, dim: DimensionId | null) {
  if (p.traveler) return;
  const vd = p.visitorId ? visitorData.get(p.visitorId) : undefined;
  // Drawn the way the grid draws them (clusterService's appendReturnMark): orbiting a plain dot, a corner badge on an icon.
  // This engine had no return mark at all, so a returning visitor's dot was bare in the zoomed view (seen on production, 2026-10-12: "Where's the circle
  // glyph?"). Built from the dot's WORLD radius, so the drawing is the same at every zoom and the canvas transform scales it. Skipped where the grid skips
  // it: an icon that is already the return arrow (the New vs Returning grouping in icon mode).
  if (vd?.visitor_type === "return" && p.r * batchScale >= 2.5) {
    const asIcon = !!p.iconUrl && p.r * batchScale >= ICON_MIN_SCREEN_R;
    if (!(asIcon && /return/i.test(String(p.iconUrl)))) {
      // the same rule as the grid (returnMarkFor): the arrows take the DOT'S colour; an icon's corner badge stays black
      // sized for at most RETURN_MARK_MAX_SCREEN_R on screen: past that the dot keeps growing but the arrows do not, or at 25x they overflow the card
      const mark = returnMarkFor(asIcon, p.r, asIcon ? null : particleColor(p, dim), RETURN_MARK_MAX_SCREEN_R / batchScale);
      const rimg = iconReady(mark.uri);
      if (rimg) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, a + 0.45);   // an away dot is dimmed; its arrows must stay readable
        ctx.drawImage(rimg, p.x + mark.offsetX - mark.size / 2, p.y + mark.offsetY - mark.size / 2, mark.size, mark.size);
        ctx.restore();
      }
    }
  }
}

function drawParticle(
  ctx: CanvasRenderingContext2D, p: Particle,
  dim: DimensionId | null, now: number, alpha: number,
  slotGsz?: number
) {
  // slot clouds never recolour the dot (mockup); state is the glyph only
  const col = particleColor(p, dim);
  const a = alpha * (p.traveler ? p.travelerAlpha : 1);
  if (a <= 0) return;

  // trail
  if (p.trail.length >= 2) {
    ctx.save();
    ctx.globalAlpha = a * 0.25;
    ctx.strokeStyle = col;
    ctx.lineWidth = p.r * 0.8;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(p.trail[0].x, p.trail[0].y);
    for (let i = 1; i < p.trail.length; i++) {
      ctx.lineTo(p.trail[i].x, p.trail[i].y);
    }
    ctx.stroke();
    ctx.restore();
  }

  // core: its icon in icon mode (a dot in flight or on its way out is not a plain disc), else a circle
  let drewIcon = false;
  if (p.iconUrl && p.r * batchScale >= ICON_MIN_SCREEN_R) {
    const img = iconReady(p.iconUrl);
    if (img) {
      const w = 2 * p.r, h = w * (p.iconAspect || 1);
      const spr = iconSprite(p.iconUrl, img, w * batchScale * canvasDpr(), p.iconAspect || 1);
      ctx.save();
      ctx.globalAlpha = a;
      ctx.drawImage(spr, p.x - w / 2, p.y - h / 2, w, h);
      ctx.restore();
      drewIcon = true;
    }
  }
  if (!drewIcon) {
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  drawReturnMark(ctx, p, a, dim);   // under the glyph below, as in the crowd
  drawBadges(ctx, p, a, dim);

  // interaction glyph (white shape drawn on top)
  if (!p.traveler && batchActivity) {
    if (!drewIcon) drawFlash(ctx, p, now);
    drawGlyph(ctx, p, a, now, slotGsz);
  }
}

// A burst of activity throws a brief spark of light off the dot, like lightning
// inside the cloud; the glyph below it says what happened.
const FLASH_MS = 450;
function drawFlash(ctx: CanvasRenderingContext2D, p: Particle, now: number) {
  const st = p.interactionState;
  if ((st !== "clicking" && st !== "typing" && st !== "scrolling") || p.stateT0 === undefined) return;
  const t = (now - p.stateT0) / FLASH_MS;
  if (t < 0 || t >= 1) return;
  const R = Math.min(p.r * 3.4, 40 / batchScale);   // never more than ~40px on screen, however far in
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R);
  const a = (1 - t) * (1 - t);
  g.addColorStop(0, `rgba(255,255,255,${(0.9 * a).toFixed(3)})`);
  g.addColorStop(0.35, `rgba(255,244,214,${(0.5 * a).toFixed(3)})`);
  g.addColorStop(1, "rgba(255,244,214,0)");
  ctx.save();
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(p.x, p.y, R, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawGlyph(
  ctx: CanvasRenderingContext2D, p: Particle, alpha: number, now: number, slotGsz?: number
) {
  const st = p.interactionState;
  // Slot clouds flash an action glyph for a moment (mockup: life 550ms);
  // resting states (idle, away, reading, watching) stay while they are true.
  if (
    slotGsz !== undefined &&
    (st === "clicking" || st === "typing" || st === "scrolling") &&
    (p.stateT0 === undefined || now - p.stateT0 > SLOT_ACTION_LIFE_MS)
  ) {
    return;
  }
  const gsz = slotGsz !== undefined ? slotGsz : Math.max(p.r * 1.9, 2.6);
  ctx.save();
  ctx.globalAlpha = Math.min(1, alpha + 0.2);
  ctx.translate(p.x, p.y);
  // The app's own badges sit a white mark on a dark halo (.pulse-*-badge-under)
  // so they read on any fill; a bare white glyph vanishes on a pale page.
  const halo = "rgba(17,24,39,0.4)";
  const mark = "rgba(255,255,255,0.96)";
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  const haloW = Math.max(0.8, gsz * 0.2);
  const paint = () => {
    ctx.lineWidth = haloW; ctx.strokeStyle = halo; ctx.stroke();
    ctx.fillStyle = mark; ctx.fill();
  };
  ctx.beginPath();
  switch (st) {
    case "clicking": {
      ctx.moveTo(-gsz * 0.41, -gsz * 0.65);
      ctx.lineTo(-gsz * 0.41, gsz * 0.44);
      ctx.lineTo(-gsz * 0.14, gsz * 0.17);
      ctx.lineTo(gsz * 0.07, gsz * 0.65);
      ctx.lineTo(gsz * 0.27, gsz * 0.58);
      ctx.lineTo(gsz * 0.07, gsz * 0.1);
      ctx.lineTo(gsz * 0.41, gsz * 0.1);
      ctx.closePath();
      paint();
      break;
    }
    case "typing": {
      ctx.rect(-gsz * 0.14, -gsz * 0.65, gsz * 0.28, gsz * 1.3);
      paint();
      break;
    }
    case "scrolling": {
      ctx.rect(-gsz * 0.65, -gsz * 0.14, gsz * 1.3, gsz * 0.28);
      paint();
      break;
    }
    case "idle": {
      ctx.font = `600 ${gsz * 1.5}px ${UI_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = haloW; ctx.strokeStyle = halo;
      ctx.strokeText("z", 0, 0.5);
      ctx.fillStyle = mark;
      ctx.fillText("z", 0, 0.5);
      break;
    }
    case "away": {
      awayGlyphPath(ctx, 0, 0, gsz);
      paint();
      break;
    }
    case "watching": {
      ctx.moveTo(-gsz * 0.4, -gsz * 0.55);
      ctx.lineTo(gsz * 0.6, 0);
      ctx.lineTo(-gsz * 0.4, gsz * 0.55);
      ctx.closePath();
      paint();
      break;
    }
    case "reading": {
      ctx.rect(-gsz * 0.6, -gsz * 0.38, gsz * 1.2, gsz * 0.22);
      ctx.rect(-gsz * 0.6, gsz * 0.16, gsz * 0.7, gsz * 0.22);
      paint();
      break;
    }
    default:
      break;
  }
  ctx.restore();
}

// ── batched drawing ───────────────────────────────────────────────────────────
// Thousands of dots a frame: dots of one colour are ONE path and ONE fill, and the
// state glyphs are one path per state, instead of a save/restore/arc/fill per dot.
// A glyph is only drawn where it can be read: when a dot is too small on screen to
// hold one, its state is still shown by dimming (away) and by the grouped view.

const TAU = Math.PI * 2;
// The most a returning visitor's arrows are sized for, as the dot's radius ON SCREEN (px). A dot at the default zoom (about 5x, radius ~20px) is under it, so it
// looks exactly like the grid's; zoomed far in, the dot grows but its arrows stay this size and sit just outside it, instead of overflowing the card.
const RETURN_MARK_MAX_SCREEN_R = 24;
const GLYPH_MIN_SCREEN_R = 1.3;    // dot radius on screen (px) below which a glyph is illegible
const SQUARE_DOT_MAX_R = 1.1;      // below this a dot is a 1px square: identical to a circle at that size
const FLASH_MAX_PER_FRAME = 400;
const GLYPH_STATES = ["clicking", "typing", "scrolling", "idle", "away", "watching", "reading"];

interface Bucket { col: string; a: number; ps: Particle[] }
const batchBuckets = new Map<string, Bucket>();
const glyphBuckets: Record<string, Particle[]> = {};
for (const st of GLYPH_STATES) glyphBuckets[st] = [];
const flashList: Particle[] = [];
const slowList: Particle[] = [];

function canvasDpr(): number {
  return Math.min(2, window.devicePixelRatio || 1);
}

function glyphSubpath(ctx: CanvasRenderingContext2D, st: string, x: number, y: number, g: number) {
  switch (st) {
    case "clicking":
      // the mouse cursor: an arrow head with a tail, tip at the top left (a click)
      ctx.moveTo(x - g * 0.41, y - g * 0.65);
      ctx.lineTo(x - g * 0.41, y + g * 0.44);
      ctx.lineTo(x - g * 0.14, y + g * 0.17);
      ctx.lineTo(x + g * 0.07, y + g * 0.65);
      ctx.lineTo(x + g * 0.27, y + g * 0.58);
      ctx.lineTo(x + g * 0.07, y + g * 0.1);
      ctx.lineTo(x + g * 0.41, y + g * 0.1);
      ctx.closePath();
      break;
    case "typing":
      ctx.rect(x - g * 0.14, y - g * 0.65, g * 0.28, g * 1.3);
      break;
    case "scrolling":
      ctx.rect(x - g * 0.65, y - g * 0.14, g * 1.3, g * 0.28);
      break;
    case "watching":
      ctx.moveTo(x - g * 0.4, y - g * 0.55);
      ctx.lineTo(x + g * 0.6, y);
      ctx.lineTo(x - g * 0.4, y + g * 0.55);
      ctx.closePath();
      break;
    case "reading":
      ctx.rect(x - g * 0.6, y - g * 0.38, g * 1.2, g * 0.22);
      ctx.rect(x - g * 0.6, y + g * 0.16, g * 0.7, g * 0.22);
      break;
    case "away":
      awayGlyphPath(ctx, x, y, g);
      break;
    case "idle":
      // a "z", as a stroked line
      ctx.moveTo(x - g * 0.45, y - g * 0.5);
      ctx.lineTo(x + g * 0.45, y - g * 0.5);
      ctx.lineTo(x - g * 0.45, y + g * 0.5);
      ctx.lineTo(x + g * 0.45, y + g * 0.5);
      break;
    default:
      break;
  }
}

// The size of a state glyph (the unit its shapes are drawn from; a glyph is about 1.3 units
// wide) as a function of the dot's radius ON SCREEN. It used to be a fixed 1.7x the radius, so
// on a big dot (an icon, or a dot zoomed in on) it came out wider than the dot itself. Then
// 0.29 per px of radius, which still left a glyph ~80% of an icon's width.
//   - tiny dots (under 3.8px): exactly the proportions they always had, never under 2.6;
//   - 3.8 to 10px: 6.5 units at 3.8 growing only 0.1 per px, so a glyph is a clear mark ON the
//     dot (about half an icon wide) yet never under ~8px wide, which is still readable;
//   - past 10px (an icon on a big stage, or zoomed in): it keeps pace with the icon, 0.6 units per
//     px of radius, so a glyph stays about 40% of an icon's width instead of shrinking to a speck
//     on it (it grew 0.25 per px before: a 28px icon wore a 10px mark, "almost imperceptible"),
//     and stops at 28 units.
function glyphSizeFor(rScreen: number): number {
  const AT_3_8 = 1.7 * 3.8;                      // where the tiny-dot branch ends (6.46)
  const AT_10 = AT_3_8 + 0.1 * (10 - 3.8);       // 7.08
  let g: number;
  if (rScreen < 3.8) g = Math.max(2.6, 1.7 * rScreen);
  else if (rScreen < 10) g = AT_3_8 + 0.1 * (rScreen - 3.8);
  else g = AT_10 + 0.6 * (rScreen - 10);
  return Math.min(g, 28);
}
const ICON_MIN_SCREEN_R = 2;     // a dot at least this big (px) is drawn as its icon in icon mode (a 4px logo is still a logo)
const iconList: Particle[] = [];
const iconImgs: HTMLImageElement[] = [];
const iconAlphas: number[] = [];

// (an icon gets no spark: the white glow washed the icon itself out for a moment, which read as the
// icon flashing; its glyph already says what happened)
function pushGlyph(p: Particle, now: number, slotGsz: number | undefined, asIcon = false) {
  if (!batchActivity) return;
  const st = p.interactionState;
  if (st === "focused" || p.r * batchScale < GLYPH_MIN_SCREEN_R) return;
  const action = st === "clicking" || st === "typing" || st === "scrolling";
  if (action) {
    const recent = p.stateT0 !== undefined && now - p.stateT0 < FLASH_MS;
    if (recent && !asIcon && flashList.length < FLASH_MAX_PER_FRAME) flashList.push(p);
    // slot clouds flash an action glyph for a moment (mockup: life 550ms)
    if (slotGsz !== undefined && (p.stateT0 === undefined || now - p.stateT0 > SLOT_ACTION_LIFE_MS)) return;
  }
  const gl = glyphBuckets[st];
  if (gl) gl.push(p);
}
let batchScale = 1;

/**
 * Draw a whole crowd. `alphaOf` is each dot's opacity, `scale` the camera zoom (so a
 * glyph is only drawn where it can be read), `slotGsz` the glyph size for slot
 * clouds (undefined = size from the dot, as the zoomed view does), `cull` the world
 * rectangle that is actually on screen (dots outside it cost nothing).
 */
const badgeList: Particle[] = [];
const badgeAlphas: number[] = [];
function drawParticlesBatched(
  ctx: CanvasRenderingContext2D, list: Particle[], now: number, dim: DimensionId | null,
  alphaOf: (p: Particle) => number, scale: number, slotGsz: number | undefined,
  cull?: { x0: number; y0: number; x1: number; y1: number }
) {
  batchBuckets.forEach((b) => { b.ps.length = 0; });
  if (batchBuckets.size > 64) batchBuckets.clear();
  for (const st of GLYPH_STATES) glyphBuckets[st].length = 0;
  flashList.length = 0;
  slowList.length = 0;
  badgeList.length = 0; badgeAlphas.length = 0;
  iconList.length = 0; iconImgs.length = 0; iconAlphas.length = 0;
  batchScale = scale;
  batchActivity = activityProvider ? !!activityProvider() : true;

  for (let i = 0; i < list.length; i++) {
    const p = list[i];
    if (cull && (p.x < cull.x0 || p.x > cull.x1 || p.y < cull.y0 || p.y > cull.y1)) continue;
    if (isGhost(p)) { ghostHidden++; continue; }
    if (hiddenInZoom(p)) continue;
    if (p.traveler || p.exitTarget || p.trail.length >= 2) { slowList.push(p); continue; }
    const a = alphaOf(p);
    if (a <= 0) continue;
    badgeList.push(p); badgeAlphas.push(a);
    // icon mode: a dot big enough to read is drawn as its category icon, not a dot
    if (p.iconUrl && p.r * scale >= ICON_MIN_SCREEN_R) {
      const img = iconReady(p.iconUrl);
      if (img) { iconList.push(p); iconImgs.push(img); iconAlphas.push(a); pushGlyph(p, now, slotGsz, true); continue; }
    }
    const col = particleColor(p, dim);
    const key = col + "|" + a.toFixed(2);
    let b = batchBuckets.get(key);
    if (!b) { b = { col, a, ps: [] }; batchBuckets.set(key, b); }
    b.ps.push(p);
    pushGlyph(p, now, slotGsz);
  }

  batchBuckets.forEach((b) => {
    if (!b.ps.length) return;
    ctx.globalAlpha = b.a;
    ctx.fillStyle = b.col;
    ctx.beginPath();
    for (let i = 0; i < b.ps.length; i++) {
      const p = b.ps[i], r = p.r;
      if (r * scale < SQUARE_DOT_MAX_R) ctx.rect(p.x - r, p.y - r, 2 * r, 2 * r);
      else { ctx.moveTo(p.x + r, p.y); ctx.arc(p.x, p.y, r, 0, TAU); }
    }
    ctx.fill();
  });
  ctx.globalAlpha = 1;

  // icons (icon mode): the same size and aspect as the real icon bubble they replace.
  // Each is blitted from a small pre-rendered copy, never re-rasterised from the SVG
  // per draw, so a crowd of thousands of icons stays cheap.
  const dprNow = canvasDpr();
  for (let i = 0; i < iconList.length; i++) {
    const p = iconList[i];
    const w = 2 * p.r, h = w * (p.iconAspect || 1);
    const spr = iconSprite(p.iconUrl as string, iconImgs[i], w * scale * dprNow, p.iconAspect || 1);
    ctx.globalAlpha = iconAlphas[i];
    ctx.drawImage(spr, p.x - w / 2, p.y - h / 2, w, h);
  }
  ctx.globalAlpha = 1;

  // the spark an action throws off the dot
  for (const p of flashList) drawFlash(ctx, p, now);

  // a returning visitor's arrows go UNDER the state glyphs (a crowd of hundreds would make this a per-frame cost; the marks are for readable crowds)
  if (badgeList.length <= 400) for (let i = 0; i < badgeList.length; i++) drawReturnMark(ctx, badgeList[i], badgeAlphas[i], dim);

  // state glyphs: a white mark on a dark halo, one path per state
  const halo = "rgba(17,24,39,0.4)";
  const mark = "rgba(255,255,255,0.96)";
  // glyph size follows the dot's size on screen by glyphSizeFor's schema
  const gszOf = (p: Particle) => (slotGsz !== undefined ? slotGsz : glyphSizeFor(p.r * scale) / scale);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (const st of GLYPH_STATES) {
    const ps = glyphBuckets[st];
    if (!ps.length) continue;
    ctx.globalAlpha = Math.min(1, alphaOf(ps[0]) + 0.2);
    ctx.beginPath();
    for (const p of ps) glyphSubpath(ctx, st, p.x, p.y, gszOf(p));
    const g0 = gszOf(ps[0]);
    const haloW = Math.max(0.7, g0 * 0.15);
    if (st === "idle") {
      const zw = Math.max(0.7, g0 * 0.26);
      ctx.lineWidth = zw + haloW; ctx.strokeStyle = halo; ctx.stroke();
      ctx.lineWidth = zw; ctx.strokeStyle = mark; ctx.stroke();
    } else {
      ctx.lineWidth = haloW; ctx.strokeStyle = halo; ctx.stroke();
      ctx.fillStyle = mark; ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  // (a crowd of hundreds would make this a per-frame cost; the badges are for readable crowds)
  if (badgeList.length <= 400) for (let i = 0; i < badgeList.length; i++) drawBadges(ctx, badgeList[i], badgeAlphas[i], dim);

  // dots in flight keep the full per-dot treatment
  for (const p of slowList) drawParticle(ctx, p, dim, now, alphaOf(p), slotGsz);
}

// ── physics tick for an array of particles ────────────────────────────────────

function tickParticles(
  particles: Particle[], dt: number, now: number,
  cx: number, cy: number,
  dim: DimensionId | null, regions: Region[]
) {
  for (const p of particles) {
    const tgt = computeTarget(p, cx, cy, dim, regions);
    const tt = Math.min(1, (now - p.tweenT0) / p.tweenDur);
    const e = dim ? easeOutCubic(tt) : easeInOutCubic(tt);   // grouped view: start at speed, settle gently
    let baseX = p.tweenStartX + (tgt.x - p.tweenStartX) * e;
    let baseY = p.tweenStartY + (tgt.y - p.tweenStartY) * e;
    if (tt >= 1) {
      if (p.sx === undefined || p.sy === undefined) { p.sx = baseX; p.sy = baseY; }
      p.sx += (tgt.x - p.sx) * 0.08;
      p.sy += (tgt.y - p.sy) * 0.08;
      baseX = p.sx; baseY = p.sy;
    } else {
      p.sx = baseX; p.sy = baseY;
    }
    const wobble = tt >= 1 ? 1 : e;
    p.x = baseX + Math.cos(now * 0.0009 * p.speedMul + p.phase) * WOBBLE_AMP * wobble;
    p.y = baseY + Math.sin(now * 0.0011 * p.speedMul + p.phase * 1.7) * WOBBLE_AMP * wobble;

    if (p.trail.length) {
      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > TRAIL_MAX) p.trail.shift();
      if (tt >= 1 && p.trail.length > 6) p.trail = [];
    }

    if (p.traveler && p.travelerDir === 1 && tt >= 1) {
      p.traveler = false;
    }
  }
}

// ── per-slot cloud overlay ────────────────────────────────────────────────────

function getOrCreateSlotCloud(
  slotIndex: number, pageName: string,
  cx: number, cy: number,
  w: number, h: number
): SlotCloud {
  let cloud = slotClouds.get(slotIndex);
  if (!cloud) {
    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.pointerEvents = "none";
    canvas.style.cursor = "pointer";
    canvas.setAttribute("data-density-cloud", String(slotIndex));
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d")!;
    cloud = {
      arrived: new Map(),
      pending: new Map(),
      pendingTarget: new Map(),
      pendingRest: new Map(),
      pack: newPack(),
      slotIndex, pageName, canvas, ctx,
      particles: [], travelerTimer: 0,
      cx, cy, w, h,
      byId: new Map(), mv: 0, mvSeen: -1, lastReconcile: 0, pvSeen: -1, lastScan: 0, frame: 0,
    };
    slotClouds.set(slotIndex, cloud);
    positionSlotCanvas(cloud);
      }
  cloud.pageName = pageName;
  cloud.cx = cx; cloud.cy = cy;
  cloud.w = w; cloud.h = h;
  positionSlotCanvas(cloud);
  return cloud;
}

function positionSlotCanvas(cloud: SlotCloud) {
  if (!svgRect) return;
  const { canvas, cx, cy, w, h } = cloud;
  const dpr = canvasDpr();
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.left = `${svgRect.left + cx - w / 2}px`;
  canvas.style.top  = `${svgRect.top  + cy - h / 2}px`;
  canvas.style.width  = `${w}px`;
  canvas.style.height = `${h}px`;
  canvas.style.zIndex = "50";
}

function destroySlotCloud(slotIndex: number, release = true) {
  const cloud = slotClouds.get(slotIndex);
  if (!cloud) return;
  slotClouds.delete(slotIndex);
  // each real bubble reappears exactly where its dot was, so the cloud turns
  // back into the d3 cluster in place
  if (release) {
    for (const p of cloud.particles) releaseDot(p.visitorId, cloud.cx + p.x, cloud.cy + p.y, "dissolved", cloud.slotIndex);
    if (cloud.pending.size) pendingTargetsHandler?.();
  }
  cloud.canvas.remove();
}

function slotR0(cloud: SlotCloud): number {
  // grid-slot mockup: r = min(W, H) / 2 * 0.86
  return (Math.min(cloud.w, cloud.h) / 2) * 0.86;
}

// A cloud is kept in step with the world by EVENTS, not by re-reading everything
// every frame: a page change bumps `pageVersion`, a membership change bumps the
// cloud's own `mv`. The periodic pass below is only a safety net, and it SAYS SO
// (cloudStats.repaired + a warning) if it ever finds something an event missed.
const SAFETY_PASS_MS = 2000;
const HIDE_PASS_MS = 250;
let periodicHook: (() => void) | null = null;
export function setPeriodicHook(fn: (() => void) | null) {
  periodicHook = fn;
}
let lastHidePass = 0;
// A real bubble flying to its spot normally arrives in well under a second (measured
// with the app's own d3 forces: 0.3-0.7s from 40-400px). One that has not arrived
// after this long inside its box is swapped for its dot where it is, and the dot
// eases the rest of the way, so a bubble never sits in a cloud for long.
const PENDING_FALLBACK_MS = 1500;
// Settings > Bubble Speed makes every real bubble GLIDE (a CSS transition) to where the simulation
// has put it. The simulation rewrites every position on every tick, and each rewrite restarts the
// transition, so the bubble on screen does not trail the simulation by "the glide time": it closes
// on its target exponentially, with a time constant of several glide times (Slow: it had covered
// about a third of the way when the simulation had long since parked it). Swapping it for its dot
// when the SIMULATION says it has arrived made it vanish mid-flight and reappear in its spot. So
// when the app glides (the host returns its glide time, 0 when it does not) a bubble lands when its
// ON-SCREEN position reaches its spot, wherever the simulation says it is.
let landingLagProvider: (() => number) | null = null;
// Settings > Bubble Speed is how long one of the app's bubble moves takes (Fast 100ms, Medium 1s,
// Slow 5s). The zoomed view moves things on its own clock, so on Slow it ran at Fast's pace. A move
// there now takes at least the chosen time (its own natural time when that is longer).
function moveMs(natural: number): number {
  return Math.max(natural, landingLagProvider?.() || 0);
}
export function setLandingLagProvider(fn: typeof landingLagProvider) {
  landingLagProvider = fn;
}
// A bubble counts as ARRIVED when it is within this of its spot (at least this many px, or
// half the spot spacing when that is bigger), or when it has come to rest nearby. MEASURED
// on the real app: with a strong "Bubble Cluster Spacing" the other bubbles on the board
// leave a flying bubble parked 4-5px off its spot for good, so a 4px rule never fired and
// every arrival waited for the timeout.
const LAND_TOL_MIN = 7;
const LAND_SETTLED_MAX = 24;      // ...resting no further than this (or 2.5 spacings) from its spot
const LAND_REST_FRAMES = 4;       // ...for this many frames in a row, moving under 0.2px

function addLanded(cloud: SlotCloud, p: Particle) {
  p.interactionState = visitorInteraction.get(p.visitorId) || "focused";
  p.stateT0 = visitorStateT0.get(p.visitorId);
  cloud.particles.push(p);
  cloud.byId.set(p.visitorId, p);
}

function placeWithoutBubble(cloud: SlotCloud, vid: string, now: number, R0: number) {
  // no real bubble to wait for: place the dot straight into its spot
  const ang = Math.random() * Math.PI * 2;
  const rr = Math.sqrt(Math.random()) * R0 * 0.5;
  const p0 = makeParticle(Math.cos(ang) * rr, Math.sin(ang) * rr, now, vid);
  p0.vx = 0; p0.vy = 0;
  addLanded(cloud, p0);
}

function syncSlotParticles(cloud: SlotCloud, now: number) {
  const R0 = slotR0(cloud);
  cloud.seeded = true;

  // A visitor becomes a dot only once its REAL bubble has arrived at the exact
  // honeycomb spot reserved for it. Until then the real d3 bubble flies there
  // under the app's own physics (the host aims it at getPendingTarget), so
  // there is never a second bubble created to glide into place.
  const halfW = cloud.w / 2 - 4;
  const halfH = cloud.h / 2 - 4;
  // In the first moments after the page loads every visitor is already here (the
  // board is rebuilt silently), so there is no flight to wait for: a dot appears
  // immediately where its bubble is. After that, a growing crowd uses the real flight.
  // A cloud formed by a click on a quiet slot is the same: everyone is already here.
  const justLoaded = performance.now() < LOAD_WINDOW_MS || now < (cloud.instantUntil ?? 0);
  let instantLanded = false;

  // 1. Who is on this page now? Only when a page changed (or as the safety net).
  const safety = now - cloud.lastScan > SAFETY_PASS_MS;
  if (cloud.pvSeen !== pageVersion || safety) {
    const eventDriven = cloud.pvSeen !== pageVersion;
    cloud.pvSeen = pageVersion;
    cloud.lastScan = now;
    let repaired = 0;

    for (const [vid, pg] of visitorPage) {
      if (cloud.byId.has(vid) || cloud.arrived.has(vid) || cloud.pending.has(vid)) continue;
      if (slotForPage(pg, cloud.slotIndex, cloud.pageName) !== cloud.slotIndex) continue;
      // A cloud formed by clicking a quiet slot is built from what is REALLY on the board. A visitor the page map still lists but whose
      // bubble does not exist (stale: it left, or the board was rebuilt) is not there to show; making it a dot gave the stray teal dots.
      if (cloud.forced && !bubblePosProvider?.(vid)) continue;
      cloud.mv++;
      repaired++;
      if (!bubblePosProvider?.(vid)) { placeWithoutBubble(cloud, vid, now, R0); continue; }
      cloud.pending.set(vid, now);
    }

    // A visitor who left this page: put the real bubble back where the dot was
    // and let the app's own movement take it from there.
    let leaving: Particle[] | null = null;
    for (const p of cloud.particles) {
      if (slotForPage(visitorPage.get(p.visitorId), cloud.slotIndex, cloud.pageName) !== cloud.slotIndex) {
        (leaving ||= []).push(p);
      }
    }
    if (leaving) {
      const gone = new Set(leaving.map((p) => p.visitorId));
      cloud.particles = cloud.particles.filter((p) => !gone.has(p.visitorId));
      for (const p of leaving) {
        cloud.byId.delete(p.visitorId);
        releaseDot(p.visitorId, cloud.cx + p.x, cloud.cy + p.y, "moved", cloud.slotIndex);
      }
      cloud.mv++;
      repaired += leaving.length;
    }
    let dropped = false;
    for (const vid of Array.from(cloud.pending.keys())) {
      if (slotForPage(visitorPage.get(vid), cloud.slotIndex, cloud.pageName) !== cloud.slotIndex) {
        cloud.pending.delete(vid);
        cloud.pendingTarget.delete(vid);
        cloud.pendingRest.delete(vid);
        cloud.mv++;
        dropped = true;
        repaired++;
      }
    }
    if (dropped) pendingTargetsHandler?.();
    if (!eventDriven && repaired) {
      cloudStats.repaired += repaired;
      console.warn(`[density-cloud] safety pass repaired ${repaired} membership change(s) in slot ${cloud.slotIndex} that no event reported`);
    }
  }

  // 2. Visitors whose real bubble is still flying to its spot.
  for (const [vid, since] of cloud.pending) {
    const bp = bubblePosProvider?.(vid);
    if (!bp) {
      placeWithoutBubble(cloud, vid, now, R0);
      cloud.pending.delete(vid);
      cloud.pendingTarget.delete(vid);
      cloud.pendingRest.delete(vid);
      continue;
    }
    const tgt = cloud.pendingTarget.get(vid);
    const lx = bp.x - cloud.cx, ly = bp.y - cloud.cy;
    let landX: number | null = null, landY: number | null = null;
    // near its spot, or at rest near it
    const spacing = cloud.pack.s || 10;
    const dTarget = tgt ? Math.hypot(bp.x - tgt.x, bp.y - tgt.y) : Infinity;
    let rest = cloud.pendingRest.get(vid);
    if (!rest) { rest = { x: bp.x, y: bp.y, n: 0 }; cloud.pendingRest.set(vid, rest); }
    else {
      rest.n = Math.hypot(bp.x - rest.x, bp.y - rest.y) < 0.2 ? rest.n + 1 : 0;
      rest.x = bp.x; rest.y = bp.y;
    }
    const nearTarget = dTarget <= Math.max(LAND_TOL_MIN, 0.5 * spacing);
    const restingNear = rest.n >= LAND_REST_FRAMES && dTarget <= Math.max(LAND_SETTLED_MAX, 2.5 * spacing);
    const lag = landingLagProvider?.() || 0;
    const near = !!tgt && (nearTarget || restingNear);
    // where the bubble really is on screen (only asked for when the app glides)
    const vis = lag > 0 ? elementPosProvider?.(vid) ?? null : null;
    const dVis = vis && tgt ? Math.hypot(vis.x - tgt.x, vis.y - tgt.y) : Infinity;
    if (justLoaded && Math.abs(lx) <= halfW && Math.abs(ly) <= halfH) {
      landX = lx; landY = ly;                                       // page just loaded: swap in at once
      instantLanded = true;
    } else if (vis) {
      // gliding: it has landed when what is on screen is at its spot (the dot starts exactly there)
      if (dVis <= Math.max(LAND_SETTLED_MAX, 2.5 * spacing)) { landX = vis.x - cloud.cx; landY = vis.y - cloud.cy; }
      else if (now - since > PENDING_FALLBACK_MS + 4 * lag && Math.abs(lx) <= halfW && Math.abs(ly) <= halfH) { landX = vis.x - cloud.cx; landY = vis.y - cloud.cy; }
    } else if (near) {
      landX = lx; landY = ly;                                       // arrived: swap exactly where the bubble IS (no snap); the dot eases the last pixels
    } else if (now - since > PENDING_FALLBACK_MS && Math.abs(lx) <= halfW && Math.abs(ly) <= halfH) {
      landX = lx; landY = ly;                                       // never quite got there: swap where it is
    }
    if (landX === null || landY === null) continue;
    // the real bubble has arrived: hide it right now, and create the dot only
    // after it is gone (below), so the two never exist at the same time
    cloud.arrived.set(vid, { x: landX, y: landY, t: now });
    cloud.pending.delete(vid);
    cloud.pendingTarget.delete(vid);
    cloud.pendingRest.delete(vid);
    if (!instantLanded) bubbleHideHandler?.();
  }
  if (instantLanded) bubbleHideHandler?.();

  // 3. Bubbles that have been hidden: the dot follows a moment later.
  for (const [vid, a] of cloud.arrived) {
    if (slotForPage(visitorPage.get(vid), cloud.slotIndex, cloud.pageName) !== cloud.slotIndex) {
      cloud.arrived.delete(vid);
      cloud.mv++;
      releaseDot(vid, cloud.cx + a.x, cloud.cy + a.y, "moved", cloud.slotIndex);
      continue;
    }
    if (now - a.t < 40) continue; // at least two frames after the bubble was hidden
    cloud.arrived.delete(vid);
    cloudStats.landed++;
    const p = makeParticle(a.x, a.y, now, vid);
    p.vx = 0; p.vy = 0;
    addLanded(cloud, p);
  }
}

function tickSlotCloud(cloud: SlotCloud, now: number, dt: number) {
  const pack = cloud.pack;
  const Rbox = slotR0(cloud);

  // keep the pack's members equal to the cloud's (only when membership changed)
  if (cloud.mv !== cloud.mvSeen || now - cloud.lastReconcile > SAFETY_PASS_MS) {
    const ids: string[] = [];
    for (const q of cloud.particles) if (!q.exitTarget) ids.push(q.visitorId);
    cloud.pending.forEach((_, v) => ids.push(v));
    cloud.arrived.forEach((_, v) => ids.push(v));
    const posOf = (v: string) => {
      const q = cloud.byId.get(v);
      if (q) return q;
      const bp = bubblePosProvider?.(v);
      return bp ? { x: bp.x - cloud.cx, y: bp.y - cloud.cy } : undefined;
    };
    const eventDriven = cloud.mv !== cloud.mvSeen;
    const changed = packReconcile(pack, ids, posOf);
    if (changed && !eventDriven) {
      cloudStats.repaired += changed;
      console.warn(`[density-cloud] safety pass repaired ${changed} pack spot(s) in slot ${cloud.slotIndex} that no event reported`);
    }
    cloud.mvSeen = cloud.mv;
    cloud.lastReconcile = now;
  }

  // close holes (a few chains calmly when small; a lot at once after a mass exit)
  const nh = pack.holes.size;
  if (nh > 0) {
    const big = nh > 24;
    if (big || now - pack.lastCompact >= 150) {
      pack.lastCompact = now;
      packCompact(pack, big ? 1500 : 60);
    }
  }
  packFit(pack, Rbox);

  const lat = ensureLattice(pack.frontier + 8);
  const s = pack.s, J = pack.J;
  // aim each flying-in bubble at the spot reserved for it; tell the host when any aim changes
  if (cloud.pending.size) {
    let aimChanged = false;
    for (const v of cloud.pending.keys()) {
      const i = pack.owner.get(v);
      if (i === undefined) continue;
      const nx = cloud.cx + (lat.x[i] + lat.jx[i] * J) * s, ny = cloud.cy + (lat.y[i] + lat.jy[i] * J) * s;
      const old = cloud.pendingTarget.get(v);
      if (!old || Math.abs(old.x - nx) > 0.5 || Math.abs(old.y - ny) > 0.5) {
        cloud.pendingTarget.set(v, { x: nx, y: ny });
        aimChanged = true;
      }
    }
    if (aimChanged) pendingTargetsHandler?.();
  }

  const dotR = pack.r;
  const k = dt / 16.667;
  const damp = Math.pow(0.88, k);
  const frame = ++cloud.frame;
  const ps = cloud.particles;
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i];
    p.r = dotR;
    // every dot re-reads its real bubble's colour about once a second, spread over the frames
    if ((i + frame) % 60 === 0) refreshColor(p, now);
    const idx = pack.owner.get(p.visitorId);
    if (p.exitTarget || p.traveler) {
      // arriving / departing: eased flight, then it sits in its spot
      const tgtX = p.exitTarget ? p.exitTarget.x : idx === undefined ? 0 : (lat.x[idx] + lat.jx[idx] * J) * s;
      const tgtY = p.exitTarget ? p.exitTarget.y : idx === undefined ? 0 : (lat.y[idx] + lat.jy[idx] * J) * s;
      const tt = Math.min(1, (now - p.tweenT0) / p.tweenDur);
      const e = easeInOutCubic(tt);
      p.x = p.tweenStartX + (tgtX - p.tweenStartX) * e;
      p.y = p.tweenStartY + (tgtY - p.tweenStartY) * e;
      if (p.trail.length) {
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > TRAIL_MAX) p.trail.shift();
      }
      p.travelerAlpha = Math.min(1, Math.max(0, p.travelerAlpha + p.travelerDir * (dt / 800)));
      if (!p.exitTarget && tt >= 1) {
        p.traveler = false;
        p.travelerAlpha = 1;
        p.vx = 0; p.vy = 0;
        p.trail = [];
      }
      continue;
    }
    if (idx === undefined) continue;
    // settled: soft pull to its own fixed spot
    p.vx += ((lat.x[idx] + lat.jx[idx] * J) * s - p.x) * 0.0022 * k;
    p.vy += ((lat.y[idx] + lat.jy[idx] * J) * s - p.y) * 0.0022 * k;
    p.vx *= damp; p.vy *= damp;
    p.x += p.vx * k;
    p.y += p.vy * k;
  }
}

const slotAlpha = (p: Particle) => p.opacity * (batchActivity && p.interactionState === "away" ? AWAY_ALPHA : 1);

function renderSlotCloud(cloud: SlotCloud, now: number, dt: number, draw = true) {
  const { ctx, w, h } = cloud;
  tickSlotCloud(cloud, now, dt);
  if (!draw) return;
  const dpr = canvasDpr();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.translate(w / 2, h / 2);
  // away dims the whole dot (mockup: opacity * 0.55)
  drawParticlesBatched(ctx, cloud.particles, now, null, slotAlpha, 1, glyphSizeFor(cloud.pack.r));
  drawRowMarks(ctx, now, 1, (id) => cloud.byId.get(id));
  ctx.restore();
}

// ── rivers view ───────────────────────────────────────────────────────────────

function buildRiversCanvas() {
  if (riversCanvas) return;
  // canvas text only uses a face that is already loaded; ask for the weights the labels use
  try {
    void document.fonts?.load("600 21px \"Fira Sans\"");
    void document.fonts?.load("600 12px \"Fira Sans\"");
  } catch { /* labels fall back to sans-serif until it is there */ }
  riversCanvas = document.createElement("canvas");
  riversCanvas.style.position = "absolute";
  riversCanvas.style.top = "0";
  riversCanvas.style.left = "0";
  riversCanvas.style.zIndex = "200";
  riversCanvas.style.pointerEvents = "auto";
  riversCanvas.style.cursor = "default";
  document.body.appendChild(riversCanvas);
  riversCtx = riversCanvas.getContext("2d")!;
  sizeRiversCanvas();
  camera.scale = 1;
  camera.cx = riversCssW() / 2;
  camera.cy = riversCssH() / 2;

  riversCanvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    const r = riversCanvas!.getBoundingClientRect();
    const wx = e.clientX - r.left - riversOrigin.x, wy = e.clientY - r.top - riversOrigin.y;
    if (wx < 0 || wy < 0 || wx > riversCssW() || wy > riversCssH()) return;   // only over the card itself
    if (riversGridExtra > 0 && !e.ctrlKey && !e.metaKey) {
      // a layout bigger than the view: the wheel scrolls it (hold Ctrl / Cmd to zoom, as a pinch does)
      riversFitTrack = false; riversScaleAnim = null; riversCamAnim = null;
      camera.cy += e.deltaY / camera.scale;
      camera.cx += e.deltaX / camera.scale;
      clampOverflowCamera();
      return;
    }
    zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12, wx, wy);
  }, { passive: false });
  riversCanvas.addEventListener("mousedown", (e) => {
    riversDragging = true; riversDragMoved = false;
    riversDragX = e.clientX; riversDragY = e.clientY;
  });
  riversCanvas.addEventListener("mousemove", (e) => {
    if (riversDragging) return;
    const r = riversCanvas!.getBoundingClientRect();
    const mx = e.clientX - r.left - riversOrigin.x, my = e.clientY - r.top - riversOrigin.y;
    const vid = mx < 0 || my < 0 || mx > riversCssW() || my > riversCssH() ? null : nearestBubble(mx, my);
    if (vid !== hoveredVisitorId) {
      hoveredVisitorId = vid;
      if (vid) bubbleHandlers?.onHover(vid, mx + riversOff.x, my + riversOff.y);
      else bubbleHandlers?.onLeave();
    }
  });
  riversCanvas.addEventListener("mouseleave", () => {
    if (hoveredVisitorId) { hoveredVisitorId = null; bubbleHandlers?.onLeave(); }
  });
  riversCanvas.addEventListener("click", () => {
    if (riversDragMoved) return;
    if (hoveredVisitorId) {
      bubbleHandlers?.onClick(hoveredVisitorId);
      return;
    }
    // nothing under the cursor: nothing happens (the zoom buttons zoom; dragging pans)
  });
  window.addEventListener("mousemove", onRiversMouseMove);
  window.addEventListener("mouseup", onRiversMouseUp);
}

function onRiversMouseMove(e: MouseEvent) {
  if (!riversDragging) return;
  const dx = e.clientX - riversDragX, dy = e.clientY - riversDragY;
  if (Math.abs(dx) > 2 || Math.abs(dy) > 2) riversDragMoved = true;
  camera.cx -= dx / camera.scale;
  camera.cy -= dy / camera.scale;
  clampOverflowCamera();
  riversDragX = e.clientX; riversDragY = e.clientY;
}

// A layout bigger than the view may be panned from its top to its bottom, and a little past either; nowhere further (the cloud's own view has no limit, as before).
function clampOverflowCamera() {
  if (riversGridExtra <= 0) return;
  const lo = riversHome.y - 60 / camera.scale;
  const hi = riversHome.y + riversGridExtra + 60 / camera.scale;
  camera.cy = Math.max(lo, Math.min(hi, camera.cy));
}

function onRiversMouseUp() { riversDragging = false; }

function screenToWorld(sx: number, sy: number) {
  const W = riversCssW(), H = riversCssH();
  return { x: (sx - W / 2) / camera.scale + camera.cx, y: (sy - H / 2) / camera.scale + camera.cy };
}

// mockup: scale clamped to 1..40, the point under the cursor stays put
function zoomBy(factor: number, atX: number, atY: number) {
  riversFitTrack = false; riversScaleAnim = null;   // the person is driving the camera now (Reset still goes back to the framed view)
  const before = screenToWorld(atX, atY);
  camera.scale = Math.max(riversGridExtra > 0 ? 0.3 : 1, Math.min(40, camera.scale * factor));   // a layout bigger than the view can be zoomed OUT to see more of it
  const after = screenToWorld(atX, atY);
  camera.cx += before.x - after.x;
  camera.cy += before.y - after.y;
}

// The scale that frames the crowd of the zoomed slot (see FIT_TARGET). The dots sit on a honeycomb:
// ring k holds 6k of them, k spacings out, so the crowd's radius follows from how many there are.
function crowdFitScale(): number {
  const n = riversOnPage;
  const cloud = slotClouds.get(riversSlotIndex);
  const spacing = cloud?.pack.s || 10;
  let rings = 0;
  while (1 + 3 * rings * (rings + 1) < n) rings++;
  const R = Math.max(FIT_MIN_R, rings * spacing + (cloud?.pack.r || 4));
  return Math.max(1, Math.min(FIT_MAX, (FIT_TARGET * Math.min(riversCssW(), riversCssH())) / R));
}

// What "Reset" returns to: the framed crowd when this zoom opened on a quiet slot, else 1.0x.
function homeScale(): number {
  return riversAutoFit && riversActiveDimension === null ? crowdFitScale() : 1;
}

function resetCamera() {
  camera.scale = homeScale();
  camera.cx = riversHome.x;
  camera.cy = riversHome.y;
  riversCamAnim = null;
  riversScaleAnim = null;
  riversFitTrack = riversAutoFit && riversActiveDimension === null;
}

// The svg is wider than the window, so the stage is only its VISIBLE part:
// the cloud sits at the centre of what you can see, and the edge pills sit at
// the real edges of the screen instead of off the right-hand side.
/**
 * THE ZOOMED VIEW IS A CARD. A page's zoom is the page's own box drawn large, in the middle of the grey stage, with slivers of the
 * neighbouring boxes and a pill for each of the eight directions around it (so the user keeps their bearings, and a visitor who
 * comes from a neighbour is seen to come from that side). Everything that used the stage as the view (the canvas, the camera, the
 * edges visitors leave by) now uses the card, because stageRect() returns it. The single view is the same card, with no neighbours
 * (so no slivers and no pills) and a slimmer margin, since there is nothing around it to point at.
 */
const cardMode = () => riversMode;
// The big title at the bottom left of the card (the page's name, or "All pages", and its count): one size for the page zoom (DOM) and the
// single view (SVG). It was 28/15 and read too large (Chris, 2026-10-10).
const CARD_TITLE_PX = 22;
const CARD_COUNT_PX = 13;
// Where it sits: the same breathing room from the card's left and bottom edges (it was 18 / 12 and sat too close to the bottom edge).
const CARD_TITLE_INSET_PX = 20;
function cardInsets(w: number, h: number, single = riversPersistent) {
  if (single) {
    return {
      ix: Math.max(28, Math.min(56, w * 0.03)),
      it: Math.max(40, Math.min(56, h * 0.075)),
      ib: Math.max(32, Math.min(48, h * 0.06)),
    };
  }
  return {
    ix: Math.max(56, Math.min(190, w * 0.105)),
    it: Math.max(64, Math.min(104, h * 0.165)),
    ib: Math.max(56, Math.min(100, h * 0.155)),
  };
}
function stageRect() {
  const r = panelRect();
  if (!cardMode()) return r;
  const { ix, it, ib } = cardInsets(r.width, r.height);
  return { left: r.left + ix, top: r.top + it, width: Math.max(1, r.width - 2 * ix), height: Math.max(1, r.height - it - ib), offX: r.offX + ix, offY: r.offY + it };
}
function panelRect() {
  const svgR = svgEl!.getBoundingClientRect();
  // the app's own grey real-time stage: not the whole svg, which also spans the
  // metrics above it and the historical dock below it
  const panel = svgEl!.querySelector("#maincluster") as SVGGraphicsElement | null;
  const r = panel ? panel.getBoundingClientRect() : svgR;
  const left = Math.max(r.left, 0), top = Math.max(r.top, 0);
  const right = Math.min(r.right, window.innerWidth), bottom = Math.min(r.bottom, window.innerHeight);
  return {
    left, top,
    width: Math.max(1, right - left), height: Math.max(1, bottom - top),
    offX: left - svgR.left, offY: top - svgR.top,
  };
}
let riversOff = { x: 0, y: 0 };

function sizeRiversCanvas() {
  if (!riversCanvas || !svgEl) return;
  const rect = stageRect();
  riversOff = { x: rect.offX, y: rect.offY };
  const dpr = canvasDpr();
  if (cardMode()) {
    const P = panelRect(), { ix, it, ib } = cardInsets(P.width, P.height);
    riversOrigin = { x: ix, y: it };
    riversStage = { w: rect.width, h: rect.height };
    riversCardGeo = { ix, it, ib, cardW: rect.width, cardH: rect.height, panelW: P.width, panelH: P.height };
    riversCanvas.width = Math.round(P.width * dpr);
    riversCanvas.height = Math.round(P.height * dpr);
    riversCanvas.style.left = `${P.left + window.scrollX}px`;
    riversCanvas.style.top  = `${P.top  + window.scrollY}px`;
    riversCanvas.style.width  = `${P.width}px`;
    riversCanvas.style.height = `${P.height}px`;
    return;
  }
  riversOrigin = { x: 0, y: 0 };
  riversStage = null;
  riversCanvas.width = Math.round(rect.width * dpr);
  riversCanvas.height = Math.round(rect.height * dpr);
  riversCanvas.style.left = `${rect.left + window.scrollX}px`;
  riversCanvas.style.top  = `${rect.top  + window.scrollY}px`;
  riversCanvas.style.width  = `${rect.width}px`;
  riversCanvas.style.height = `${rect.height}px`;
}

function destroyRiversCanvas() {
  destroyFlightCanvas();
  if (hoveredVisitorId) { hoveredVisitorId = null; bubbleHandlers?.onLeave(); }
  window.removeEventListener("mousemove", onRiversMouseMove);
  window.removeEventListener("mouseup", onRiversMouseUp);
  riversDragging = false;
  riversCanvas?.remove();
  riversCanvas = null;
  riversCtx = null;
  riversOrigin = { x: 0, y: 0 };
  riversStage = null;
}

function buildRiversOverlay(cx: number, cy: number) {
  if (riversOverlay) return;
  // The app's own view icons (gear, page view, single view) sit at the top centre of the stage and
  // show through this transparent layer, so everything here starts BELOW them. (A header at the
  // top centre drew its title right on top of them.) The persistent single view has no Back
  // button and no title (the total is already top left): just the controls.
  let band = 52;
  {
    const row = document.querySelector(".dashboard-active-btn") as HTMLElement | null;
    if (row) band = Math.max(40, Math.min(90, row.getBoundingClientRect().bottom - stageRect().top + 10));
  }
  if (cardMode()) { buildCardOverlay(); return; }
  riversControlsBottom = band + (riversPersistent ? 36 : 72);
  const el = document.createElement("div");
  el.className = "density-rivers-overlay";
  el.style.cssText = [
    "position:absolute",
    "top:0", "left:0",
    "width:100%",
    "z-index:201",
    "pointer-events:none",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    `padding:${band}px 0 0`,
    "gap:8px",
  ].join(";");

  // back button
  const back = document.createElement("button");
  back.textContent = "← Back";
  back.style.cssText = `pointer-events:auto;font-family:${UI_FONT};background:transparent;color:#455a64;border:none;border-radius:6px;padding:5px 10px;font-size:14px;font-weight:500;cursor:pointer;transition:background .15s;`;
  back.addEventListener("mouseenter", () => { back.style.background = "rgba(55,71,79,0.08)"; });
  back.addEventListener("mouseleave", () => { back.style.background = "transparent"; });
  back.addEventListener("click", () => handleBackButton());

  // Which page this is and how many visitors are on it, at the top CENTRE with the Back button
  // just to its left (a way out beside where you are). Three columns, so the title stays
  // exactly central however wide the page name or the count is, and Back hugs its left edge.
  const header = document.createElement("div");
  header.style.cssText = "display:grid;grid-template-columns:1fr auto 1fr;align-items:center;column-gap:14px;width:100%;margin-bottom:4px;";
  const backCell = document.createElement("div");
  backCell.style.cssText = "justify-self:end;";
  backCell.appendChild(back);
  const title = document.createElement("div");
  title.style.cssText =
    "display:flex;align-items:baseline;gap:8px;" +
    `font-family:${UI_FONT};line-height:1.25;white-space:nowrap;` +
    "max-width:calc(100vw * 0.4);min-width:0;";
  const name = document.createElement("div");
  name.textContent = riversPageName;
  name.style.cssText = "font-size:18px;font-weight:600;color:#37474f;overflow:hidden;text-overflow:ellipsis;min-width:0;flex:0 1 auto;";
  const count = document.createElement("div");
  count.style.cssText = "font-size:13px;color:#696969;flex:none;";
  const countNum = document.createElement("span");
  const countWords = document.createElement("span");
  countWords.style.cssText = "color:#b4b4b4;";
  count.appendChild(countNum);
  count.appendChild(countWords);
  riversTitleNumEl = countNum;
  riversTitleWordsEl = countWords;
  riversTitleCountEl = count;
  riversOnPageShown = -1;
  title.appendChild(name);
  title.appendChild(count);
  header.appendChild(backCell);
  header.appendChild(title);
  header.appendChild(document.createElement("div"));

  // dimension buttons row
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:8px;pointer-events:auto;";
  const dims: Array<DimensionId | null> = [null, "interactionState", "browser", "newReturn", "converted"];
  for (const dimId of dims) {
    const btn = document.createElement("button");
    btn.textContent = dimId ? dimDef(dimId).label : "One cloud";
    btn.dataset.dim = dimId ?? "none";
    btn.style.cssText = `font-family:${UI_FONT};background:#1e2434;color:#ccc;border:1px solid #444;border-radius:6px;padding:5px 12px;font-size:13px;cursor:pointer;transition:background 0.2s;`;
    if (dimId === riversActiveDimension) {
      btn.style.background = "#2d4a6e";
      btn.style.color = "#fff";
      btn.style.borderColor = "#4fc3f7";
    }
    const unavailable = dimId === "interactionState" && interactionStatesProvider?.() === false;
    if (unavailable) {
      // kept visible (so it is clear the grouping exists) but off, with the reason on hover
      btn.setAttribute("aria-disabled", "true");
      btn.title = "Interaction states are only recorded live, so there are none to group a past day by.";
      btn.style.opacity = "0.45";
      btn.style.cursor = "not-allowed";
    }
    btn.addEventListener("click", () => { if (!unavailable) setRiversDimension(dimId); });
    row.appendChild(btn);
  }

  const zbtn = (label: string, fn: () => void) => {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = `font-family:${UI_FONT};background:#1e2434;color:#fff;border:1px solid #444;border-radius:6px;padding:5px 11px;font-size:13px;cursor:pointer;`;
    b.addEventListener("click", fn);
    return b;
  };
  const center = () => ({ x: riversCssW() / 2, y: riversCssH() / 2 });
  row.appendChild(zbtn("\u2212", () => { const c = center(); zoomBy(1 / 1.5, c.x, c.y); }));
  row.appendChild(zbtn("+", () => { const c = center(); zoomBy(1.5, c.x, c.y); }));
  riversZoomEl = document.createElement("span");
  riversZoomEl.style.cssText = `font-family:${UI_FONT};color:#37474f;font-size:12px;align-self:center;min-width:34px;text-align:center;`;
  riversZoomEl.textContent = "1.0\u00d7";
  row.appendChild(riversZoomEl);
  row.appendChild(zbtn("Reset", resetCamera));

  if (riversPersistent) {
    riversTitleNumEl = null; riversTitleWordsEl = null; riversTitleCountEl = null;
  } else {
    el.appendChild(header);
  }
  el.appendChild(row);
  document.body.appendChild(el);

  // position relative to SVG
  if (svgEl) {
    const rect = stageRect();
    el.style.left = `${rect.left + window.scrollX}px`;
    el.style.top  = `${rect.top  + window.scrollY}px`;
    el.style.width = `${rect.width}px`;
  }

  riversOverlay = el;
}


/** the card's own controls: Back top left, the view dropdown and zoom top right, the page's name and count bottom left */
let riversDimLabelEl: HTMLElement | null = null;
let riversCardEl: HTMLElement | null = null;
// the pills live on their own layer ABOVE the canvas (which covers the whole stage and takes the mouse), so they can be clicked
let riversPillLayer: HTMLElement | null = null;
let riversPillEls: { el: HTMLElement; countEl: HTMLElement; slot: number | undefined; shown: number }[] = [];
// "Browser" is the filter grouping like every other filter (it lists every browser, not only the four it used to name); the built-in groupings come first, then the filters.
const DIM_MENU: Array<{ id: DimensionId | null; label: string; heading?: boolean }> = [
  { id: null, label: "One cloud" },
  { id: "interactionState", label: DIMENSIONS.interactionState.label },
  { id: "newReturn", label: DIMENSIONS.newReturn.label },
  { id: "converted", label: DIMENSIONS.converted.label },
  { id: null, label: "Split by filter", heading: true },
  ...ZOOM_FILTERS.map((f) => ({ id: filterDimId(f.menuType) as DimensionId, label: f.label })),
];
function dimLabel(dim: DimensionId | null) { return dim ? dimDef(dim).label : "One cloud"; }

interface CardControls { bar: HTMLElement; ddLabel: HTMLElement; menu: HTMLElement; zoomEl: HTMLElement; closeMenu: () => void; refresh: () => void; }
/**
 * The card's controls: the view dropdown, zoom out / in, the zoom level and Reset. One builder for a page's zoom and for the single
 * view. `live` says whether the camera exists (zoom only works then), `pick` what choosing a view does, `current` which view is chosen.
 * refresh() re-reads all three (the label, the highlighted item, which items are available, whether zoom is dimmed).
 */
function buildCardControlBar(live: () => boolean, pick: (id: DimensionId | null) => void, current: () => DimensionId | null): CardControls {
  const light = `font-family:${UI_FONT};background:#fff;color:#37474f;border:1px solid #b0bec5;border-radius:4px;font-size:12px;cursor:pointer;`;
  const bar = document.createElement("div");
  bar.style.cssText = "pointer-events:auto;position:absolute;right:14px;top:12px;display:flex;gap:8px;align-items:center;";
  const dd = document.createElement("div");
  dd.style.cssText = "position:relative;";
  const ddBtn = document.createElement("button");
  ddBtn.style.cssText = light + "padding:4px 10px;display:flex;gap:6px;align-items:center;";
  const ddLabel = document.createElement("span");
  const caret = document.createElement("span");
  caret.textContent = "▾";
  caret.style.cssText = "font-size:10px;color:#78909c;";
  ddBtn.appendChild(ddLabel); ddBtn.appendChild(caret);
  const menu = document.createElement("div");
  menu.style.cssText = "display:none;position:absolute;right:0;top:calc(100% + 4px);min-width:170px;max-height:min(440px,70vh);overflow-y:auto;background:#fff;border:1px solid #cfd8dc;border-radius:6px;box-shadow:0 6px 16px rgba(0,0,0,.18);padding:4px;z-index:5;";
  const items: Array<{ id: DimensionId | null; b: HTMLButtonElement }> = [];
  const unavailable = (id: DimensionId | null) => id === "interactionState" && interactionStatesProvider?.() === false;
  for (const item of DIM_MENU) {
    if (item.heading) {
      const h = document.createElement("div");
      h.textContent = item.label;
      h.style.cssText = `font-family:${UI_FONT};font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:#90a4ae;padding:8px 10px 3px;border-top:1px solid #eceff1;margin-top:4px;`;
      menu.appendChild(h);
      continue;
    }
    const b = document.createElement("button");
    b.dataset.dim = item.id ?? "none";
    b.textContent = item.label;
    b.style.cssText = `display:block;width:100%;text-align:left;font-family:${UI_FONT};font-size:13px;color:#37474f;background:transparent;border:none;border-radius:4px;padding:6px 10px;cursor:pointer;`;
    b.addEventListener("mouseenter", () => { if (!unavailable(item.id)) b.style.background = "#eceff1"; });
    b.addEventListener("mouseleave", () => { b.style.background = (b.dataset.dim === (current() ?? "none")) ? "#eceff1" : "transparent"; });
    b.addEventListener("click", () => { if (unavailable(item.id)) return; menu.style.display = "none"; pick(item.id); });
    menu.appendChild(b);
    items.push({ id: item.id, b });
  }
  ddBtn.addEventListener("click", (e) => { e.stopPropagation(); menu.style.display = menu.style.display === "none" ? "block" : "none"; });
  const closeMenu = () => { menu.style.display = "none"; };
  dd.appendChild(ddBtn); dd.appendChild(menu);
  bar.appendChild(dd);

  const zoomBtns: HTMLButtonElement[] = [];
  const zb = (label: string, fn: () => void) => {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = light + "padding:3px 9px;";
    b.addEventListener("click", () => { if (live()) fn(); });
    zoomBtns.push(b);
    return b;
  };
  const center = () => ({ x: riversCssW() / 2, y: riversCssH() / 2 });
  bar.appendChild(zb("−", () => { const c = center(); zoomBy(1 / 1.5, c.x, c.y); }));
  bar.appendChild(zb("+", () => { const c = center(); zoomBy(1.5, c.x, c.y); }));
  const zoomEl = document.createElement("span");
  zoomEl.style.cssText = `font-family:${UI_FONT};color:#78909c;font-size:11px;min-width:30px;text-align:center;`;
  zoomEl.textContent = "1.0×";
  bar.appendChild(zoomEl);
  bar.appendChild(zb("Reset", resetCamera));

  const refresh = () => {
    ddLabel.textContent = dimLabel(current());
    for (const { id, b } of items) {
      const off = unavailable(id), on = (id ?? "none") === (current() ?? "none");
      b.style.fontWeight = on ? "600" : "400";
      b.style.background = on ? "#eceff1" : "transparent";
      b.style.opacity = off ? "0.45" : "1";
      b.style.cursor = off ? "not-allowed" : "pointer";
      if (off) { b.setAttribute("aria-disabled", "true"); b.title = "Interaction states are only recorded live, so there are none to group a past day by."; }
      else { b.removeAttribute("aria-disabled"); b.removeAttribute("title"); }
    }
    // zoom needs the camera, which exists once the crowd has formed a cloud
    const on = live();
    for (const b of zoomBtns) {
      b.style.opacity = on ? "1" : "0.45";
      b.style.cursor = on ? "pointer" : "not-allowed";
      if (on) b.removeAttribute("title"); else b.title = "Zoom works once the crowd has formed a cloud.";
    }
    if (!on) zoomEl.textContent = "1.0×";
  };
  refresh();
  return { bar, ddLabel, menu, zoomEl, closeMenu, refresh };
}

// The single view's controls: on its always-present card, like its Back, so they are there with no crowd too. Choosing a view with
// no cloud yet is remembered and applies when the cloud opens; zoom is dimmed until there is a camera.
let singleCtl: (CardControls & { wrap: HTMLElement }) | null = null;
// Asked of the host when a grouping is chosen with no cloud yet: the host re-sends the counts, and a chosen grouping opens the cloud view.
let singleCloudRequest: (() => void) | null = null;
export function setSingleCloudRequestHandler(fn: (() => void) | null) { singleCloudRequest = fn; }
function chooseSingleDimension(id: DimensionId | null) {
  if (riversMode && riversPersistent) setRiversDimension(id);   // (which also remembers it)
  else { singleViewDimension = id; singleCtl?.refresh(); singleCloudRequest?.(); }
}
function placeSingleControls(card: { left: number; top: number; w: number }) {
  if (!singleCtl) {
    const wrap = document.createElement("div");
    wrap.dataset.singleControls = "1";
    wrap.style.cssText = "position:absolute;z-index:202;height:0;pointer-events:none;";
    const ctl = buildCardControlBar(() => riversMode && riversPersistent, chooseSingleDimension, () => singleViewDimension);
    wrap.appendChild(ctl.bar);
    document.body.appendChild(wrap);
    document.addEventListener("click", ctl.closeMenu);
    singleCtl = { ...ctl, wrap };
  }
  singleCtl.wrap.style.left = `${card.left + window.scrollX}px`;
  singleCtl.wrap.style.top = `${card.top + window.scrollY}px`;
  singleCtl.wrap.style.width = `${card.w}px`;
  singleCtl.refresh();
}
function removeSingleControls() {
  if (!singleCtl) return;
  document.removeEventListener("click", singleCtl.closeMenu);
  singleCtl.wrap.remove();
  singleCtl = null;
}

function buildCardOverlay() {
  riversControlsBottom = 56;
  const rect = stageRect();
  const el = document.createElement("div");
  el.className = "density-rivers-overlay";
  el.style.cssText = [
    "position:absolute", `left:${rect.left + window.scrollX}px`, `top:${rect.top + window.scrollY}px`,
    `width:${rect.width}px`, `height:${rect.height}px`, "z-index:201", "pointer-events:none",
  ].join(";");

  const back = document.createElement("button");
  back.textContent = "← Back";
  back.style.cssText = `pointer-events:auto;position:absolute;left:14px;top:12px;font-family:${UI_FONT};background:transparent;color:#455a64;border:none;border-radius:6px;padding:4px 8px;font-size:14px;font-weight:500;cursor:pointer;transition:background .15s;`;
  back.addEventListener("mouseenter", () => { back.style.background = "rgba(55,71,79,0.08)"; });
  back.addEventListener("mouseleave", () => { back.style.background = "transparent"; });
  back.addEventListener("click", () => handleBackButton());
  // (the single view has no Back: its way out is the page view icon, which stays live above it)
  if (!riversPersistent) el.appendChild(back);

  // the view dropdown, then zoom out / in, the zoom level and Reset. (The single view's live on its always-present card instead, see
  // placeSingleControls: they exist with no crowd too, and this overlay only exists once the crowd has formed a cloud.)
  if (riversPersistent) {
    reconcileSingleCard();
    riversZoomEl = singleCtl?.zoomEl ?? null;
    riversDimLabelEl = singleCtl?.ddLabel ?? null;
  } else {
    const ctl = buildCardControlBar(() => true, (id) => setRiversDimension(id), () => riversActiveDimension);
    riversDimLabelEl = ctl.ddLabel;
    riversZoomEl = ctl.zoomEl;
    document.addEventListener("click", ctl.closeMenu);
    riversMenuCloser = ctl.closeMenu;
    riversMenuEl = ctl.menu;
    el.appendChild(ctl.bar);
  }

  // which page this is and how many are on it, bottom left of the card, large. (The single view's label lives on its always-present
  // SVG card, see reconcileSingleCard, so it is not made twice.)
  if (riversPersistent) { riversTitleNumEl = null; riversTitleWordsEl = null; riversTitleCountEl = null; riversOnPageShown = -1; }
  else {
    const title = document.createElement("div");
    title.style.cssText = `position:absolute;left:${CARD_TITLE_INSET_PX}px;bottom:${CARD_TITLE_INSET_PX}px;display:flex;align-items:baseline;gap:10px;font-family:${UI_FONT};line-height:1.15;white-space:nowrap;max-width:60%;`;
    const name = document.createElement("div");
    name.textContent = riversPageName;
    name.style.cssText = `font-size:${CARD_TITLE_PX}px;font-weight:600;color:#37474f;overflow:hidden;text-overflow:ellipsis;min-width:0;`;
    // A real page's title opens its detail drawer, like its label in the grid: a plain link (no tooltip) whose hover is a rounded tinted
    // rectangle behind the text, the SAME tint and 6px radius as Back, the grid label and the view icons. "All other pages" is a group, not a page, and has no drawer, so its title stays plain.
    if (riversSlotIndex !== UNKNOWN_SLOT_INDEX && riversSlotIndex !== SINGLE_SLOT_INDEX && riversPageName) {
      const pageName = riversPageName;
      name.dataset.pageLink = "1";
      name.style.cssText += "pointer-events:auto;cursor:pointer;border-radius:6px;padding:0 6px;margin-left:-6px;transition:background .15s;";
      name.addEventListener("mouseenter", () => { name.style.background = "rgba(55,71,79,0.08)"; });   // (the same tint as Back)
      name.addEventListener("mouseleave", () => { name.style.background = "transparent"; });
      name.addEventListener("click", () => pageTitleHandler?.(pageName));
    }
    const count = document.createElement("div");
    count.style.cssText = `font-size:${CARD_COUNT_PX}px;color:#696969;flex:none;`;
    const countNum = document.createElement("span");
    const countWords = document.createElement("span");
    countWords.style.cssText = "color:#b4b4b4;";
    count.appendChild(countNum); count.appendChild(countWords);
    riversTitleNumEl = countNum; riversTitleWordsEl = countWords; riversTitleCountEl = count;
    riversOnPageShown = -1;
    title.appendChild(name); title.appendChild(count);
    el.appendChild(title);
  }

  // the goal page's flag, bottom right of its card, where the grid puts it on the box (a real page only: not "All other pages", not the single view)
  if (!riversPersistent && riversSlotIndex !== UNKNOWN_SLOT_INDEX && riversSlotIndex !== SINGLE_SLOT_INDEX && riversPageName && goalPageProvider?.(riversPageName)) {
    const flag = createGoalFlagElement(GOAL_FLAG_CARD_PX);
    flag.style.cssText += "position:absolute;right:10px;bottom:10px;";
    el.appendChild(flag);
  }

  document.body.appendChild(el);
  riversOverlay = el;
  placeGearRow();
}
let riversMenuCloser: (() => void) | null = null;
let riversMenuEl: HTMLElement | null = null;

// The app's own view icons (gear, page view, single view) sit in a row at the top centre of the grey stage. In the card they belong in
// the card's top row, so the row is shifted down onto it (a CSS transform; the row itself is the app's, not ours).
function placeGearRow(cardTop?: number) {
  const row = document.querySelector(".dashboard-active-btn") as HTMLElement | null;
  if (!row || !svgEl) return;
  const prev = row.style.transform;
  row.style.transform = "";
  // measured WITHOUT the shift already applied (the class carries it), or a second call would shift twice
  document.body.classList.remove("density-card");
  const rr = row.getBoundingClientRect();
  const shift = (cardTop ?? stageRect().top) + 10 - rr.top;
  document.body.style.setProperty("--density-card-gear-shift", `${Math.round(shift)}px`);
  document.body.classList.add("density-card");
  row.style.transform = prev;
}

/**
 * The card's surroundings: the white card itself, a sliver of the neighbouring box on each side that has one, and a small pill for
 * every direction that has a page (name, an arrow toward the card = the way traffic flows in, and a quiet live count). Dynamic: a
 * direction with no assigned page gets neither sliver nor pill, so an edge or corner page shows fewer.
 */
const PILL_ARROWS: Record<NeighborDir, string> = { top: "\u2193", bottom: "\u2191", left: "\u2192", right: "\u2190", tl: "\u2198", tr: "\u2199", bl: "\u2197", br: "\u2196" };
/* ───────── THE ZOOM-IN TRANSITION ─────────
   Clicking a page's box does not swap views: the box GROWS into the card, the neighbouring boxes slide out to become the slivers
   around it, and the rest of the grid fades away. Back plays it in reverse (a ghost of the card shrinks back to the box). The
   moves are FLIPs (a CSS transform from where the thing was to where it now is), and the cluster's own camera scales up with the
   card so the dots grow with it. Nothing runs under reduced motion, and a silent re-zoom (after a scrub) skips it. */
const CARD_ANIM_MS = 520;
let cardAnim: { t0: number; s0: number } | null = null;
let gridHideTimer: number | null = null;
let ghostTimer: number | null = null;
let cardParts: { card?: HTMLElement; slivers: Partial<Record<"top" | "bottom" | "left" | "right", HTMLElement>> } = { slivers: {} };
const prefersReducedMotion = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** a grid box's rectangle on screen (the box is hidden during a zoom, but still laid out) */
function boxRectFor(slot: number | undefined): DOMRect | null {
  if (slot === undefined || !svgEl) return null;
  const r = Array.from(svgEl.querySelectorAll("rect")).find((e) => e.getAttribute("rectIndex") === String(slot) || e.getAttribute("rectindex") === String(slot));
  return r ? (r as SVGGraphicsElement).getBoundingClientRect() : null;
}
function flipFrom(el: HTMLElement, from: DOMRect, delayMs = 0) {
  const to = el.getBoundingClientRect();
  if (!(to.width > 0 && to.height > 0)) return;
  el.style.transformOrigin = "0 0";
  el.style.transition = "none";
  el.style.transform = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`;
  void el.getBoundingClientRect();
  el.style.transition = `transform ${CARD_ANIM_MS}ms cubic-bezier(.2,.8,.2,1) ${delayMs}ms`;
  el.style.transform = "none";
}
function fadeIn(el: HTMLElement | null, delayMs: number) {
  if (!el) return;
  el.style.transition = "none";
  el.style.opacity = "0";
  void el.getBoundingClientRect();
  el.style.transition = `opacity 260ms ease ${delayMs}ms`;
  el.style.opacity = "1";
}
/** play the opening: the card grows out of its box, the neighbours slide out, the rest fades in */
function playCardOpen(fromBox: DOMRect, neighbors: SlotNeighbors) {
  const final = stageRect();
  flipFrom(cardParts.card as HTMLElement, fromBox);
  (["top", "bottom", "left", "right"] as const).forEach((dir) => {
    const el = cardParts.slivers[dir], src = boxRectFor(neighbors.slots?.[dir]);
    if (el && src) flipFrom(el, src);
  });
  for (const p of riversPillEls) fadeIn(p.el, CARD_ANIM_MS * 0.55);
  fadeIn(riversCanvas, CARD_ANIM_MS * 0.35);
  fadeIn(riversOverlay, CARD_ANIM_MS * 0.5);
  cardAnim = { t0: performance.now(), s0: Math.max(0.12, Math.min(1, Math.min(fromBox.width / final.width, fromBox.height / final.height))) };
}
/** the reverse: a ghost of the card shrinks back into the box while the grid fades in */
function playCardClose(box: DOMRect | null) {
  const card = cardParts.card;
  if (!card || !box || prefersReducedMotion()) return;
  const r = card.getBoundingClientRect();
  const ghost = document.createElement("div");
  ghost.style.cssText = `position:absolute;z-index:199;pointer-events:none;background:#fff;border-radius:3px;box-shadow:0 8px 22px rgba(0,0,0,.20);transform-origin:0 0;left:${r.left + window.scrollX}px;top:${r.top + window.scrollY}px;width:${r.width}px;height:${r.height}px;`;
  document.body.appendChild(ghost);
  void ghost.getBoundingClientRect();
  ghost.style.transition = `transform ${CARD_ANIM_MS * 0.8}ms cubic-bezier(.4,0,.2,1), opacity ${CARD_ANIM_MS * 0.8}ms ease`;
  ghost.style.transform = `translate(${box.left - r.left}px, ${box.top - r.top}px) scale(${box.width / r.width}, ${box.height / r.height})`;
  ghost.style.opacity = "0.2";
  if (ghostTimer) window.clearTimeout(ghostTimer);
  ghostTimer = window.setTimeout(() => { ghost.remove(); ghostTimer = null; }, CARD_ANIM_MS * 0.8 + 60);
}

let panHandler: ((slot: number, dir: NeighborDir) => void) | null = null;
export function setPanHandler(fn: typeof panHandler) { panHandler = fn; }
// The card's big page title is a link to the page's detail drawer, exactly as the page's label is in the grid (the host emits the same event).
let pageTitleHandler: ((pageName: string) => void) | null = null;
export function setPageTitleHandler(fn: typeof pageTitleHandler) { pageTitleHandler = fn; }
export function isPageZoomOpen(): boolean { return riversMode && !riversPersistent; }

let panning = false;
/**
 * Pan the stage to a neighbouring page: the card you are on slides away from that side and fades while the neighbour's card slides in
 * from it, with the same easing and time as the zoom-in and zoom-out, so the movements read as one family. The old view is frozen
 * into a ghost (a clone of its card, controls and a snapshot of its dots), the new page is opened underneath without its own
 * opening transition, and both are shifted a fraction of a card in the direction of the pan.
 */
export function panToSlot(slot: number, pageName: string, neighbors: SlotNeighbors, cx: number, cy: number, w: number, h: number, dir: NeighborDir) {
  if (!riversMode || riversPersistent || !svgEl || panning) return;
  if (prefersReducedMotion()) { handleBackButton(false); zoomSlot(slot, pageName, neighbors, cx, cy, w, h, false); return; }
  panning = true;
  const P = panelRect(), g = riversCardGeo;
  const dx = dir === "left" || dir === "tl" || dir === "bl" ? -1 : dir === "right" || dir === "tr" || dir === "br" ? 1 : 0;
  const dy = dir === "top" || dir === "tl" || dir === "tr" ? -1 : dir === "bottom" || dir === "bl" || dir === "br" ? 1 : 0;
  const tx = dx * g.cardW * 0.28, ty = dy * g.cardH * 0.28;
  // the ghost of the view being left, clipped to the stage
  const wrap = document.createElement("div");
  wrap.style.cssText = `position:absolute;z-index:202;pointer-events:none;overflow:hidden;left:${P.left + window.scrollX}px;top:${P.top + window.scrollY}px;width:${P.width}px;height:${P.height}px;`;
  const inner = document.createElement("div");
  inner.style.cssText = "position:absolute;inset:0;";
  wrap.appendChild(inner);
  if (riversCardEl) { const c = riversCardEl.cloneNode(true) as HTMLElement; c.style.left = "0"; c.style.top = "0"; inner.appendChild(c); }
  if (riversPillLayer) { const pl = riversPillLayer.cloneNode(true) as HTMLElement; pl.style.left = "0"; pl.style.top = "0"; pl.style.zIndex = ""; pl.style.pointerEvents = "none"; pl.querySelectorAll<HTMLElement>("*").forEach((n) => { n.style.pointerEvents = "none"; }); inner.appendChild(pl); }
  if (riversCanvas) {
    try {
      const im = document.createElement("img");
      im.src = riversCanvas.toDataURL();
      im.style.cssText = `position:absolute;left:0;top:0;width:${P.width}px;height:${P.height}px;`;
      inner.appendChild(im);
    } catch { /* no snapshot: the dots simply are not in the ghost */ }
  }
  if (riversOverlay) {
    const o = riversOverlay.cloneNode(true) as HTMLElement;
    o.querySelectorAll<HTMLElement>("*").forEach((n) => { n.style.pointerEvents = "none"; });   // (a frozen picture, not controls)
    o.style.left = `${g.ix}px`; o.style.top = `${g.it}px`;
    inner.appendChild(o);
  }
  document.body.appendChild(wrap);
  // open the new page (silently: the pan is its transition)
  handleBackButton(false);
  zoomSlot(slot, pageName, neighbors, cx, cy, w, h, false);
  const fresh = [riversEdgeEl, riversPillLayer, riversCanvas, riversOverlay];
  for (const el of fresh) {
    if (!el) continue;
    el.style.transition = "none";
    el.style.opacity = "0";
    el.style.transform = `translate(${tx}px, ${ty}px)`;
  }
  void wrap.getBoundingClientRect();
  const ease = `${CARD_ANIM_MS}ms cubic-bezier(.2,.8,.2,1)`;
  for (const el of fresh) {
    if (!el) continue;
    el.style.transition = `transform ${ease}, opacity ${CARD_ANIM_MS * 0.7}ms ease`;
    el.style.opacity = "1";
    el.style.transform = "none";
  }
  inner.style.transition = `transform ${ease}, opacity ${CARD_ANIM_MS * 0.7}ms ease`;
  inner.style.transform = `translate(${-tx}px, ${-ty}px)`;
  inner.style.opacity = "0";
  window.setTimeout(() => { wrap.remove(); panning = false; for (const el of fresh) { if (el) { el.style.transition = ""; el.style.transform = ""; } } }, CARD_ANIM_MS + 80);
}

function buildCardChrome(neighbors: SlotNeighbors) {
  const P = panelRect(), { ix, it, ib } = cardInsets(P.width, P.height);
  const W = P.width, H = P.height, cardW = W - 2 * ix, cardH = H - it - ib;
  const sT = it * 0.4, sB = ib * 0.4, sS = ix * 0.45;
  const el = document.createElement("div");
  el.style.cssText = `position:absolute;z-index:199;pointer-events:none;left:${P.left + window.scrollX}px;top:${P.top + window.scrollY}px;width:${W}px;height:${H}px;overflow:hidden;`;
  const box = (css: string) => { const d = document.createElement("div"); d.style.cssText = "position:absolute;background:#fff;box-shadow:0 6px 14px rgba(0,0,0,.16);" + css; el.appendChild(d); return d; };
  cardParts = { slivers: {} };
  cardParts.card = box(`left:${ix}px;top:${it}px;width:${cardW}px;height:${cardH}px;border-radius:3px;box-shadow:0 8px 22px rgba(0,0,0,.20);`);
  if (neighbors.top) cardParts.slivers.top = box(`left:${ix}px;top:-6px;width:${cardW}px;height:${sT + 6}px;border-radius:0 0 3px 3px;`);
  if (neighbors.bottom) cardParts.slivers.bottom = box(`left:${ix}px;bottom:-6px;width:${cardW}px;height:${sB + 6}px;border-radius:3px 3px 0 0;`);
  if (neighbors.left) cardParts.slivers.left = box(`left:-6px;top:${it}px;width:${sS + 6}px;height:${cardH}px;border-radius:0 3px 3px 0;`);
  if (neighbors.right) cardParts.slivers.right = box(`right:-6px;top:${it}px;width:${sS + 6}px;height:${cardH}px;border-radius:3px 0 0 3px;`);
  riversPillEls = [];
  const layer = document.createElement("div");
  layer.style.cssText = `position:absolute;z-index:203;pointer-events:none;overflow:hidden;left:${P.left + window.scrollX}px;top:${P.top + window.scrollY}px;width:${W}px;height:${H}px;`;
  const pill = (dir: NeighborDir, pos: string) => {
    const name = neighbors[dir];
    if (!name) return;
    const sp = document.createElement("span");
    sp.style.cssText = `position:absolute;display:flex;align-items:center;gap:5px;max-width:${Math.max(110, ix - 10)}px;font-family:${UI_FONT};font-size:11px;letter-spacing:.02em;color:#546e7a;background:rgba(255,255,255,.92);border:1px solid #cfd8dc;border-radius:999px;padding:3px 9px;white-space:nowrap;` + pos;
    const arrow = document.createElement("span");
    arrow.textContent = PILL_ARROWS[dir];
    const nm = document.createElement("span");
    nm.textContent = name;
    nm.style.cssText = "overflow:hidden;text-overflow:ellipsis;min-width:0;";
    const ct = document.createElement("span");
    ct.style.cssText = "color:#b0bec5;font-size:10px;flex:none;";
    sp.appendChild(arrow); sp.appendChild(nm); sp.appendChild(ct);
    layer.appendChild(sp);
    riversPillEls.push({ el: sp, countEl: ct, slot: neighbors.slots?.[dir], shown: -1 });
    // a pill is a way to get there: clicking it pans the stage over to that page
    const target = neighbors.slots?.[dir];
    if (target !== undefined) {
      sp.style.pointerEvents = "auto";
      sp.style.cursor = "pointer";
      sp.addEventListener("mouseenter", () => { sp.style.background = "#fff"; sp.style.borderColor = "#90a4ae"; });
      sp.addEventListener("mouseleave", () => { sp.style.background = "rgba(255,255,255,.92)"; sp.style.borderColor = "#cfd8dc"; });
      sp.addEventListener("click", (e) => { e.stopPropagation(); panHandler?.(target, dir); });
    }
  };
  const cx = `left:${ix + cardW / 2}px;transform:translate(-50%,-50%);`;
  const midY = it + cardH / 2;
  pill("top", `${cx}top:${sT}px;`);
  pill("bottom", `${cx}top:${H - sB}px;`);
  pill("left", `left:6px;top:${midY}px;transform:translateY(-50%);`);
  pill("right", `right:6px;top:${midY}px;transform:translateY(-50%);`);
  pill("tl", `left:6px;top:${sT}px;transform:translateY(-50%);`);
  pill("tr", `right:6px;top:${sT}px;transform:translateY(-50%);`);
  pill("bl", `left:6px;top:${H - sB}px;transform:translateY(-50%);`);
  pill("br", `right:6px;top:${H - sB}px;transform:translateY(-50%);`);
  document.body.appendChild(el);
  document.body.appendChild(layer);
  riversCardEl = el;
  riversPillLayer = layer;
}
function updatePillCounts() {
  for (const p of riversPillEls) {
    const n = p.slot === undefined ? 0 : (slotCounts.get(p.slot) ?? 0);
    if (n === p.shown) continue;
    p.shown = n;
    p.countEl.textContent = n > 0 ? String(n) : "";
  }
}

function buildEdgePills(neighbors: SlotNeighbors) {
  if (riversEdgeEl || !svgEl) return;
  riversNeighbors = neighbors;
  if (cardMode()) {
    // (the single view's card is the always-present one on the SVG, see reconcileSingleCard: nothing to build here)
    if (riversPersistent) return;
    buildCardChrome(neighbors); riversEdgeEl = riversCardEl; return;
  }
  const rect = stageRect();
  const el = document.createElement("div");
  el.style.cssText = [
    "position:absolute", "z-index:201", "pointer-events:none",
    `left:${rect.left + window.scrollX}px`, `top:${rect.top + window.scrollY}px`,
    `width:${rect.width}px`, `height:${rect.height}px`,
  ].join(";");
  // arrows point toward the cloud: the label names where visitors come from
  // and go to in that direction (rivers mockup .edge-label)
  const pill = (text: string, pos: string) => {
    const sp = document.createElement("span");
    sp.textContent = text;
    sp.style.cssText = `position:absolute;font-family:${UI_FONT};font-size:12px;letter-spacing:.02em;color:#455a64;background:rgba(255,255,255,.85);border:1px solid #cfd8dc;border-radius:999px;padding:4px 11px;` + pos;
    el.appendChild(sp);
  };
  if (neighbors.top) pill(`\u2193 ${neighbors.top}`, `top:${riversControlsBottom + 20}px;left:50%;transform:translateX(-50%);`);
  if (neighbors.bottom) pill(`${neighbors.bottom} \u2191`, "bottom:10px;left:50%;transform:translateX(-50%);");
  if (neighbors.left) pill(`${neighbors.left} \u2192`, "left:10px;top:50%;transform:translateY(-50%);");
  if (neighbors.right) pill(`\u2190 ${neighbors.right}`, "right:10px;top:50%;transform:translateY(-50%);");
  document.body.appendChild(el);
  riversEdgeEl = el;
}

function destroyRiversOverlay() {
  riversEdgeEl?.remove();
  riversEdgeEl = null;
  riversCardEl = null;
  riversPillLayer?.remove();
  riversPillLayer = null;
  riversPillEls = [];
  riversDimLabelEl = null;
  if (riversMenuCloser) { document.removeEventListener("click", riversMenuCloser); riversMenuCloser = null; }
  riversMenuEl = null;
  if (!singleCardShown) document.body.classList.remove("density-card");
  riversTitleCountEl = null;
  riversTitleNumEl = null;
  riversTitleWordsEl = null;
  riversZoomEl = null;
  riversOverlay?.remove();
  riversOverlay = null;
}

function populateRiversParticles(now: number, slotIndex: number) {
  riversSlotIndex = slotIndex;
  riversParticles = [];
  riversById = new Map();
  riversKey = "";
  riversLeaving.clear();
  riversLeaveReason.clear();
  riversCamAnim = riversPersistent || cardMode() ? null : { t0: now };
  riversRegions = [];
  syncRiversParticles(now);
}

function setRiversDimension(dim: DimensionId | null) {
  if (!svgEl) return;
  riversActiveDimension = dim;
  if (riversPersistent) singleViewDimension = dim;
  resetRiversPacks();
  const cx = riversHome.x;
  const cy = riversHome.y;
  const now = performance.now();
  // A zoom opened on a quiet slot glides between its framed crowd (one cloud) and 1.0x (a grouping
  // lays its categories out over the whole stage, for that scale).
  if (riversAutoFit) {
    riversFitTrack = false;
    riversCamAnim = null;
    camera.cx = cx; camera.cy = cy;
    riversScaleAnim = { t0: now, from: camera.scale, to: dim ? 1 : crowdFitScale() };
  }

  if (dim) dimDef(dim).dynamic?.reset();   // a long list is laid out afresh, busiest groups first, each time it is chosen
  riversGridExtra = 0;
  riversRegions = dim ? layoutRegions(riversParticles.filter((p) => !p.following), dim, cx, cy) : [];
  if (riversGridExtra > 0) {
    // a layout bigger than the view opens at its top (where the busiest groups are), not wherever the camera was
    riversFitTrack = false; riversScaleAnim = null; riversCamAnim = null;
    camera.cx = cx; camera.cy = cy; camera.scale = Math.min(camera.scale, 1);
  }
  for (const p of riversParticles) {
    startTween(p, now);
    p.homeAngle = Math.random() * Math.PI * 2;
  }

  // update button highlight
  if (riversDimLabelEl) riversDimLabelEl.textContent = dimLabel(dim);
  const controlsRoot = riversPersistent ? singleCtl?.bar ?? null : riversOverlay;
  if (controlsRoot && cardMode()) {
    for (const b of Array.from(controlsRoot.querySelectorAll("button[data-dim]")) as HTMLButtonElement[]) {
      const on = b.dataset.dim === (dim ?? "none");
      b.style.fontWeight = on ? "600" : "400";
      b.style.background = on ? "#eceff1" : "transparent";
    }
  } else if (riversOverlay) {
    for (const btn of Array.from(riversOverlay.querySelectorAll("button[data-dim]")) as HTMLButtonElement[]) {
      const isActive = btn.dataset.dim === (dim ?? "none");
      btn.style.background = isActive ? "#2d4a6e" : "#1e2434";
      btn.style.color = isActive ? "#fff" : "#ccc";
      btn.style.borderColor = isActive ? "#4fc3f7" : "#444";
    }
  }
}

const riversPacks = new Map<string, Pack>();
let riversPackLastCheck = 0;

function resetRiversPacks() {
  riversPacks.clear();
  riversPackLastCheck = 0;
}

function assignRiversPacking(list: Particle[], W: number, H: number, now: number) {
  const def = riversActiveDimension ? dimDef(riversActiveDimension) : null;
  const groups = new Map<string, Particle[]>();
  let dirty = false;
  for (const q of list) {
    if (q.exitTarget) continue;
    const k = def ? def.getCategory(q) : "_";
    if (q.pcat !== k) { q.pcat = k; dirty = true; }
    const g = groups.get(k);
    if (g) g.push(q); else groups.set(k, [q]);
  }
  for (const k of Array.from(riversPacks.keys())) if (!groups.has(k)) riversPacks.delete(k);
  const safety = now - riversPackLastCheck > SAFETY_PASS_MS;
  if (safety) riversPackLastCheck = now;

  // Every group fits the SAME fixed region, and every dot is the same size
  // everywhere: set by whichever group is most crowded.
  const Rcap = def ? riversRegionR : Math.min(W, H) * 0.42;
  let sShared = Infinity;
  groups.forEach((g, k) => {
    let pack = riversPacks.get(k);
    if (!pack) { pack = newPack(); riversPacks.set(k, pack); }
    if (dirty || safety || pack.owner.size !== g.length) {
      const byId = new Map(g.map((q) => [q.visitorId, q] as const));
      packReconcile(pack, g.map((q) => q.visitorId),
        (v) => { const q = byId.get(v); return q ? { x: q.x - W / 2, y: q.y - H / 2 } : undefined; });
    }
    const nh = pack.holes.size;
    if (nh > 0) {
      const big = nh > 24;
      if (big || now - pack.lastCompact >= 150) { pack.lastCompact = now; packCompact(pack, big ? 1500 : 60); }
    }
    sShared = Math.min(sShared, packFitS(pack, Rcap));
  });
  if (!isFinite(sShared)) return;
  let dotR = 0;
  groups.forEach((g, k) => {
    const pack = riversPacks.get(k)!;
    packFit(pack, Rcap, sShared);
    dotR = pack.r;
    const lat = ensureLattice(pack.frontier + 8);
    for (const q of g) {
      const i = pack.owner.get(q.visitorId);
      if (i === undefined) continue;
      q.packX = (lat.x[i] + lat.jx[i] * pack.J) * pack.s; q.packY = (lat.y[i] + lat.jy[i] * pack.J) * pack.s;
    }
  });
  riversPackS = sShared;
  if (dotR > 0) riversDotR = dotR;
  for (const q of list) q.r = dotR;
}

// One-cloud view: every dot is exactly where the slot cloud has it. A visitor
// whose real bubble is still flying is drawn at that bubble's real position.
// When the view is grouped, only dots that have landed move to their group.
function updateRiversFollow(now: number, dim: DimensionId | null) {
  const st = stageRect();
  const cloud = slotClouds.get(riversSlotIndex);
  const cxr = riversCenter.x, cyr = riversCenter.y;
  for (let i = 0; i < riversParticles.length; i++) {
    const p = riversParticles[i];
    if (p.exitTarget) continue;      // on its way out through a pill: nothing to follow
    if (p.arrive && dim === null) {  // flying in from the spawn point: held at its spot, its flight is drawn on the full-window layer
      const h = arrivalHome(p);
      if (h) { p.x = h.x; p.y = h.y; p.sx = h.x; p.sy = h.y; p.tweenStartX = h.x; p.tweenStartY = h.y; p.tweenT0 = now; p.tweenDur = 1; }
      p.r = p.src?.r || slotClouds.get(riversSlotIndex)?.pack.r || p.r;
      p.following = true;
      continue;
    }
    // coming in from a neighbour's sliver: one straight run, easing in, to the place the cloud holds for it; then an ordinary dot
    if (p.enter && dim === null && !riversLeaving.has(p.visitorId)) {
      const q0 = p.src, a0 = riversArrived.get(p.visitorId), pt0 = cloud?.pendingTarget.get(p.visitorId);
      let tx = NaN, ty = NaN;
      if (q0) { tx = cxr + q0.x; ty = cyr + q0.y; }
      else if (a0) { tx = cxr + a0.x; ty = cyr + a0.y; }
      else if (pt0) { tx = pt0.x - st.offX; ty = pt0.y - st.offY; }
      if (isFinite(tx)) {
        const k = Math.min(1, (now - p.enter.t0) / p.enter.dur), e = 1 - (1 - k) * (1 - k);
        p.x = p.enter.x + (tx - p.enter.x) * e; p.y = p.enter.y + (ty - p.enter.y) * e;
        p.sx = p.x; p.sy = p.y; p.tweenStartX = p.x; p.tweenStartY = p.y; p.tweenT0 = now; p.tweenDur = 1;
        if (k >= 1) p.enter = undefined;
      } else { p.x = p.enter.x; p.y = p.enter.y; }
      p.following = true;
      continue;
    }
    // One cloud: everyone is shown where the real scene has them. Grouped: every dot, one
    // still flying in included, heads straight for its own category; only a visitor leaving
    // the page keeps following its real bubble (down to the dock when it closes the site).
    const follow = dim === null || riversLeaving.has(p.visitorId);
    if (!follow) {
      if (p.following) { startTween(p, now); p.following = false; }
      continue;
    }
    let px = 0, py = 0, has = false;
    const q = p.src;
    // (a dot only takes the slot cloud's own size in the one-cloud view; grouped, its size is the
    // view's shared dot size, and a visitor who is leaving keeps the size it had on screen)
    const leaving = riversLeaving.has(p.visitorId);
    if (q) { px = cxr + q.x; py = cyr + q.y; if (dim === null) p.r = q.r; has = true; }
    else {
      const a = riversArrived.get(p.visitorId);
      if (a) { px = cxr + a.x; py = cyr + a.y; if (dim === null) p.r = cloud?.pack.r || p.r; has = true; }
      else if (p.spawned && !leaving) {
        // a new visitor not yet landed waits at the spot reserved for it; it NEVER follows its real
        // bubble (which is drawn through the camera and would come in from somewhere else)
        const pt = cloud?.pendingTarget.get(p.visitorId);
        if (pt && cloud) { px = pt.x - st.offX; py = pt.y - st.offY; if (dim === null) p.r = cloud.pack.r || p.r; has = true; }
      } else {
        const bp = leaverPos(p.visitorId);
        if (bp) {
          px = bp.x - st.offX; py = bp.y - st.offY;
          if (dim === null && !leaving) p.r = (bubbleRadiusProvider?.() || 4) * DOT_SIZE_VS_BUBBLE;
          has = true;
        }
      }
    }
    if (has) {
      p.x = px; p.y = py; p.sx = px; p.sy = py;
      p.tweenStartX = px; p.tweenStartY = py; p.tweenT0 = now; p.tweenDur = 1;
    }
    p.following = true;
  }
}

const rAlpha = () => 0.88;
let riversFrame = 0;
const NO_MOVERS: Particle[] = [];

function renderRivers(now: number) {
  if (!riversCtx || !riversCanvas || !svgEl) return;
  const dpr = canvasDpr();
  const CW = riversCanvas.width / dpr, CH = riversCanvas.height / dpr;   // the canvas (the whole stage in a card)
  const W = riversCssW(), H = riversCssH();                              // the view: the card, or the whole canvas
  riversStageDims = { W, H };
  const ctx = riversCtx;
  const dim = riversActiveDimension;
  riversStageR = Math.min(W, H) * 0.32;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, CW, CH);
  // No background of our own: the app's real-time grey stage is already there.

  // a card opens by scaling its contents up with the card (the camera stays on the cluster)
  if (cardAnim) {
    const tt = Math.min(1, (now - cardAnim.t0) / CARD_ANIM_MS);
    camera.cx = riversHome.x; camera.cy = riversHome.y;
    camera.scale = cardAnim.s0 + (1 - cardAnim.s0) * easeOutCubic(tt);
    if (tt >= 1) cardAnim = null;
  }
  // the camera pans from where the grid was to the slot, once, on opening
  if (riversCamAnim) {
    const tt = Math.min(1, (now - riversCamAnim.t0) / CAM_PAN_MS);
    const e = easeInOutCubic(tt);
    camera.cx = W / 2 + (riversHome.x - W / 2) * e;
    camera.cy = H / 2 + (riversHome.y - H / 2) * e;
    // a quiet slot's zoom also closes in on its crowd as it pans there (one cloud only: a grouping lays out for 1.0x)
    if (riversFitTrack && dim === null) camera.scale = 1 + (crowdFitScale() - 1) * e;
    if (tt >= 1) riversCamAnim = null;
  }
  // between the framed view and a grouping's own 1.0x layout
  if (riversScaleAnim) {
    const ts = Math.min(1, (now - riversScaleAnim.t0) / FIT_SWITCH_MS);
    camera.scale = riversScaleAnim.from + (riversScaleAnim.to - riversScaleAnim.from) * easeInOutCubic(ts);
    if (ts >= 1) { riversScaleAnim = null; riversFitTrack = riversAutoFit && dim === null; }
  } else if (riversFitTrack && dim === null && !riversCamAnim) {
    // the crowd of a quiet slot can grow while it is watched: keep it framed, easing so it never jumps
    const target = crowdFitScale();
    if (Math.abs(camera.scale - target) > 0.005) camera.scale += (target - camera.scale) * 0.06;
  }
  updateRiversFollow(now, dim);
  const movers = dim || riversExiting > 0 ? riversParticles.filter((p) => !p.following || p.exitTarget) : NO_MOVERS;
  if (riversExiting > 0) {
    // those leaving fade out over the last part of their trip
    for (const q of riversParticles) {
      if (!q.exitTarget) continue;
      const tt = (now - q.tweenT0) / q.tweenDur;
      q.travelerAlpha = Math.max(0, 1 - Math.max(0, tt - 0.45) / 0.55);
    }
  }
  if (!dim) riversGridExtra = 0;
  riversRegions = dim ? layoutRegions(movers, dim, riversHome.x, riversHome.y) : [];

  ctx.save();
  ctx.translate(riversOrigin.x + W / 2, riversOrigin.y + H / 2);
  ctx.scale(camera.scale, camera.scale);
  ctx.translate(-camera.cx, -camera.cy);

  const frame = ++riversFrame;
  for (let i = 0; i < riversParticles.length; i++) {
    if ((i + frame) % 60 === 0) refreshColor(riversParticles[i], now);   // about once a second each
  }
  if (movers.length) {
    assignRiversPacking(movers, W, H, now);
    tickParticles(movers, 16, now, riversCenter.x, riversCenter.y, dim, riversRegions);
  }

  // only what is on screen is drawn
  const padW = 24 / camera.scale;
  const cull = {
    x0: camera.cx - W / (2 * camera.scale) - padW, x1: camera.cx + W / (2 * camera.scale) + padW,
    y0: camera.cy - H / (2 * camera.scale) - padW, y1: camera.cy + H / (2 * camera.scale) + padW,
  };
  // a visitor still flying in from the spawn point is drawn on the full-window layer instead
  const drawable = riversArriving > 0 ? riversParticles.filter((q) => !q.arrive) : riversParticles;
  if (cardMode()) {
    // inside the card everything is clipped to it; a dot on its way out of the card, or coming in from a neighbour, is not: it
    // crosses the sliver and fades at the stage's edge
    const inCard = drawable.filter((q) => !q.exitTarget && !q.enter), travelling = drawable.filter((q) => q.exitTarget || q.enter);
    ctx.save();
    ctx.beginPath();
    ctx.rect(cull.x0 + padW, cull.y0 + padW, cull.x1 - cull.x0 - 2 * padW, cull.y1 - cull.y0 - 2 * padW);
    ctx.clip();
    drawParticlesBatched(ctx, inCard, now, dim, rAlpha, camera.scale, undefined, cull);
    ctx.restore();
    if (travelling.length) {
      const g = riversCardGeo, sc = camera.scale;
      const wide = { x0: camera.cx - (W / 2 + g.ix) / sc - padW, x1: camera.cx + (W / 2 + g.panelW - g.ix - W) / sc + padW, y0: camera.cy - (H / 2 + g.it) / sc - padW, y1: camera.cy + (H / 2 + g.panelH - g.it - H) / sc + padW };
      drawParticlesBatched(ctx, travelling, now, dim, rAlpha, camera.scale, undefined, wide);
    }
  } else {
    drawParticlesBatched(ctx, drawable, now, dim, rAlpha, camera.scale, undefined, cull);
  }
  if (hoveredVisitorId) {
    const p = riversById.get(hoveredVisitorId);
    if (p) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + 4 / camera.scale, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(240,185,85,0.85)";
      ctx.lineWidth = 1.5 / camera.scale;
      ctx.stroke();
    }
  }
  drawRowMarks(ctx, now, camera.scale, (id) => riversById.get(id));

  // labels keep a constant on-screen size however far in we are: the NUMBER large on top,
  // the category's name small beneath it
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = 0.95;
  const labelFont = UI_FONT;
  const total = riversRegions.reduce((t, r) => t + r.count, 0);
  // in the card a layout bigger than the view is cut at the card's edge, like its dots, not left drawn across the stage
  if (cardMode()) {
    ctx.beginPath();
    ctx.rect(cull.x0 + padW, cull.y0 + padW, cull.x1 - cull.x0 - 2 * padW, cull.y1 - cull.y0 - 2 * padW);
    ctx.clip();
  }
  const clip = (text: string, maxPx: number) => {
    if (!(maxPx > 0) || ctx.measureText(text).width <= maxPx) return text;
    let t = text;
    while (t.length > 1 && ctx.measureText(t + "\u2026").width > maxPx) t = t.slice(0, -1);

    return t + "\u2026";
  };
  for (const reg of riversRegions) {
    const none = reg.count === 0;
    const yName = reg.y - reg.r - 12 / camera.scale;
    const yNum = yName - 21 / camera.scale;
    // the number large, and beside it - smaller and lighter - what share of the page it is
    const numText = reg.count.toLocaleString("en-US");
    const shareText = none ? "" : formatShare(reg.count, total);   // an empty group has no share to show
    ctx.font = `600 ${21 / camera.scale}px ${labelFont}`;
    const numW = ctx.measureText(numText).width;
    ctx.font = `600 ${12 / camera.scale}px ${labelFont}`;
    const gap = 6 / camera.scale;
    const shareW = shareText ? ctx.measureText(shareText).width + gap : 0;
    const left = reg.x - (numW + shareW) / 2;
    ctx.textAlign = "left";
    ctx.font = `600 ${21 / camera.scale}px ${labelFont}`;
    ctx.fillStyle = none ? "#9aa5ab" : "#37474f";
    ctx.fillText(numText, left, yNum);
    if (shareText) {
      ctx.font = `600 ${12 / camera.scale}px ${labelFont}`;
      ctx.fillStyle = none ? "#b0bac0" : "#78909c";
      ctx.fillText(shareText, left + numW + gap, yNum + 2 / camera.scale);
    }
    ctx.textAlign = "center";
    ctx.font = `600 ${12 / camera.scale}px ${labelFont}`;
    ctx.fillStyle = none ? "#9aa5ab" : "#78909c";
    ctx.fillText(riversGridExtra > 0 ? clip(reg.label, (riversCellW - 8) / camera.scale * 1) : reg.label, reg.x, yName);
  }
  ctx.restore();

  ctx.restore();

  renderFlights(now);
  if (riversArriving > 0) riversArriving = riversParticles.reduce((n, q) => n + (q.arrive ? 1 : 0), 0);
  if (riversTitleCountEl && riversOnPageShown !== riversOnPage) {
    riversOnPageShown = riversOnPage;
    if (riversTitleNumEl) riversTitleNumEl.textContent = riversOnPage.toLocaleString("en-US");
    if (riversTitleWordsEl) riversTitleWordsEl.textContent = riversSlotIndex === UNKNOWN_SLOT_INDEX ? " on other pages" : riversSlotIndex === SINGLE_SLOT_INDEX ? " onsite" : " on page";
  }
  if (riversPillEls.length) updatePillCounts();
  if (riversZoomEl) {
    const t = `${camera.scale.toFixed(1)}\u00d7`;
    if (riversZoomEl.textContent !== t) riversZoomEl.textContent = t;
  }
  if (riversCanvas) {
    // the pan hand: open while there is something to pan (zoomed in), closed while dragging;
    // a dot you can open shows a pointer; otherwise the ordinary arrow
    riversCanvas.style.cursor = riversDragging
      ? "grabbing"
      : hoveredVisitorId ? "pointer" : camera.scale > 1.05 || riversGridExtra > 0 ? "grab" : "default";
  }
}

// ── animation loop ────────────────────────────────────────────────────────────

function loop(ts: number) {
  rafHandle = requestAnimationFrame(loop);
  if (document.hidden) { lastTs = ts; return; }
  const dt = Math.min(ts - lastTs, DT_CAP);
  lastTs = ts;
  const now = ts;

  // the slot clouds keep running underneath the zoomed view: it shows THEIR dots
  for (const cloud of slotClouds.values()) {
    syncSlotParticles(cloud, now);
    renderSlotCloud(cloud, now, dt, !riversMode);
  }
  // Real bubbles must never show under a cloud. That is a STATE, so it is reconciled
  // here continuously and never left to whichever event happens to run the pass.
  if (now - lastHidePass > HIDE_PASS_MS) {
    lastHidePass = now;
    periodicHook?.();                       // cheap host checks that must hold with or without clouds
    reconcileSingleCard();
    if (slotClouds.size) bubbleHideHandler?.();
  }
  if (stampEl && now - stampAt > 1000) {
    stampAt = now;
    const perfText = perfProbeText();
    stampEl.textContent = `${stampBase}${perfText ? ` · ${perfText}` : ""} · cloud: landed ${cloudStats.landed}, moved ${cloudStats.moved}, left ${cloudStats.left}, dissolved ${cloudStats.dissolved}, repaired ${cloudStats.repaired} · arrivals: flew ${arrivalStats.flew}, in place ${arrivalStats.inPlace} (no spawn point ${arrivalStats.noSpawn}, already here when the view opened ${arrivalStats.quiet}), already in the cloud ${arrivalStats.alreadyThere}, from a page ${arrivalStats.fromPage} · ghost dots hidden ${ghostHidden} · single view: opened ${singleViewStats.opened}, repaired ${singleViewStats.repaired}, failed ${singleViewStats.failed}${stampExtra ? ` · ${stampExtra()}` : ""}${(playbackStatsProvider?.() || "") ? ` · ${playbackStatsProvider!()}` : ""}`;
  }
  if (riversMode) {
    syncRiversParticles(now);
    renderRivers(now);
  }
}

// The zoomed view's list of dots is rebuilt when something that decides it changed
// (a page change, the cloud's membership, a visitor leaving) and at most every
// quarter second otherwise, not every frame.
let riversKey = "";
let riversRebuiltAt = -1e9;
let riversById = new Map<string, Particle>();
const RIVERS_REFRESH_MS = 250;

function syncRiversParticles(now: number) {
  if (!svgEl) return;
  const st = stageRect();
  const cloud = slotClouds.get(riversSlotIndex);
  riversCenter = cloud
    ? { x: cloud.cx - st.offX, y: cloud.cy - st.offY }
    : { x: st.width / 2, y: st.height / 2 };
  riversHome = riversPersistent ? { x: st.width / 2, y: st.height / 2 } : riversCenter;

  // the cloud's own maps, not copies of them
  riversLanded = cloud ? cloud.byId : new Map();
  riversArrived = cloud ? cloud.arrived : new Map();
  const key = `${pageVersion}|${cloud ? `${cloud.mv}:${cloud.byId.size}:${cloud.arrived.size}:${cloud.pending.size}` : "x"}|${riversLeaving.size}`;
  if (key === riversKey && now - riversRebuiltAt < RIVERS_REFRESH_MS) return;
  riversKey = key;
  riversRebuiltAt = now;

  const want = new Set<string>();
  riversLanded.forEach((_, v) => want.add(v));
  riversArrived.forEach((_, v) => want.add(v));
  for (const [vid, pg] of visitorPage.entries()) {
    if (slotForPage(pg, riversSlotIndex, riversPageName) === riversSlotIndex) want.add(vid);
  }
  riversOnPage = want.size;   // everyone on this page now (visitors already leaving are added below)
  for (const [vid, since] of Array.from(riversLeaving.entries())) {
    if (now - since > 8000) { riversLeaving.delete(vid); riversLeaveReason.delete(vid); } else want.add(vid);
  }

  const have = riversById;
  const next: Particle[] = [];
  const nextById = new Map<string, Particle>();
  for (const vid of want) {
    const q = riversLanded.get(vid);
    const a = riversArrived.get(vid);
    const isLanded = !!q || !!a;
    const bp = isLanded ? null : leaverPos(vid);
    const exiting = !!have.get(vid)?.exitTarget;
    // not in the cloud and its real bubble is gone (it docked): nothing to show
    // (a dot already on its way out through an edge pill needs no bubble)
    if (!isLanded && !bp && !exiting) { riversLeaving.delete(vid); riversLeaveReason.delete(vid); continue; }
    // finished leaving through its pill
    if (exiting && now - (have.get(vid) as Particle).tweenT0 > (have.get(vid) as Particle).tweenDur + 200) {
      riversLeaving.delete(vid); riversLeaveReason.delete(vid);
      continue;
    }
    let p = have.get(vid);
    if (!p) {
      const x = q ? riversCenter.x + q.x : a ? riversCenter.x + a.x : bp ? bp.x - st.offX : riversCenter.x;
      const y = q ? riversCenter.y + q.y : a ? riversCenter.y + a.y : bp ? bp.y - st.offY : riversCenter.y;
      p = makeParticle(x, y, now, vid);
      p.following = true;
      // A brand-new visitor (no previous page, not in the cloud yet) is NEVER placed at its real
      // bubble: that bubble's path, drawn through the camera, comes in from somewhere else
      // entirely. It flies in from where it spawned in the sidebar, or, if that is unknown,
      // simply appears at its own spot.
      // (in the all-pages view a visitor not yet in the cloud is always an ARRIVAL, whatever page it has been on: there are no other
      // pages for it to have "moved here" from, and the old path entered it from the bottom-right corner)
      if (!isLanded && (!visitorPrevPage.has(vid) || riversPersistent)) {
        p.spawned = true;
        const sp = spawnPointProvider?.(vid);
        // Everyone who was already here when the view opened is THERE, not arriving (they flew in from the sidebar as tiny bubbles); anyone
        // who spawned after it opened flies in. This used to be "nobody for the first 3 seconds", which also swallowed every real arrival
        // right after a scrub or entering a replay (the single view re-opens each time), the very moments a replay is watched.
        const spawnedAfterOpen = !!sp && (sp.at === undefined || sp.at >= zoomOpenedAt - 200);
        if (sp && spawnedAfterOpen) { p.arrive = { x0: sp.x, y0: sp.y, tc: now, t0: -1 }; ensureFlightCanvas(); arrivalStats.flew++; }
        else { arrivalStats.inPlace++; if (!sp) arrivalStats.noSpawn++; else arrivalStats.quiet++; }
        const h = arrivalHome(p) || riversCenter;
        p.x = h.x; p.y = h.y; p.sx = h.x; p.sy = h.y;
        p.tweenStartX = h.x; p.tweenStartY = h.y; p.tweenT0 = now; p.tweenDur = 1;
      } else if (isLanded && !visitorPrevPage.has(vid)) {
        arrivalStats.alreadyThere++;
      } else if (!isLanded) {
        arrivalStats.fromPage++;
        // Moved here from another page: in a card the visitor comes in from the NEIGHBOUR'S SLIVER on the side where that page lies (all
        // eight directions), in a straight line to its place in the cluster. (It used to follow its real bubble, whose position on the
        // grid has no meaning inside a card.)
        if (cardMode()) {
          const d = dirOfPage(visitorPrevPage.get(vid));
          if (d) {
            const e = sliverPoint(d);
            p.enter = { x: e.x, y: e.y, t0: now, dur: moveMs(1100) };
            p.x = e.x; p.y = e.y; p.sx = e.x; p.sy = e.y; p.tweenStartX = e.x; p.tweenStartY = e.y; p.tweenT0 = now; p.tweenDur = 1;
          }
        }
      }
    }
    // a visitor who moved to another page leaves through the pill of that page, instead of
    // wandering off after its real bubble and sitting wherever that one stops
    if (!isLanded && !p.exitTarget && riversLeaving.has(vid) && riversLeaveReason.get(vid) === "moved") {
      startEdgeExit(p, vid, now, bp);
    }
    p.landed = isLanded;
    p.src = q;
    p.interactionState = visitorInteraction.get(vid) || "focused";
    p.stateT0 = visitorStateT0.get(vid);
    next.push(p);
    nextById.set(vid, p);
  }
  riversParticles = next;
  riversById = nextById;
  riversExiting = 0;
  riversArriving = 0;
  for (const q of next) { if (q.exitTarget) riversExiting++; if (q.arrive) riversArriving++; }
}

// Where a slot's box is on the real board (svg coords): lets a visitor who leaves for another page
// head the way that page REALLY lies from this one, not toward a side picked from where the dot
// happens to sit in the cloud.
let slotCenterProvider: ((slot: number) => { x: number; y: number } | null) | null = null;
export function setSlotCenterProvider(fn: typeof slotCenterProvider) {
  slotCenterProvider = fn;
}
const RIVERS_EXIT_MS = 900;
let riversExiting = 0;
let riversArriving = 0;
let riversDotR = 0;   // the size of every dot in the grouped view
/** which of the eight directions around the card a page lies in (null if that cannot be told) */
function dirOfPage(page?: string): NeighborDir | null {
  // the all-pages view has no other pages to come from
  if (riversPersistent) return null;
  const srcSlot = page && pageSlotProvider ? pageSlotProvider(page) : -1;
  const src = srcSlot >= 0 ? slotCenterProvider?.(srcSlot) : null;
  const cl = slotClouds.get(riversSlotIndex);
  if (!src || !cl) return null;
  const c0 = slotCenterProvider?.(0), c1 = slotCenterProvider?.(1), c5 = slotCenterProvider?.(5);
  const cw = c0 && c1 ? Math.abs(c1.x - c0.x) || 1 : 1, rh = c0 && c5 ? Math.abs(c5.y - c0.y) || 1 : 1;
  return entryDirection(src.x - cl.cx, src.y - cl.cy, cw, rh);
}
/** the middle of a neighbour's sliver (or corner region) in the view's world coordinates, where a visitor from that side comes in */
function sliverPoint(dir: NeighborDir) {
  const g = riversCardGeo, W = riversCssW(), H = riversCssH(), sc = camera.scale;
  const sS = g.ix * 0.45, sT = g.it * 0.4, sB = g.ib * 0.4;
  const left = dir === "left" || dir === "tl" || dir === "bl", right = dir === "right" || dir === "tr" || dir === "br";
  const top = dir === "top" || dir === "tl" || dir === "tr", bottom = dir === "bottom" || dir === "bl" || dir === "br";
  const sx = left ? -g.ix + sS / 2 : right ? g.cardW + g.ix - sS / 2 : g.cardW / 2;
  const sy = top ? -g.it + sT / 2 : bottom ? g.cardH + g.ib - sB / 2 : g.cardH / 2;
  return { x: camera.cx + (sx - W / 2) / sc, y: camera.cy + (sy - H / 2) / sc };
}

function startEdgeExit(p: Particle, vid: string, now: number, bp: { x: number; y: number } | null | undefined) {
  const cloud = slotClouds.get(riversSlotIndex);
  const st = stageRect();
  // Which way is the page it went to? From this slot's box to that page's box on the real board.
  // (Falling back to the way its real bubble is moving, and only then to the right.)
  let dirX = 0, dirY = 0;
  const destSlot = pageSlotProvider ? pageSlotProvider(visitorPage.get(vid) ?? "") : -1;
  const dest = destSlot >= 0 ? slotCenterProvider?.(destSlot) : null;
  if (dest && cloud) {
    dirX = dest.x - cloud.cx;
    dirY = dest.y - cloud.cy;
  } else if (bp) {
    dirX = bp.x - st.offX - p.x;
    dirY = bp.y - st.offY - p.y;
  }
  if (Math.abs(dirX) < 1e-6 && Math.abs(dirY) < 1e-6) { dirX = 1; dirY = 0; }
  // the point where that straight line leaves what is on screen right now
  const W = riversCssW(), H = riversCssH(), sc = camera.scale;
  // (in a card the way out runs on across the neighbouring sliver to the edge of the grey stage, where the dot is gone)
  const g = cardMode() ? riversCardGeo : null, gl = g ? g.ix : 0, gt = g ? g.it : 0, gr = g ? g.panelW - g.ix - W : 0, gb = g ? g.panelH - g.it - H : 0;
  const L = camera.cx - (W / 2 + gl) / sc, R = camera.cx + (W / 2 + gr) / sc;
  const T = camera.cy - (H / 2 + gt) / sc, B = camera.cy + (H / 2 + gb) / sc;
  const m = EDGE_MARGIN / sc;
  const tx = dirX > 0 ? (R + m - p.x) / dirX : dirX < 0 ? (L - m - p.x) / dirX : Infinity;
  const ty = dirY > 0 ? (B + m - p.y) / dirY : dirY < 0 ? (T - m - p.y) / dirY : Infinity;
  const t = Math.max(0, Math.min(tx, ty));
  p.exitTarget = { x: p.x + dirX * t, y: p.y + dirY * t };
  p.tweenStartX = p.x; p.tweenStartY = p.y; p.tweenT0 = now; p.tweenDur = moveMs(RIVERS_EXIT_MS);
  p.sx = undefined; p.sy = undefined;
  p.traveler = true; p.travelerDir = -1; p.travelerAlpha = 1;
  p.following = false; p.landed = false;
}

function edgePoint(edge: "top" | "bottom" | "left" | "right", W: number, H: number, nearX?: number, nearY?: number) {
  const along = (len: number, near?: number) =>
    near !== undefined ? Math.max(len * 0.1, Math.min(len * 0.9, near)) : len * (0.2 + Math.random() * 0.6);
  if (edge === "left") return { x: -EDGE_MARGIN, y: along(H, nearY) };
  if (edge === "right") return { x: W + EDGE_MARGIN, y: along(H, nearY) };
  if (edge === "top") return { x: along(W, nearX), y: -EDGE_MARGIN };
  return { x: along(W, nearX), y: H + EDGE_MARGIN };
}

function edgeForPage(page: string): "top" | "bottom" | "left" | "right" | undefined {
  const n = riversNeighbors;
  if (n.top === page) return "top";
  if (n.bottom === page) return "bottom";
  if (n.left === page) return "left";
  if (n.right === page) return "right";
  return undefined;
}

function nearestEdge(x: number, y: number, W: number, H: number): "top" | "bottom" | "left" | "right" {
  const d = { left: x, right: W - x, top: y, bottom: H - y };
  let best: "top" | "bottom" | "left" | "right" = "left";
  for (const k of Object.keys(d) as Array<keyof typeof d>) if (d[k] < d[best]) best = k;
  return best;
}

function riversTweenAll(now: number) {
  for (const p of riversParticles) {
    startTween(p, now);
    p.homeAngle = Math.random() * Math.PI * 2;
  }
}

// ── public API ────────────────────────────────────────────────────────────────

/** Call once when the SVG element is ready. */
let resizeListening = false;
/**
 * Escape closes a page's zoom, back to the grid (the same as Back). (The single view's Escape lives in AnalyticsView, beside the Page
 * view icon it shares its code with; both use isFreshEscape.) Escape never fights what the user is doing: a typed field keeps it, the
 * card's own view menu takes it first (it closes), and so does any popup that has already claimed the key (defaultPrevented; this
 * listens on window, after the document-level ones).
 */
const onEscapeKey = (e: KeyboardEvent) => {
  if (!isFreshEscape(e)) return;
  if (!isPageZoomOpen() || !riversOverlay?.isConnected || panning) return;
  if (closeCardMenu()) return;
  handleBackButton();
};
/** Closes the card's view menu if it is open. True when it was (that Escape is spent). */
export function closeCardMenu(): boolean {
  for (const m of [riversMenuEl, singleCtl?.menu ?? null]) {
    if (m && m.style.display !== "none") { m.style.display = "none"; return true; }
  }
  return false;
}
const onWindowResize = () => {
  resize();
  sizeFlightCanvas();
};

// Staging only: a stale tab (an SPA keeps its old JavaScript until reloaded) has
// been mistaken for a bug twice, so show which build the tab is actually running.
let stampEl: HTMLElement | null = null;
let stampBase = "";
let stampAt = 0;
function showStagingBuildStamp() {
  const id = process.env.REACT_APP_BUILD_ID;
  if (!id || !/staging/i.test(window.location.hostname) || document.getElementById("skydive-build-stamp")) return;
  const el = document.createElement("div");
  el.id = "skydive-build-stamp";
  el.textContent = `staging build ${id}`;
  stampEl = el;
  stampBase = `staging build ${id}`;
  el.style.cssText = "position:fixed;left:6px;top:2px;z-index:3000;font:10px ui-monospace,Menlo,monospace;color:#90a4ae;pointer-events:none;";
  document.body.appendChild(el);
}

export function init(svg: SVGElement) {
  showStagingBuildStamp();
  svgEl = svg;
  if (!resizeListening) {
    window.addEventListener("resize", onWindowResize);
    window.addEventListener("keydown", onEscapeKey);
    resizeListening = true;
  }
  svgRect = svg.getBoundingClientRect();
  singleCardKey = ""; singleCountEl = null;   // (a new SVG: the old card went with the old one)
  reconcileSingleCard();
  if (!rafHandle) {
    lastTs = performance.now();
    rafHandle = requestAnimationFrame(loop);
  }
}

/** Update the cached SVG bounding rect (call on resize). */
export function resize(svg?: SVGElement) {
  if (svg) svgEl = svg;
  if (svgEl) {
    svgRect = svgEl.getBoundingClientRect();
    // resize-twice pattern: also re-measure on next rAF after CSS settles
    requestAnimationFrame(() => {
      if (svgEl) svgRect = svgEl.getBoundingClientRect();
      for (const cloud of slotClouds.values()) positionSlotCanvas(cloud);
      if (riversCanvas && svgEl) sizeRiversCanvas();
    });
  }
  for (const cloud of slotClouds.values()) positionSlotCanvas(cloud);
  if (riversCanvas) sizeRiversCanvas();
  if (riversOverlay && svgEl) {
    const rect = stageRect();
    riversOverlay.style.left = `${rect.left + window.scrollX}px`;
    riversOverlay.style.top  = `${rect.top  + window.scrollY}px`;
    riversOverlay.style.width = `${rect.width}px`;
  }
}

/**
 * Called by clusterService on every change to per-slot visitor count.
 * cx/cy/w/h are in page coordinates (pixels from viewport origin).
 */
export function notifyParticleCount(
  slotIndex: number, pageName: string, count: number,
  cx: number, cy: number, w: number, h: number
) {
  slotCounts.set(slotIndex, count);
  if (!svgEl) return;   // not mounted (the dashboard is not on screen): a cloud must never appear on another page
  const now = performance.now();
  if (riversMode) {
    // the cloud under the zoomed view stays exactly as it is. That includes the single view's own
    // cloud when the crowd thins out: its view STAYS OPEN (it closed at under 15 before, and with
    // it the controls and the chosen grouping, mid-playback as a day quietened). It ends only when
    // the single view itself is left, or the board is wiped (resetVisitors).
    return;
  }
  // The single view with a GROUPING chosen is the grouped view whatever the crowd: the category numbers and names are drawn by the cloud
  // view, so without it a quiet moment (or a scrub back to one) left the dropdown on "Browser" and the labels gone until 15 visitors
  // arrived. A chosen grouping keeps the cloud view up at any size, zero included.
  const keepGrouped = slotIndex === SINGLE_SLOT_INDEX && singleViewDimension !== null;
  const existing = slotClouds.get(slotIndex);
  if (existing) {
    // Sticky: the visitor count wobbles around any threshold, and a cloud that
    // forms and dissolves with it is a distracting flash, not information.
    if (
      !keepGrouped &&
      count < CLOUD_EXIT_THRESHOLD &&
      now - (existing.bornAt ?? 0) >= CLOUD_MIN_LIFE_MS
    ) {
      destroySlotCloud(slotIndex);
      lastCloudExit.set(slotIndex, now);
      return;
    }
    // the single view's cloud is shown as its rivers view: if it is not open, open it
    if (slotIndex === SINGLE_SLOT_INDEX && (count >= CLOUD_THRESHOLD || keepGrouped)) openSingleView(true);
    return;
  }
  if ((count >= CLOUD_THRESHOLD || keepGrouped) && !riversMode) {
    const since = now - (lastCloudExit.get(slotIndex) ?? -Infinity);
    if (keepGrouped || since >= CLOUD_REFORM_COOLDOWN_MS || count >= CLOUD_REFORM_OVERRIDE) {
      const c = getOrCreateSlotCloud(slotIndex, pageName, cx, cy, w, h);
      c.bornAt = now;
      if (slotIndex === SINGLE_SLOT_INDEX) openSingleView(false);
    }
  }
}

let zoomChangeHandler: ((open: boolean) => void) | null = null;
export function setZoomChangeHandler(fn: typeof zoomChangeHandler) {
  zoomChangeHandler = fn;
}
/** for keeping the zoom across a refresh: which page's zoom is open, and which sub-view ("One cloud" is null) */
export function getZoomSnapshot(): { slotIndex: number; pageName: string; dimension: string | null } | null {
  if (!riversMode || riversPersistent || riversSlotIndex < 0) return null;
  return { slotIndex: riversSlotIndex, pageName: riversPageName, dimension: riversActiveDimension };
}
export function setZoomDimension(dim: string | null) {
  // "browser" is what a saved view from before the filter groupings called the browser grouping
  const id = dim === "browser" ? filterDimId("browser") : dim;
  if (id !== null && !(id in DIMENSIONS) && parseFilterDim(id) === null) return;   // a grouping this build does not know: stay as is
  if (riversMode) setRiversDimension(id as DimensionId | null);
}

export function isZoomed(): boolean {
  return riversMode;
}
/** Visitors released while the zoomed view is open (their real bubble is on its way somewhere). */
export function getLeavingVisitorIds(): string[] {
  return Array.from(riversLeaving.keys());
}

/** Read-only snapshot of the new visitors currently flying in from the spawn point (client px), for tests. */
export function getArrivalsDebug() {
  return arrivalsDrawn.slice();
}

/** Read-only snapshot of the zoomed view's camera, for tests. */
export function getCameraDebug() {
  return {
    scale: camera.scale, cx: camera.cx, cy: camera.cy,
    autoFit: riversAutoFit, opening: riversFitTrack, panning: !!riversCamAnim, scaleAnim: !!riversScaleAnim,
    onPage: riversOnPage, fit: riversMode ? crowdFitScale() : null,
    stage: riversMode ? { w: riversCssW(), h: riversCssH() } : null,
  };
}

/** Read-only snapshot of the bubbles currently flying out of the zoomed view (client px), for tests. */
export function getFlightsDebug() {
  return flights.map((f) => ({ id: f.p.visitorId, r: f.p.r, x: f.p.x, y: f.p.y, x0: f.x0, y0: f.y0, x1: f.x1, y1: f.y1, ageMs: performance.now() - f.t0, durMs: f.dur }));
}

/** Read-only counters and pack state, for tests and the staging stamp. */
export function getCloudStats() {
  return { ...cloudStats };
}
export function getPackDebug(slotIndex: number) {
  const c = slotClouds.get(slotIndex);
  if (!c) return null;
  const pk = c.pack;
  return { members: pk.owner.size, frontier: pk.frontier, holes: pk.holes.size, s: pk.s, r: pk.r, particles: c.particles.length, pending: c.pending.size, arrived: c.arrived.size };
}

/** Read-only snapshot of the grouped view's category spots, for tests. */
export function getRegionsDebug(): Array<{ label: string; x: number; y: number; r: number; count: number }> {
  return riversRegions.map((r) => ({ label: r.label, x: r.x, y: r.y, r: r.r, count: r.count }));
}

/** Read-only snapshot of the zoomed view's dots (stage px), for tests. */
export function getRiversDotPositions(): Array<{ id: string; x: number; y: number; r: number; scale: number; leaving: boolean }> {
  return riversParticles.map((p) => ({ id: p.visitorId, x: p.x, y: p.y, r: p.r, scale: camera.scale, leaving: !!p.exitTarget }));
}

/** Read-only snapshot of a slot cloud's dots (centre-relative px), for tests. */
export function getSlotDotPositions(slotIndex: number): Array<{ id: string; x: number; y: number; r: number }> {
  const cloud = slotClouds.get(slotIndex);
  if (!cloud) return [];
  return cloud.particles
    .filter((p) => !p.traveler && !p.exitTarget)
    .map((p) => ({ id: p.visitorId, x: p.x, y: p.y, r: p.r }));
}

/** Call when a visitor arrives or changes page. */
export function notifyVisitorPage(visitorId: string, pageName: string) {
  const before = visitorPage.get(visitorId);
  if (before !== undefined && before !== pageName) visitorPrevPage.set(visitorId, before);
  visitorPage.set(visitorId, pageName);
  pageVersion++;
}

/** Call on every interaction pulse event. */
export function notifyInteractionState(visitorId: string, state: string) {
  const t0 = performance.now();
  visitorInteraction.set(visitorId, state);
  visitorStateT0.set(visitorId, t0);
  for (const cloud of slotClouds.values()) {
    const p = cloud.byId.get(visitorId);
    if (p) { p.interactionState = state; p.stateT0 = t0; }
  }
  if (riversMode) {
    const rp = riversById.get(visitorId);
    if (rp) { rp.interactionState = state; rp.stateT0 = t0; }
  }
}

export function getStateT0(visitorId: string): number | undefined {
  return visitorStateT0.get(visitorId);
}

/** Call on new-visitor with the session's demographic data. */
export function getInteractionState(visitorId: string): string | undefined {
  return visitorInteraction.get(visitorId);
}

export function notifyVisitorData(
  visitorId: string,
  data: { browser?: string; visitor_type?: string; goalReached?: boolean; unidentified?: boolean; attrs?: Record<string, string | undefined> }
) {
  visitorData.set(visitorId, data);
}

/** Call on disconnect-visitor. */
/**
 * Tell the clouds a visitor is closing the site. Returns whether that departure can be SEEN: false
 * only while a zoomed view is open and nothing in it shows this visitor leaving (a visitor on a
 * page that is not a cloud has no dot to follow), so its bubble flies to the dock behind the zoom,
 * out of sight. The host then skips the dock's burst, which would otherwise go off with nothing
 * arriving. (A visitor released from ANOTHER page's cloud is shown: the zoomed view follows its real
 * bubble and the host reveals it once it is below the stage.)
 */
export function notifyVisitorDeparture(visitorId: string): boolean {
  for (const cloud of slotClouds.values()) {
    let had = cloud.pending.delete(visitorId);
    cloud.pendingTarget.delete(visitorId);
    cloud.pendingRest.delete(visitorId);
    if (cloud.arrived.delete(visitorId)) had = true;
    const p = cloud.byId.get(visitorId);
    if (p) {
      had = true;
      cloud.byId.delete(visitorId);
      cloud.particles.splice(cloud.particles.indexOf(p), 1);
      releaseDot(visitorId, cloud.cx + p.x, cloud.cy + p.y, "left", cloud.slotIndex);
    }
    if (had) cloud.mv++;
  }
  if (riversMode) {
    if (riversById.has(visitorId) && !riversLeaving.has(visitorId)) {
      const now = performance.now();
      if (!startDepartureFlight(visitorId, now)) {
        riversLeaving.set(visitorId, now);
        riversLeaveReason.set(visitorId, "left");
      }
    }
  }
  visitorPage.delete(visitorId);
  pageVersion++;
  visitorPrevPage.delete(visitorId);
  visitorInteraction.delete(visitorId);
  visitorStateT0.delete(visitorId);
  visitorData.delete(visitorId);
  // what the zoomed view is now drawing for this visitor: a flight to the dock, or a leaving dot
  return !riversMode || riversLeaving.has(visitorId) || flights.some((f) => f.p.visitorId === visitorId);
}

/**
 * The single-cluster view has no page boxes, so no slot can hold a cloud: every
 * cloud turns back into its real bubbles exactly where its dots were.
 */
export function dissolveAll() {
  if (riversMode) handleBackButton(false);
  for (const idx of Array.from(slotClouds.keys())) destroySlotCloud(idx, true);
}

/**
 * The board is being wiped and rebuilt from somewhere else (entering or leaving playback, every seek):
 * the real bubbles are gone, so every cloud and everything the service knew about who is where goes
 * too. Without this the visitors who were on screen before the wipe stayed behind as GHOST dots (no
 * bubble, so no colour: they drew in the fallback teal) alongside the rebuilt ones, in a cloud that
 * counted far more dots than the page's own "on page" number. The rebuild re-announces every visitor
 * it draws, and the clouds re-form from the new counts.
 */
let lastResetAt = -1e9;
const RESET_QUIET_MS = 8000;
export function resetVisitors() {
  lastResetAt = performance.now();
  // A page's zoomed view is KEPT across the wipe (scrubbing the timeline must not throw you back to the grid): note it now, and
  // open it again at the end, on a fresh cloud for the same slot.
  let resume: { slotIndex: number; pageName: string; cx: number; cy: number; w: number; h: number; neighbors: SlotNeighbors } | null = null;
  if (riversMode && !riversPersistent && riversSlotIndex >= 0) {
    const c = slotClouds.get(riversSlotIndex);
    if (c) resume = { slotIndex: riversSlotIndex, pageName: riversPageName, cx: c.cx, cy: c.cy, w: c.w, h: c.h, neighbors: lastZoomNeighbors };
  }
  if (riversMode) handleBackButton(false);
  rowHoverId = null;
  rowSelectId = null;
  for (const idx of Array.from(slotClouds.keys())) destroySlotCloud(idx, false);
  visitorPage.clear();
  visitorPrevPage.clear();
  visitorInteraction.clear();
  visitorStateT0.clear();
  visitorData.clear();
  lastCloudExit.clear();
  pageVersion++;
  if (resume) zoomSlot(resume.slotIndex, resume.pageName, resume.neighbors, resume.cx, resume.cy, resume.w, resume.h, false);
}

/** Returns true if slotIndex has an active density cloud. */
export function isCloudActive(slotIndex: number): boolean {
  return slotClouds.has(slotIndex);
}

/** Handles a click on a density-cloud slot — enters rivers view. */
let lastZoomNeighbors: SlotNeighbors = {};
let zoomOpenedAt = -1e9;
export function handleSlotClick(slotIndex: number, pageName: string, neighbors: SlotNeighbors = {}, persistent = false, autoFit = false, animate = true) {
  if (riversMode) return;
  lastZoomNeighbors = neighbors;
  zoomOpenedAt = performance.now();
  // Settle everyone at the moment the zoom opens: no flights from an earlier view carry over, and every visitor already on the page
  // takes its place in the cluster at once instead of arriving or flying to it.
  flights.length = 0;
  arrivalsDrawn = [];
  const openedCloud = slotClouds.get(slotIndex);
  if (openedCloud) openedCloud.instantUntil = Math.max(openedCloud.instantUntil ?? 0, zoomOpenedAt + FORCED_LAND_MS);
  riversMode = true;
  riversPersistent = persistent;
  riversAutoFit = autoFit && !persistent;
  riversFitTrack = riversAutoFit;
  riversScaleAnim = null;
  // the view icons rise above the zoomed view so they can still be used, and in a PAGE's zoom the
  // two view-switching icons are disabled (AnalyticsView.scss)
  document.body.classList.add("density-zoom-open");
  document.body.classList.toggle("density-page-zoom-open", !persistent);
  zoomChangeHandler?.(true);
  // A page's zoom opens with a transition (the box grows into the card): the rest of the grid fades while it plays and is hidden for
  // good when it ends. Everything else (single view, a silent re-zoom after a scrub, reduced motion) hides the grid at once.
  const fromBox = !persistent && animate && !prefersReducedMotion() ? boxRectFor(slotIndex) : null;
  if (fromBox && svgEl) {
    svgEl.querySelectorAll("g[rectIndex]").forEach((g) => {
      const el = g as SVGElement, mine = el.getAttribute("rectIndex") === String(slotIndex);
      el.style.transition = mine ? "none" : "opacity 300ms ease";
      el.style.opacity = "0";
    });
    if (gridHideTimer) window.clearTimeout(gridHideTimer);
    gridHideTimer = window.setTimeout(() => { gridHideTimer = null; if (!riversMode) return; svgEl?.classList.add("density-zoomed"); setGridHidden(true); }, CARD_ANIM_MS * 0.6);
  } else {
    svgEl?.classList.add("density-zoomed"); // hides the grid boxes + real bubbles; the grey stage and historical dock stay
    setGridHidden(true);
  }
  riversPageName = pageName;
  riversActiveDimension = null;
  resetRiversPacks();

  // hide slot cloud overlays
  for (const cloud of slotClouds.values()) {
    cloud.canvas.style.display = "none";
  }

  buildRiversCanvas();
  const now = performance.now();
  populateRiversParticles(now, slotIndex);

  if (svgEl) {
    const rect = stageRect();
    buildRiversOverlay(rect.width / 2, rect.height / 2);
    buildEdgePills(neighbors);
    if (cardMode()) {
      camera.cx = riversHome.x; camera.cy = riversHome.y; camera.scale = 1;
      if (fromBox) playCardOpen(fromBox, neighbors);
      // The single view's card has no box to grow from, so it fades in when the crowd first makes a cloud. Not after a board wipe
      // (a scrub closes and re-opens it): a card that blinks on every seek is worse than one that is simply back.
      else if (persistent && !prefersReducedMotion() && performance.now() - lastResetAt > RESET_QUIET_MS) fadeInSingleCard();
    }
    // the single view comes back in the grouping it was left in (unless that one is unavailable now)
    if (persistent && singleViewDimension && !(singleViewDimension === "interactionState" && interactionStatesProvider?.() === false)) {
      setRiversDimension(singleViewDimension);
    }
  }
}

/**
 * The grid boxes are hidden by the .density-zoomed class (AnalyticsView.scss), and ALSO inline: measured in a browser, the
 * stylesheet rule alone failed intermittently (the boxes stayed painted under a zoomed view, one run in four), and an
 * inline !important does not depend on the style recalculation catching up.
 */
function setGridHidden(on: boolean) {
  if (!svgEl) return;
  svgEl.querySelectorAll("g[rectIndex]").forEach((g) => {
    const el = g as SVGElement;
    if (on) el.style.setProperty("visibility", "hidden", "important"); else el.style.removeProperty("visibility");
  });
  // flow lines belong to the grid view: they are off while a zoomed view is open
  // (both styles: the dashed lines' layer above the boxes, and the ribbons' layer under them)
  ["#flow-lines-layer", "#flow-ribbons-layer"].forEach((selector) => {
    const flow = svgEl?.querySelector(selector) as SVGElement | null;
    if (flow) { if (on) flow.style.setProperty("display", "none", "important"); else flow.style.removeProperty("display"); }
  });
}

/**
 * THE SINGLE VIEW'S CARD IS ALWAYS THERE. The same white card as a page's zoom, drawn in the SVG itself, above the (blanked) page
 * boxes and below every real bubble, so the d3 bubbles of a small crowd sit on it exactly as the cloud's dots do once the crowd is big
 * enough to open the rivers view (which then adds only its controls and canvas on top: it has no card of its own). It is a STATE,
 * reconciled every pass from the host (`singleViewProvider`), never left to one event: a rebuilt SVG, a resize or a missed toggle all
 * repair themselves. The host also calls it straight after a view switch so the card never lingers over the grid.
 */
const SINGLE_CARD_ID = "single-view-card";
const SVG_NS = "http://www.w3.org/2000/svg";
let singleViewProvider: (() => boolean) | null = null;
export function setSingleViewProvider(fn: (() => boolean) | null) { singleViewProvider = fn; }
// How many visitors are on the board RIGHT NOW, counted from the host's own list. The label does not use the per-slot counts the host
// sends: those are computed one step BEFORE an arriving bubble is added, so they trail an arrival until the next event.
let singleCountProvider: (() => number) | null = null;
export function setSingleCountProvider(fn: (() => number) | null) { singleCountProvider = fn; }
// The card's "Back": like a page's zoom, the single view has one, and it exists with the card (a small crowd has no cloud overlay to
// hold it). It runs the host's exit, which is the same code as the Page view icon and Escape.
let singleViewExitHandler: (() => void) | null = null;
export function setSingleViewExitHandler(fn: (() => void) | null) { singleViewExitHandler = fn; }
let singleBackEl: HTMLButtonElement | null = null;
function placeSingleBack(card: { left: number; top: number }) {
  if (!singleBackEl) {
    const b = document.createElement("button");
    b.textContent = "← Back";
    b.dataset.singleBack = "1";
    b.style.cssText = `position:absolute;z-index:202;font-family:${UI_FONT};background:transparent;color:#455a64;border:none;border-radius:6px;padding:4px 8px;font-size:14px;font-weight:500;cursor:pointer;transition:background .15s;`;
    b.addEventListener("mouseenter", () => { b.style.background = "rgba(55,71,79,0.08)"; });
    b.addEventListener("mouseleave", () => { b.style.background = "transparent"; });
    b.addEventListener("click", () => singleViewExitHandler?.());
    document.body.appendChild(b);
    singleBackEl = b;
  }
  singleBackEl.style.left = `${card.left + 14 + window.scrollX}px`;
  singleBackEl.style.top = `${card.top + 12 + window.scrollY}px`;
}
function removeSingleBack() { singleBackEl?.remove(); singleBackEl = null; }
let singleCardShown = false;
let singleCardKey = "";
let singleCardCount = -1;
let singleCountEl: SVGTSpanElement | null = null;
const num = (n: number) => String(Math.round(n * 100) / 100);
export function reconcileSingleCard() {
  if (!svgEl) return;
  const want = !!singleViewProvider?.();
  // the page boxes are blank in the single view: they must not react (hover, hints, clicks). A class, reconciled with the card.
  svgEl.classList.toggle("single-view", want);
  let g = svgEl.querySelector(`#${SINGLE_CARD_ID}`) as SVGGElement | null;
  if (!want) {
    g?.remove();
    removeSingleBack();
    removeSingleControls();
    singleCardShown = false; singleCardKey = ""; singleCardCount = -1; singleCountEl = null;
    if (!riversMode) document.body.classList.remove("density-card");
    return;
  }
  const m = (svgEl as unknown as SVGGraphicsElement).getScreenCTM();
  if (!m || !m.a || !m.d) return;
  const P = panelRect(), { ix, it, ib } = cardInsets(P.width, P.height, true);
  const px = { left: P.left + ix, top: P.top + it, w: Math.max(1, P.width - 2 * ix), h: Math.max(1, P.height - it - ib) };
  const key = [px.left, px.top, px.w, px.h, m.a, m.d, m.e, m.f].map((v) => Math.round(v * 2) / 2).join(",");
  if (!g || key !== singleCardKey || !singleCountEl || !singleCountEl.isConnected) {
    g?.remove();
    g = document.createElementNS(SVG_NS, "g") as SVGGElement;
    g.setAttribute("id", SINGLE_CARD_ID);
    g.setAttribute("pointer-events", "none");
    // the card in the SVG's own units
    const x = (px.left - m.e) / m.a, y = (px.top - m.f) / m.d, w = px.w / m.a, h = px.h / m.d, u = 1 / m.a;
    const rect = (dx: number, dy: number, grow: number, fill: string, opacity: number | null) => {
      const r = document.createElementNS(SVG_NS, "rect");
      r.setAttribute("x", num(x - grow + dx)); r.setAttribute("y", num(y - grow + dy));
      r.setAttribute("width", num(w + 2 * grow)); r.setAttribute("height", num(h + 2 * grow));
      r.setAttribute("rx", num(3 * u + grow)); r.setAttribute("fill", fill);
      if (opacity !== null) r.setAttribute("fill-opacity", String(opacity));
      g!.appendChild(r);
    };
    // a soft shadow from stacked translucent rectangles (a blur filter under moving bubbles would be repainted every frame)
    // (a page's card has box-shadow 0 8px 22px at .20: about .10 at the edge, fading to nothing 22px out, which this steps through)
    for (let k = 11; k >= 1; k--) rect(0, 8 * u, k * 2 * u, "#000", 0.009);
    rect(0, 0, 0, "#fff", null);
    // which view this is and how many are in it, bottom left, large
    const text = document.createElementNS(SVG_NS, "text");
    // (the baseline sits ~6px above the DOM title's box bottom, so the all-pages label lines up with a page zoom's)
    text.setAttribute("x", num(x + CARD_TITLE_INSET_PX * u)); text.setAttribute("y", num(y + h - (CARD_TITLE_INSET_PX + 6) * u));
    text.setAttribute("font-family", UI_FONT.replace(/"/g, "'"));
    const t1 = document.createElementNS(SVG_NS, "tspan");
    t1.textContent = "All pages"; t1.setAttribute("font-size", num(CARD_TITLE_PX * u)); t1.setAttribute("font-weight", "600"); t1.setAttribute("fill", "#37474f");
    const t2 = document.createElementNS(SVG_NS, "tspan");
    t2.setAttribute("font-size", num(CARD_COUNT_PX * u)); t2.setAttribute("dx", num(10 * u)); t2.setAttribute("fill", "#696969");
    const t3 = document.createElementNS(SVG_NS, "tspan");
    t3.setAttribute("fill", "#b4b4b4");
    t3.textContent = " onsite";
    const cnt = document.createElementNS(SVG_NS, "tspan");
    t2.appendChild(cnt); t2.appendChild(t3);
    text.appendChild(t1); text.appendChild(t2);
    g.appendChild(text);
    singleCountEl = cnt; singleCardCount = -1;
    singleCardKey = key;
    singleCardShown = true;
    svgEl.appendChild(g);   // placed properly just below
    placeGearRow(px.top);
  }
  placeSingleBack(px);
  placeSingleControls(px);
  // above every box (the blanked page boxes would otherwise paint over it), below every bubble, which are appended later
  const boxes = svgEl.querySelectorAll(":scope > g[rectIndex]");
  const anchor = boxes.length ? boxes[boxes.length - 1] : (svgEl.querySelector("#ambient-under") || svgEl.querySelector("#maincluster"));
  if (anchor && g.previousElementSibling !== anchor) anchor.after(g);
  const n = singleCountProvider ? singleCountProvider() : (slotCounts.get(SINGLE_SLOT_INDEX) ?? 0);
  if (singleCountEl && n !== singleCardCount) { singleCardCount = n; singleCountEl.textContent = n.toLocaleString("en-US"); }
  if (!document.body.classList.contains("density-card")) placeGearRow(px.top);
}

function fadeInSingleCard() {
  const els = [riversEdgeEl, riversPillLayer, riversOverlay, riversCanvas].filter((e): e is HTMLElement => !!e);
  for (const el of els) { el.style.transition = "none"; el.style.opacity = "0"; }
  void document.body.getBoundingClientRect();
  for (const el of els) { el.style.transition = "opacity 350ms ease"; el.style.opacity = "1"; }
  window.setTimeout(() => { for (const el of els) { el.style.transition = ""; el.style.opacity = ""; } }, 420);
}

/** Exits rivers view, returns to grid. */
export function handleBackButton(animate = true) {
  if (!riversMode) return;
  // the way back: a ghost of the card shrinks into its box (measured before anything is torn down), and the grid fades in
  const closeBox = animate && cardMode() && !riversPersistent ? boxRectFor(riversSlotIndex) : null;
  if (closeBox) playCardClose(closeBox);
  if (gridHideTimer) { window.clearTimeout(gridHideTimer); gridHideTimer = null; }
  cardAnim = null;
  riversMode = false;
  zoomChangeHandler?.(false);
  svgEl?.classList.remove("density-zoomed"); setGridHidden(false);
  svgEl?.querySelectorAll("g[rectIndex]").forEach((g) => {
    const el = g as SVGElement;
    if (closeBox) { el.style.transition = "none"; el.style.opacity = "0"; void (el as unknown as SVGGraphicsElement).getBoundingClientRect(); el.style.transition = "opacity 320ms ease 120ms"; el.style.opacity = ""; window.setTimeout(() => { el.style.transition = ""; }, 520); }
    else { el.style.transition = ""; el.style.opacity = ""; }
  });
  riversPersistent = false;
  riversAutoFit = false; riversFitTrack = false; riversScaleAnim = null;
  document.body.classList.remove("density-zoom-open", "density-page-zoom-open");
  riversPageName = "";
  riversSlotIndex = -1;
  riversLeaving.clear();
  riversLeaveReason.clear();
  riversCamAnim = null;
  riversParticles = [];
  riversById = new Map();
  riversKey = "";
  riversRegions = [];

  destroyRiversCanvas();
  destroyRiversOverlay();

  // A cloud that exists only because it was clicked open (zoomSlot) goes back to being the d3
  // cluster it was, unless the crowd grew into a real cloud while the view was open.
  for (const [idx, cloud] of Array.from(slotClouds.entries())) {
    if (cloud.forced && (slotCounts.get(idx) ?? 0) < CLOUD_EXIT_THRESHOLD) destroySlotCloud(idx);
  }

  // restore slot cloud overlays
  for (const cloud of slotClouds.values()) {
    cloud.canvas.style.display = "";
  }
}

// Time a clicked-open cloud lets visitors who are already on the page land where their bubble is.
const FORCED_LAND_MS = 1500;

/**
 * Zooms into ANY page slot, busy or quiet. The zoomed view is drawn FROM the slot's cloud (its dots
 * and spots), and a quiet slot has none, so one is formed for it on the spot. The visitors already
 * on the page land at once where their bubbles are (nothing flies in from the sidebar for people
 * who have been here for minutes), and handleBackButton dissolves the cloud again if the crowd
 * did not grow into one.
 */
export function zoomSlot(
  slotIndex: number, pageName: string, neighbors: SlotNeighbors,
  cx: number, cy: number, w: number, h: number, animate = true
) {
  if (riversMode || !svgEl) return;
  if (!slotClouds.has(slotIndex)) {
    const now = performance.now();
    const cloud = getOrCreateSlotCloud(slotIndex, pageName, cx, cy, w, h);
    cloud.bornAt = now;
    cloud.forced = true;
    cloud.instantUntil = now + FORCED_LAND_MS;
    syncSlotParticles(cloud, now);
  }
  // Every zoom opens at 1x (Chris: the auto-fit, up to 6x, was far too big; the zoom controls reach that level when wanted).
  // The auto-fit camera is still in the service (handleSlotClick's autoFit argument) but nothing asks for it.
  handleSlotClick(slotIndex, pageName, neighbors, false, false, animate);
}

/** Full teardown — call when the analytics view unmounts. */
export function destroy() {
  cancelAnimationFrame(rafHandle);
  rafHandle = 0;
  rowHoverId = null;
  rowSelectId = null;
  for (const idx of Array.from(slotClouds.keys())) destroySlotCloud(idx, false);
  // a zoomed view that is torn down with the service is CLOSED, not left "open" with no canvas:
  // riversMode staying true made the next init refuse every cloud and every click
  if (riversMode) {
    riversMode = false;
    zoomChangeHandler?.(false);
  }
  riversPersistent = false;
  riversAutoFit = false; riversFitTrack = false; riversScaleAnim = null;
  singleViewDimension = null;
  riversPageName = "";
  riversSlotIndex = -1;
  riversLeaving.clear();
  riversLeaveReason.clear();
  riversCamAnim = null;
  riversParticles = [];
  riversById = new Map();
  riversKey = "";
  riversRegions = [];
  flights.length = 0;
  arrivalsDrawn = [];
  destroyRiversCanvas();
  destroyRiversOverlay();
  slotCounts.clear();
  lastCloudExit.clear();
  visitorInteraction.clear();
  visitorStateT0.clear();
  visitorPage.clear();
  visitorPrevPage.clear();
  visitorData.clear();
  svgEl?.classList.remove("density-zoomed"); setGridHidden(false);
  riversPersistent = false;
  svgEl?.querySelector(`#${SINGLE_CARD_ID}`)?.remove();
  removeSingleBack();
  removeSingleControls();
  singleCardShown = false; singleCardKey = ""; singleCardCount = -1; singleCountEl = null;
  document.body.classList.remove("density-zoom-open", "density-page-zoom-open", "density-card");
  svgEl = null;
  svgRect = null;
  window.removeEventListener("resize", onWindowResize);
  window.removeEventListener("keydown", onEscapeKey);
  resizeListening = false;
}

// For the real-browser check (scripts/zoom-hide-unknown-check.mjs): which groups the zoomed view lays out, and how many dots of each are being drawn. The demo build only.
if (typeof window !== "undefined" && process.env.REACT_APP_DEMO === "1") {
  (window as any).__zoomRegions = () => ({
    dim: riversActiveDimension,
    regions: riversRegions.map((r) => ({ label: r.label, count: r.count })),
    hiddenDots: riversParticles.filter((p) => hiddenInZoom(p)).length,
    dots: riversParticles.length,
  });
}
